import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, Calendar, Share2, Copy, Check, X, ChevronRight,
  Sparkles, Crown, Gift, Image, LogOut, Menu, Home, 
  Settings, Award, Users, Play, PartyPopper, CreditCard,
  Building, Hash, Mail, Lock, Eye, EyeOff, Plus, Ticket,
  Heart, Star, ExternalLink, Wallet, DollarSign, TrendingUp, Shield,
  MessageCircle, Send, ChevronLeft, Filter, Grid, List, Camera,
  Video, FileImage, Clock, Bell, Bookmark, MoreHorizontal, Trash2, Edit3,
  Search, Compass, Zap, TrendingDown, BarChart3, PieChart, Activity,
  Globe, MapPin, ChevronDown, CircleDollarSign, Flame, Target, Eye as EyeIcon,
  MessageSquare, ArrowRight, ArrowUp, ArrowDown, RefreshCw, Upload, Inbox
} from "lucide-react";

// ============ MOCK DATABASE (localStorage) ============
const DB_VERSION = 3; // Increment when schema changes

const getDB = () => {
  const defaultDB = {
    version: DB_VERSION,
    creators: {},
    fans: {},
    subscriptions: [],
    posts: [],
    stories: [],
    messages: [],
    conversations: [],
    notifications: [],
    tips: [],
    savedPosts: []
  };
  const stored = localStorage.getItem('fanpass_demo_db');
  if (!stored) {
    localStorage.setItem('fanpass_demo_db', JSON.stringify(defaultDB));
    return defaultDB;
  }
  const parsed = JSON.parse(stored);
  // Version migration - reset if old schema
  if (!parsed.version || parsed.version < DB_VERSION) {
    localStorage.removeItem('fanpass_demo_db');
    localStorage.setItem('fanpass_demo_db', JSON.stringify(defaultDB));
    return defaultDB;
  }
  return {
    version: parsed.version,
    creators: parsed.creators || {},
    fans: parsed.fans || {},
    subscriptions: parsed.subscriptions || [],
    posts: parsed.posts || [],
    stories: parsed.stories || [],
    messages: parsed.messages || [],
    conversations: parsed.conversations || [],
    notifications: parsed.notifications || [],
    tips: parsed.tips || [],
    savedPosts: parsed.savedPosts || []
  };
};

const saveDB = (db) => localStorage.setItem('fanpass_demo_db', JSON.stringify(db));

// Initialize demo creators
const initDemoData = () => {
  const db = getDB();
  
  // Demo Creator 1 - Sarah Chen (with multiple experience drops)
  if (!db.creators['sarah-chen']) {
    db.creators['sarah-chen'] = {
      id: 'sarah-chen',
      username: 'sarah-chen',
      name: 'Sarah Chen',
      email: 'sarah@example.com',
      photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
      bio: 'Wellness coach & yoga instructor',
      category: 'Wellness',
      membershipPrice: '$5/month',
      // Multiple Experience Drops
      experienceDrops: [
        // Active Drops (3-4)
        {
          id: 'drop-1',
          title: 'VIP Wellness Retreat in Bali!',
          type: '1-on-1 Experience',
          status: 'active',
          drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: 'Join me for an exclusive 3-day wellness retreat in Bali! Includes flights, luxury accommodation, daily yoga sessions, and private coaching.',
          numberOfWinners: 1,
          entries: 892,
          media: [
            { type: 'image', url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800' },
            { type: 'image', url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800' }
          ]
        },
        {
          id: 'drop-2',
          title: '1-on-1 Virtual Yoga Session',
          type: '1-on-1 Video Call',
          status: 'active',
          drawDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: 'Win a private 60-minute yoga session with personalized guidance and wellness tips!',
          numberOfWinners: 3,
          entries: 456,
          media: [{ type: 'image', url: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800' }]
        },
        {
          id: 'drop-3',
          title: 'Exclusive Merch Bundle',
          type: 'Physical Prize',
          status: 'active',
          drawDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: 'Win a signed yoga mat, wellness journal, and exclusive merch!',
          numberOfWinners: 5,
          entries: 723,
          media: []
        },
        // Completed Drops (5-6)
        {
          id: 'drop-6',
          title: 'Holiday Wellness Package',
          type: 'Physical Prize',
          status: 'completed',
          drawDate: '2025-12-15',
          description: 'Premium wellness gift box with organic products.',
          numberOfWinners: 3,
          entries: 1245,
          winners: [
            { name: 'Jessica M.', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150' },
            { name: 'David K.', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150' },
            { name: 'Emma R.', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150' }
          ],
          media: []
        },
        {
          id: 'drop-7',
          title: 'Private Coaching Session',
          type: 'Mentorship Session',
          status: 'completed',
          drawDate: '2025-11-20',
          description: '3-month private wellness coaching package.',
          numberOfWinners: 1,
          entries: 987,
          winners: [{ name: 'Sarah T.', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150' }],
          media: []
        },
        {
          id: 'drop-8',
          title: 'Yoga Retreat Costa Rica 2024',
          type: '1-on-1 Experience',
          status: 'completed',
          drawDate: '2025-10-01',
          description: 'All-inclusive yoga retreat experience.',
          numberOfWinners: 2,
          entries: 2156,
          winners: [
            { name: 'Mike L.', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150' },
            { name: 'Anna W.', photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150' }
          ],
          media: []
        },
        {
          id: 'drop-9',
          title: 'Virtual Workshop Access',
          type: 'Exclusive Event Access',
          status: 'completed',
          drawDate: '2025-09-15',
          description: 'Access to exclusive virtual workshop series.',
          numberOfWinners: 20,
          entries: 1567,
          winners: [],
          media: []
        },
        {
          id: 'drop-10',
          title: 'Birthday Giveaway Special',
          type: 'Physical Prize',
          status: 'completed',
          drawDate: '2025-08-20',
          description: 'Special birthday celebration giveaway!',
          numberOfWinners: 10,
          entries: 2089,
          winners: [],
          media: []
        }
      ],
      // Keep single experienceDrop for backward compatibility (first active drop)
      experienceDrop: {
        title: 'VIP Wellness Retreat in Bali!',
        type: '1-on-1 Experience',
        drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: 'Join me for an exclusive 3-day wellness retreat in Bali! Includes flights, luxury accommodation, daily yoga sessions, and private coaching.',
        numberOfWinners: 1,
        media: [
          { type: 'image', url: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800' },
          { type: 'image', url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800' }
        ],
        pastWinners: [
          { name: 'Jessica M.', date: '2025-12-15', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150' },
          { name: 'David K.', date: '2025-11-20', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150' }
        ]
      },
      bankDetails: {
        bankName: 'Commonwealth Bank',
        accountLast4: '4567',
        routingLast4: '1234',
        verified: true
      },
      content: [
        { id: '1', text: 'Morning meditation routine - exclusive for members! 🧘‍♀️', image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800', date: new Date().toISOString() },
        { id: '2', text: 'Behind the scenes from my latest retreat...', image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800', date: new Date(Date.now() - 86400000).toISOString() }
      ],
      // Updated metrics to match demo data
      stats: { 
        totalEarnings: 42750, 
        pendingPayout: 8900, 
        totalMembers: 850,
        thisMonthEarnings: 4850,
        thisMonthNewMembers: 89,
        avgSubscriptionPrice: 5,
        totalExperienceEntries: 2360,
        contentViews: 15847,
        conversionRate: 14.8
      },
      createdAt: new Date().toISOString()
    };
  }
  
  // Demo Creator 2 - Marcus Gaming (with multiple experience drops)
  if (!db.creators['marcus-gaming']) {
    db.creators['marcus-gaming'] = {
      id: 'marcus-gaming',
      username: 'marcus-gaming',
      name: 'Marcus Gaming',
      email: 'marcus@example.com',
      photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      bio: 'Pro esports player & streamer',
      category: 'Gaming',
      membershipPrice: '$10/month',
      experienceDrops: [
        // Active Drops
        {
          id: 'mg-drop-1',
          title: 'Gaming Session + Signed Merch!',
          type: 'Live Stream Appearance',
          status: 'active',
          drawDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: 'Win a private gaming session with me! Plus signed merch and exclusive in-game items.',
          numberOfWinners: 3,
          entries: 1834,
          media: [
            { type: 'image', url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800' },
            { type: 'image', url: 'https://images.unsplash.com/photo-1612287230202-1ff1d85d1bdf?w=800' }
          ]
        },
        {
          id: 'mg-drop-2',
          title: 'Pro Controller Giveaway',
          type: 'Physical Prize',
          status: 'active',
          drawDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: 'Win a custom pro controller signed by me!',
          numberOfWinners: 5,
          entries: 2341,
          media: []
        },
        // Scheduled
        {
          id: 'mg-drop-3',
          title: 'Tournament VIP Access',
          type: 'Exclusive Event Access',
          status: 'scheduled',
          drawDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          description: 'VIP access to major gaming tournament with backstage pass!',
          numberOfWinners: 2,
          entries: 0,
          media: []
        },
        // Completed
        {
          id: 'mg-drop-4',
          title: 'Holiday Gaming Marathon',
          type: 'Live Stream Appearance',
          status: 'completed',
          drawDate: '2025-12-10',
          description: 'Co-stream during holiday marathon.',
          numberOfWinners: 3,
          entries: 3456,
          winners: [
            { name: 'Alex T.', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150' },
            { name: 'Sam W.', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150' },
            { name: 'Chris L.', photo: 'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=150' }
          ],
          media: []
        },
        {
          id: 'mg-drop-5',
          title: 'Gaming PC Giveaway',
          type: 'Physical Prize',
          status: 'completed',
          drawDate: '2025-11-15',
          description: 'Full gaming PC setup!',
          numberOfWinners: 1,
          entries: 8976,
          winners: [{ name: 'Jordan K.', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150' }],
          media: []
        }
      ],
      experienceDrop: {
        title: 'Gaming Session + Signed Merch!',
        type: 'Live Stream Appearance',
        drawDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: 'Win a private gaming session with me! Plus signed merch and exclusive in-game items.',
        numberOfWinners: 3,
        media: [
          { type: 'image', url: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800' },
          { type: 'image', url: 'https://images.unsplash.com/photo-1612287230202-1ff1d85d1bdf?w=800' }
        ],
        pastWinners: [
          { name: 'Alex T.', date: '2025-12-10', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150' },
          { name: 'Sam W.', date: '2025-11-28', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150' },
          { name: 'Chris L.', date: '2025-11-15', photo: 'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=150' }
        ]
      },
      bankDetails: {
        bankName: 'Bank of America',
        accountLast4: '8901',
        routingLast4: '5678',
        verified: true
      },
      content: [
        { id: '1', text: 'New strategy guide dropping tomorrow! 🎮', image: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800', date: new Date().toISOString() }
      ],
      stats: { 
        totalEarnings: 68500, 
        pendingPayout: 12500, 
        totalMembers: 720,
        thisMonthEarnings: 7200,
        thisMonthNewMembers: 112,
        avgSubscriptionPrice: 10,
        totalExperienceEntries: 4175,
        contentViews: 24680,
        conversionRate: 18.2
      },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 3 - Mia Fitness
  if (!db.creators['mia-fitness']) {
    db.creators['mia-fitness'] = {
      id: 'mia-fitness',
      username: 'mia-fitness',
      name: 'Mia Rodriguez',
      email: 'mia@example.com',
      photo: 'https://images.unsplash.com/photo-1594381898411-846e7d193883?w=400',
      bio: 'HIIT & strength training specialist',
      category: 'Fitness',
      membershipPrice: '$8/month',
      experienceDrops: [
        { id: 'mf-drop-1', title: 'Personal Training Session', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Win a private 1-hour training session!', numberOfWinners: 2, entries: 567, media: [] }
      ],
      stats: { totalEarnings: 34200, pendingPayout: 5600, totalMembers: 425, thisMonthNewMembers: 67, contentViews: 12340 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 4 - Chef Antonio
  if (!db.creators['chef-antonio']) {
    db.creators['chef-antonio'] = {
      id: 'chef-antonio',
      username: 'chef-antonio',
      name: 'Chef Antonio',
      email: 'antonio@example.com',
      photo: 'https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=400',
      bio: 'Michelin-starred chef sharing secret recipes',
      category: 'Food & Cooking',
      membershipPrice: '$12/month',
      experienceDrops: [
        { id: 'ca-drop-1', title: 'Private Cooking Class', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Learn to cook a 5-course meal with me!', numberOfWinners: 1, entries: 892, media: [] }
      ],
      stats: { totalEarnings: 58900, pendingPayout: 9800, totalMembers: 490, thisMonthNewMembers: 45, contentViews: 18920 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 5 - Luna Arts
  if (!db.creators['luna-arts']) {
    db.creators['luna-arts'] = {
      id: 'luna-arts',
      username: 'luna-arts',
      name: 'Luna Martinez',
      email: 'luna@example.com',
      photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
      bio: 'Digital artist & NFT creator',
      category: 'Art & Design',
      membershipPrice: '$7/month',
      experienceDrops: [
        { id: 'la-drop-1', title: 'Custom Portrait Commission', type: 'Physical Prize', status: 'active', drawDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Win a custom digital portrait!', numberOfWinners: 3, entries: 1234, media: [] }
      ],
      stats: { totalEarnings: 28500, pendingPayout: 4200, totalMembers: 380, thisMonthNewMembers: 52, contentViews: 9870 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 6 - DJ Nexus
  if (!db.creators['dj-nexus']) {
    db.creators['dj-nexus'] = {
      id: 'dj-nexus',
      username: 'dj-nexus',
      name: 'DJ Nexus',
      email: 'nexus@example.com',
      photo: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400',
      bio: 'EDM producer & live performer',
      category: 'Music',
      membershipPrice: '$6/month',
      experienceDrops: [
        { id: 'dn-drop-1', title: 'VIP Festival Tickets', type: 'Exclusive Event Access', status: 'active', drawDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Win backstage passes to my next show!', numberOfWinners: 5, entries: 2156, media: [] }
      ],
      stats: { totalEarnings: 45600, pendingPayout: 7800, totalMembers: 760, thisMonthNewMembers: 98, contentViews: 28450 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 7 - Jake Sports (Sports/Athlete)
  if (!db.creators['jake-sports']) {
    db.creators['jake-sports'] = {
      id: 'jake-sports',
      username: 'jake-sports',
      name: 'Jake Thompson',
      email: 'jake@example.com',
      photo: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400',
      bio: 'Pro basketball player sharing training secrets & behind-the-scenes life',
      category: 'Sports',
      membershipPrice: '$25/month',
      experienceDrops: [
        { id: 'js-drop-1', title: 'Courtside Game Experience', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Watch the game courtside with me + meet & greet!', numberOfWinners: 2, entries: 3421, media: [] }
      ],
      stats: { totalEarnings: 125000, pendingPayout: 18000, totalMembers: 1250, thisMonthNewMembers: 187, contentViews: 89500 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 8 - Bella Fashion (Fashion/Style)
  if (!db.creators['bella-fashion']) {
    db.creators['bella-fashion'] = {
      id: 'bella-fashion',
      username: 'bella-fashion',
      name: 'Bella Romano',
      email: 'bella@example.com',
      photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400',
      bio: 'Fashion designer & stylist. Paris Fashion Week insider tips',
      category: 'Fashion',
      membershipPrice: '$18/month',
      experienceDrops: [
        { id: 'bf-drop-1', title: 'Personal Shopping Day in Milan', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'A full day shopping with me in Milan + style consultation!', numberOfWinners: 1, entries: 1876, media: [] }
      ],
      stats: { totalEarnings: 67800, pendingPayout: 9200, totalMembers: 890, thisMonthNewMembers: 112, contentViews: 45600 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 9 - Alex Travel (Travel/Adventure)
  if (!db.creators['alex-travel']) {
    db.creators['alex-travel'] = {
      id: 'alex-travel',
      username: 'alex-travel',
      name: 'Alex Wanderer',
      email: 'alex.travel@example.com',
      photo: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400',
      bio: 'Full-time traveller. 85 countries and counting. Travel hacks & hidden gems',
      category: 'Travel',
      membershipPrice: '$8/month',
      experienceDrops: [
        { id: 'at-drop-1', title: 'Join My Bali Adventure Trip', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Win a spot on my next Bali trip - all expenses paid!', numberOfWinners: 3, entries: 5632, media: [] }
      ],
      stats: { totalEarnings: 34500, pendingPayout: 4800, totalMembers: 1450, thisMonthNewMembers: 234, contentViews: 78900 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 10 - Tech Mike (Technology/Coding)
  if (!db.creators['tech-mike']) {
    db.creators['tech-mike'] = {
      id: 'tech-mike',
      username: 'tech-mike',
      name: 'Mike Chen',
      email: 'mike@example.com',
      photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400',
      bio: 'Senior software engineer at FAANG. Career coaching & coding tutorials',
      category: 'Technology',
      membershipPrice: '$30/month',
      experienceDrops: [
        { id: 'tm-drop-1', title: '1-Hour Career Coaching Call', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Personal career advice session - resume review, interview prep & more', numberOfWinners: 5, entries: 2890, media: [] }
      ],
      stats: { totalEarnings: 89000, pendingPayout: 12000, totalMembers: 680, thisMonthNewMembers: 89, contentViews: 34500 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 11 - Comedy Chris (Comedy/Entertainment)
  if (!db.creators['comedy-chris']) {
    db.creators['comedy-chris'] = {
      id: 'comedy-chris',
      username: 'comedy-chris',
      name: 'Chris Taylor',
      email: 'chris@example.com',
      photo: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400',
      bio: 'Stand-up comedian. Netflix special coming soon. Daily jokes & BTS content',
      category: 'Comedy',
      membershipPrice: '$4/month',
      experienceDrops: [
        { id: 'cc-drop-1', title: 'VIP Comedy Show + After Party', type: 'Exclusive Event Access', status: 'active', drawDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Front row seats to my show + hang out backstage!', numberOfWinners: 4, entries: 4521, media: [] }
      ],
      stats: { totalEarnings: 28900, pendingPayout: 3400, totalMembers: 2100, thisMonthNewMembers: 345, contentViews: 156000 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 12 - Photography Pro (Photography)
  if (!db.creators['photo-emma']) {
    db.creators['photo-emma'] = {
      id: 'photo-emma',
      username: 'photo-emma',
      name: 'Emma Rodriguez',
      email: 'emma.photo@example.com',
      photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
      bio: 'Award-winning photographer. Lightroom presets, tutorials & photo walks',
      category: 'Photography',
      membershipPrice: '$12/month',
      experienceDrops: [
        { id: 'pe-drop-1', title: 'Private Photography Workshop', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'A full day workshop with me - location of your choice!', numberOfWinners: 2, entries: 1234, media: [] }
      ],
      stats: { totalEarnings: 52000, pendingPayout: 6800, totalMembers: 920, thisMonthNewMembers: 78, contentViews: 67800 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 13 - Crypto Dan (Finance/Crypto)
  if (!db.creators['crypto-dan']) {
    db.creators['crypto-dan'] = {
      id: 'crypto-dan',
      username: 'crypto-dan',
      name: 'Daniel Kim',
      email: 'dan@example.com',
      photo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400',
      bio: 'Crypto trader & financial analyst. Daily market insights & trading strategies',
      category: 'Finance',
      membershipPrice: '$50/month',
      experienceDrops: [
        { id: 'cd-drop-1', title: 'Private Trading Mentorship Day', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Full day of live trading with me - learn my exact strategies', numberOfWinners: 1, entries: 987, media: [] }
      ],
      stats: { totalEarnings: 178000, pendingPayout: 24000, totalMembers: 420, thisMonthNewMembers: 56, contentViews: 23400 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 14 - Yoga Maya (Yoga/Mindfulness)
  if (!db.creators['yoga-maya']) {
    db.creators['yoga-maya'] = {
      id: 'yoga-maya',
      username: 'yoga-maya',
      name: 'Maya Singh',
      email: 'maya@example.com',
      photo: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400',
      bio: 'Certified yoga instructor & meditation guide. Find your inner peace',
      category: 'Wellness',
      membershipPrice: '$7/month',
      experienceDrops: [
        { id: 'ym-drop-1', title: 'Sunrise Yoga Retreat in Costa Rica', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '3-day wellness retreat with daily yoga & meditation sessions', numberOfWinners: 2, entries: 3456, media: [] }
      ],
      stats: { totalEarnings: 41200, pendingPayout: 5600, totalMembers: 1680, thisMonthNewMembers: 198, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 15 - Racing Ryan (Motorsports)
  if (!db.creators['racing-ryan']) {
    db.creators['racing-ryan'] = {
      id: 'racing-ryan',
      username: 'racing-ryan',
      name: 'Ryan Mitchell',
      email: 'ryan@example.com',
      photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400',
      bio: 'Professional race car driver. Behind-the-scenes of motorsport life',
      category: 'Sports',
      membershipPrice: '$15/month',
      experienceDrops: [
        { id: 'rr-drop-1', title: 'Hot Lap Experience at the Track', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Ride shotgun with me for 3 hot laps around the circuit!', numberOfWinners: 3, entries: 2678, media: [] }
      ],
      stats: { totalEarnings: 78500, pendingPayout: 11200, totalMembers: 1120, thisMonthNewMembers: 145, contentViews: 67500 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Creator 16 - Indie Singer Sophie (Music/Indie)
  if (!db.creators['indie-sophie']) {
    db.creators['indie-sophie'] = {
      id: 'indie-sophie',
      username: 'indie-sophie',
      name: 'Sophie Waters',
      email: 'sophie@example.com',
      photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400',
      bio: 'Indie singer-songwriter. Exclusive acoustic sessions & songwriting breakdowns',
      category: 'Music',
      membershipPrice: '$9/month',
      experienceDrops: [
        { id: 'is-drop-1', title: 'Private Living Room Concert', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'I\'ll perform a private concert just for you and 5 friends!', numberOfWinners: 1, entries: 1567, media: [] }
      ],
      stats: { totalEarnings: 36700, pendingPayout: 4900, totalMembers: 980, thisMonthNewMembers: 123, contentViews: 54300 },
      createdAt: new Date().toISOString()
    };
  }

  // === MORE CREATORS FOR EACH NICHE ===

  // GAMING - More creators
  if (!db.creators['gaming-luna']) {
    db.creators['gaming-luna'] = {
      id: 'gaming-luna', username: 'gaming-luna', name: 'Luna Storm',
      email: 'luna@example.com', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400',
      bio: 'Competitive Valorant player & Twitch streamer. Daily tips & gameplay',
      category: 'Gaming', membershipPrice: '$8/month',
      experienceDrops: [{ id: 'gl-drop-1', title: 'Play Ranked With Me', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Play 5 ranked games with me on stream!', numberOfWinners: 2, entries: 3421, media: [] }],
      stats: { totalEarnings: 45000, pendingPayout: 6200, totalMembers: 1890, thisMonthNewMembers: 234, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['gaming-kai']) {
    db.creators['gaming-kai'] = {
      id: 'gaming-kai', username: 'gaming-kai', name: 'Kai Gamer',
      email: 'kai@example.com', photo: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400',
      bio: 'Minecraft builder & speedrunner. World record holder. Building tutorials',
      category: 'Gaming', membershipPrice: '$6/month',
      experienceDrops: [{ id: 'gk-drop-1', title: 'Build With Me Session', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '2-hour building session on my private server', numberOfWinners: 3, entries: 2156, media: [] }],
      stats: { totalEarnings: 28000, pendingPayout: 3800, totalMembers: 1245, thisMonthNewMembers: 167, contentViews: 67000 },
      createdAt: new Date().toISOString()
    };
  }

  // WELLNESS - More creators
  if (!db.creators['wellness-james']) {
    db.creators['wellness-james'] = {
      id: 'wellness-james', username: 'wellness-james', name: 'James Mindful',
      email: 'james@example.com', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      bio: 'Meditation teacher & breathwork coach. 10+ years experience. Find your calm',
      category: 'Wellness', membershipPrice: '$10/month',
      experienceDrops: [{ id: 'wj-drop-1', title: 'Private Meditation Retreat', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Weekend meditation retreat in the mountains', numberOfWinners: 5, entries: 1876, media: [] }],
      stats: { totalEarnings: 52000, pendingPayout: 7100, totalMembers: 1120, thisMonthNewMembers: 145, contentViews: 45000 },
      createdAt: new Date().toISOString()
    };
  }

  // MUSIC - More creators
  if (!db.creators['music-aria']) {
    db.creators['music-aria'] = {
      id: 'music-aria', username: 'music-aria', name: 'Aria Beats',
      email: 'aria@example.com', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400',
      bio: 'Pop singer & songwriter. Behind-the-scenes of music production',
      category: 'Music', membershipPrice: '$12/month',
      experienceDrops: [{ id: 'ma-drop-1', title: 'Studio Session Visit', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Visit my studio and watch me record!', numberOfWinners: 2, entries: 2890, media: [] }],
      stats: { totalEarnings: 67000, pendingPayout: 9200, totalMembers: 1560, thisMonthNewMembers: 198, contentViews: 78000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['music-beats']) {
    db.creators['music-beats'] = {
      id: 'music-beats', username: 'music-beats', name: 'Producer Max',
      email: 'max@example.com', photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400',
      bio: 'Hip-hop producer. Worked with major artists. Beat-making tutorials & samples',
      category: 'Music', membershipPrice: '$15/month',
      experienceDrops: [{ id: 'mb-drop-1', title: 'Custom Beat Production', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'I\'ll produce a custom beat just for you!', numberOfWinners: 1, entries: 1234, media: [] }],
      stats: { totalEarnings: 89000, pendingPayout: 12000, totalMembers: 890, thisMonthNewMembers: 112, contentViews: 56000 },
      createdAt: new Date().toISOString()
    };
  }

  // SPORTS - More creators
  if (!db.creators['sports-lisa']) {
    db.creators['sports-lisa'] = {
      id: 'sports-lisa', username: 'sports-lisa', name: 'Lisa Tennis',
      email: 'lisa@example.com', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
      bio: 'Pro tennis player. Grand Slam competitor. Training tips & match analysis',
      category: 'Sports', membershipPrice: '$20/month',
      experienceDrops: [{ id: 'sl-drop-1', title: 'Tennis Lesson With Me', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '2-hour private tennis coaching session', numberOfWinners: 2, entries: 1567, media: [] }],
      stats: { totalEarnings: 78000, pendingPayout: 10500, totalMembers: 780, thisMonthNewMembers: 89, contentViews: 45000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['sports-mike']) {
    db.creators['sports-mike'] = {
      id: 'sports-mike', username: 'sports-mike', name: 'Mike Surfer',
      email: 'mike.surf@example.com', photo: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400',
      bio: 'Pro surfer & ocean advocate. Surf trips, wave forecasting & ocean life',
      category: 'Sports', membershipPrice: '$11/month',
      experienceDrops: [{ id: 'sm-drop-1', title: 'Surf Trip to Bali', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '5-day surf trip with daily lessons', numberOfWinners: 2, entries: 3421, media: [] }],
      stats: { totalEarnings: 56000, pendingPayout: 7800, totalMembers: 1340, thisMonthNewMembers: 167, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }

  // FASHION - More creators
  if (!db.creators['fashion-marc']) {
    db.creators['fashion-marc'] = {
      id: 'fashion-marc', username: 'fashion-marc', name: 'Marc Styles',
      email: 'marc@example.com', photo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400',
      bio: 'Men\'s fashion expert & stylist. NYC Fashion Week insider',
      category: 'Fashion', membershipPrice: '$14/month',
      experienceDrops: [{ id: 'fm-drop-1', title: 'Wardrobe Makeover Day', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Full day wardrobe overhaul + shopping trip', numberOfWinners: 1, entries: 987, media: [] }],
      stats: { totalEarnings: 45000, pendingPayout: 6100, totalMembers: 670, thisMonthNewMembers: 78, contentViews: 34000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['fashion-zara']) {
    db.creators['fashion-zara'] = {
      id: 'fashion-zara', username: 'fashion-zara', name: 'Zara Glam',
      email: 'zara@example.com', photo: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=400',
      bio: 'Sustainable fashion advocate. Thrift hauls & eco-friendly style tips',
      category: 'Fashion', membershipPrice: '$7/month',
      experienceDrops: [{ id: 'fz-drop-1', title: 'Thrift Shopping Adventure', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Day of thrift shopping + styling session', numberOfWinners: 3, entries: 2345, media: [] }],
      stats: { totalEarnings: 34000, pendingPayout: 4600, totalMembers: 1450, thisMonthNewMembers: 189, contentViews: 67000 },
      createdAt: new Date().toISOString()
    };
  }

  // TRAVEL - More creators
  if (!db.creators['travel-nina']) {
    db.creators['travel-nina'] = {
      id: 'travel-nina', username: 'travel-nina', name: 'Nina Nomad',
      email: 'nina@example.com', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
      bio: 'Solo female traveller. Safety tips, hidden gems & budget travel hacks',
      category: 'Travel', membershipPrice: '$9/month',
      experienceDrops: [{ id: 'tn-drop-1', title: 'Japan Trip With Me', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '10-day Japan adventure - all included!', numberOfWinners: 2, entries: 4567, media: [] }],
      stats: { totalEarnings: 48000, pendingPayout: 6500, totalMembers: 1780, thisMonthNewMembers: 223, contentViews: 98000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['travel-adventure']) {
    db.creators['travel-adventure'] = {
      id: 'travel-adventure', username: 'travel-adventure', name: 'Tom Explorer',
      email: 'tom@example.com', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
      bio: 'Adventure travel & extreme sports. Skydiving, bungee, mountain climbing',
      category: 'Travel', membershipPrice: '$13/month',
      experienceDrops: [{ id: 'ta-drop-1', title: 'Skydiving Experience', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Tandem skydive with me - I\'ll film it all!', numberOfWinners: 1, entries: 2890, media: [] }],
      stats: { totalEarnings: 62000, pendingPayout: 8400, totalMembers: 1120, thisMonthNewMembers: 145, contentViews: 78000 },
      createdAt: new Date().toISOString()
    };
  }

  // TECHNOLOGY - More creators
  if (!db.creators['tech-sarah']) {
    db.creators['tech-sarah'] = {
      id: 'tech-sarah', username: 'tech-sarah', name: 'Sarah Code',
      email: 'sarah.code@example.com', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400',
      bio: 'Frontend developer & UI designer. React tutorials & career advice',
      category: 'Technology', membershipPrice: '$20/month',
      experienceDrops: [{ id: 'ts-drop-1', title: 'Portfolio Review Session', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '1-hour portfolio review + career guidance', numberOfWinners: 5, entries: 1876, media: [] }],
      stats: { totalEarnings: 72000, pendingPayout: 9800, totalMembers: 890, thisMonthNewMembers: 112, contentViews: 56000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['tech-ai']) {
    db.creators['tech-ai'] = {
      id: 'tech-ai', username: 'tech-ai', name: 'Dr. AI Smith',
      email: 'ai.smith@example.com', photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400',
      bio: 'AI researcher & educator. Making machine learning accessible to everyone',
      category: 'Technology', membershipPrice: '$25/month',
      experienceDrops: [{ id: 'tai-drop-1', title: 'AI Project Mentorship', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Month-long mentorship on your AI project', numberOfWinners: 2, entries: 1234, media: [] }],
      stats: { totalEarnings: 95000, pendingPayout: 13000, totalMembers: 560, thisMonthNewMembers: 67, contentViews: 34000 },
      createdAt: new Date().toISOString()
    };
  }

  // COMEDY - More creators
  if (!db.creators['comedy-amy']) {
    db.creators['comedy-amy'] = {
      id: 'comedy-amy', username: 'comedy-amy', name: 'Amy Laughs',
      email: 'amy@example.com', photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
      bio: 'Stand-up comedian & writer. Daily jokes, sketch comedy & improv tips',
      category: 'Comedy', membershipPrice: '$5/month',
      experienceDrops: [{ id: 'ca-drop-1', title: 'Write Jokes With Me', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Brainstorm and write comedy together!', numberOfWinners: 3, entries: 3456, media: [] }],
      stats: { totalEarnings: 32000, pendingPayout: 4300, totalMembers: 2100, thisMonthNewMembers: 278, contentViews: 123000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['comedy-sketch']) {
    db.creators['comedy-sketch'] = {
      id: 'comedy-sketch', username: 'comedy-sketch', name: 'Sketch Bros',
      email: 'sketch@example.com', photo: 'https://images.unsplash.com/photo-1552058544-f2b08422138a?w=400',
      bio: 'Sketch comedy duo. BTS of our videos & collab opportunities',
      category: 'Comedy', membershipPrice: '$6/month',
      experienceDrops: [{ id: 'cs-drop-1', title: 'Be In Our Next Video', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Appear as a guest in our sketch!', numberOfWinners: 1, entries: 4521, media: [] }],
      stats: { totalEarnings: 41000, pendingPayout: 5600, totalMembers: 1890, thisMonthNewMembers: 234, contentViews: 156000 },
      createdAt: new Date().toISOString()
    };
  }

  // PHOTOGRAPHY - More creators
  if (!db.creators['photo-landscape']) {
    db.creators['photo-landscape'] = {
      id: 'photo-landscape', username: 'photo-landscape', name: 'Jake Landscapes',
      email: 'jake.photo@example.com', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      bio: 'Landscape photographer. National Geographic featured. Editing tutorials',
      category: 'Photography', membershipPrice: '$15/month',
      experienceDrops: [{ id: 'pl-drop-1', title: 'Iceland Photo Trip', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: '7-day photography expedition in Iceland', numberOfWinners: 2, entries: 2678, media: [] }],
      stats: { totalEarnings: 67000, pendingPayout: 9100, totalMembers: 780, thisMonthNewMembers: 98, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['photo-portrait']) {
    db.creators['photo-portrait'] = {
      id: 'photo-portrait', username: 'photo-portrait', name: 'Maria Portraits',
      email: 'maria@example.com', photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400',
      bio: 'Celebrity portrait photographer. Lighting techniques & posing guides',
      category: 'Photography', membershipPrice: '$18/month',
      experienceDrops: [{ id: 'pp-drop-1', title: 'Professional Headshot Session', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Full professional photoshoot + edited photos', numberOfWinners: 3, entries: 1567, media: [] }],
      stats: { totalEarnings: 78000, pendingPayout: 10600, totalMembers: 670, thisMonthNewMembers: 84, contentViews: 45000 },
      createdAt: new Date().toISOString()
    };
  }

  // FINANCE - More creators
  if (!db.creators['finance-invest']) {
    db.creators['finance-invest'] = {
      id: 'finance-invest', username: 'finance-invest', name: 'Invest With Emma',
      email: 'emma.invest@example.com', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400',
      bio: 'Stock market analyst. Daily market insights & long-term investing strategies',
      category: 'Finance', membershipPrice: '$35/month',
      experienceDrops: [{ id: 'fi-drop-1', title: 'Portfolio Review Call', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Personal portfolio review & recommendations', numberOfWinners: 3, entries: 876, media: [] }],
      stats: { totalEarnings: 125000, pendingPayout: 17000, totalMembers: 450, thisMonthNewMembers: 56, contentViews: 34000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['finance-budget']) {
    db.creators['finance-budget'] = {
      id: 'finance-budget', username: 'finance-budget', name: 'Budget Boss',
      email: 'budget@example.com', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400',
      bio: 'Personal finance coach. Debt-free journey, budgeting & saving money',
      category: 'Finance', membershipPrice: '$8/month',
      experienceDrops: [{ id: 'fb-drop-1', title: 'Budget Planning Session', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Create a personalised budget plan together', numberOfWinners: 5, entries: 2345, media: [] }],
      stats: { totalEarnings: 48000, pendingPayout: 6500, totalMembers: 1560, thisMonthNewMembers: 198, contentViews: 78000 },
      createdAt: new Date().toISOString()
    };
  }

  // FOOD & COOKING - More creators
  if (!db.creators['food-baker']) {
    db.creators['food-baker'] = {
      id: 'food-baker', username: 'food-baker', name: 'Baker Ben',
      email: 'ben@example.com', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
      bio: 'Pastry chef & bread baker. Sourdough secrets & dessert recipes',
      category: 'Food & Cooking', membershipPrice: '$10/month',
      experienceDrops: [{ id: 'fba-drop-1', title: 'Baking Day at My Kitchen', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 18 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Full day baking session at my professional kitchen', numberOfWinners: 2, entries: 1876, media: [] }],
      stats: { totalEarnings: 52000, pendingPayout: 7100, totalMembers: 1120, thisMonthNewMembers: 145, contentViews: 67000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['food-vegan']) {
    db.creators['food-vegan'] = {
      id: 'food-vegan', username: 'food-vegan', name: 'Vegan Vibes',
      email: 'vegan@example.com', photo: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400',
      bio: 'Plant-based chef. Easy vegan recipes & meal prep guides',
      category: 'Food & Cooking', membershipPrice: '$7/month',
      experienceDrops: [{ id: 'fv-drop-1', title: 'Vegan Cooking Class', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Live cooking session - make 3 dishes together!', numberOfWinners: 5, entries: 2567, media: [] }],
      stats: { totalEarnings: 38000, pendingPayout: 5200, totalMembers: 1450, thisMonthNewMembers: 187, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }

  // FITNESS - More creators
  if (!db.creators['fitness-yoga']) {
    db.creators['fitness-yoga'] = {
      id: 'fitness-yoga', username: 'fitness-yoga', name: 'Yoga With Tom',
      email: 'tom.yoga@example.com', photo: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400',
      bio: 'Yoga instructor & flexibility coach. Daily flows & mobility routines',
      category: 'Fitness', membershipPrice: '$9/month',
      experienceDrops: [{ id: 'fy-drop-1', title: 'Private Yoga Retreat', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 40 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Weekend yoga retreat at a mountain lodge', numberOfWinners: 4, entries: 2890, media: [] }],
      stats: { totalEarnings: 45000, pendingPayout: 6100, totalMembers: 1340, thisMonthNewMembers: 167, contentViews: 78000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['fitness-strength']) {
    db.creators['fitness-strength'] = {
      id: 'fitness-strength', username: 'fitness-strength', name: 'Strong Sara',
      email: 'sara@example.com', photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
      bio: 'Powerlifter & strength coach. Form tutorials & program design',
      category: 'Fitness', membershipPrice: '$12/month',
      experienceDrops: [{ id: 'fs-drop-1', title: 'Personal Training Day', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Full day of training at my gym', numberOfWinners: 2, entries: 1567, media: [] }],
      stats: { totalEarnings: 58000, pendingPayout: 7900, totalMembers: 980, thisMonthNewMembers: 123, contentViews: 56000 },
      createdAt: new Date().toISOString()
    };
  }

  // ART & DESIGN - More creators
  if (!db.creators['art-digital']) {
    db.creators['art-digital'] = {
      id: 'art-digital', username: 'art-digital', name: 'Digital Dan',
      email: 'dan.art@example.com', photo: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400',
      bio: 'Digital artist & illustrator. Procreate tutorials & brush packs',
      category: 'Art & Design', membershipPrice: '$11/month',
      experienceDrops: [{ id: 'ad-drop-1', title: 'Custom Portrait Commission', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'I\'ll create a custom digital portrait of you!', numberOfWinners: 3, entries: 2345, media: [] }],
      stats: { totalEarnings: 52000, pendingPayout: 7100, totalMembers: 1120, thisMonthNewMembers: 145, contentViews: 67000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['art-traditional']) {
    db.creators['art-traditional'] = {
      id: 'art-traditional', username: 'art-traditional', name: 'Paint With Lily',
      email: 'lily@example.com', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400',
      bio: 'Oil painter & art teacher. Traditional techniques & composition tips',
      category: 'Art & Design', membershipPrice: '$14/month',
      experienceDrops: [{ id: 'at-drop-1', title: 'Private Painting Lesson', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Half-day painting session at my studio', numberOfWinners: 2, entries: 1234, media: [] }],
      stats: { totalEarnings: 62000, pendingPayout: 8400, totalMembers: 780, thisMonthNewMembers: 98, contentViews: 45000 },
      createdAt: new Date().toISOString()
    };
  }

  // NEW NICHE: BEAUTY & MAKEUP
  if (!db.creators['beauty-glam']) {
    db.creators['beauty-glam'] = {
      id: 'beauty-glam', username: 'beauty-glam', name: 'Glam Queen',
      email: 'glam@example.com', photo: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=400',
      bio: 'Celebrity makeup artist. Red carpet looks & everyday glam tutorials',
      category: 'Beauty', membershipPrice: '$13/month',
      experienceDrops: [{ id: 'bg-drop-1', title: 'Glam Makeover Experience', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 16 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Full glam makeover + professional photoshoot', numberOfWinners: 2, entries: 3456, media: [] }],
      stats: { totalEarnings: 67000, pendingPayout: 9100, totalMembers: 1560, thisMonthNewMembers: 198, contentViews: 123000 },
      createdAt: new Date().toISOString()
    };
  }
  if (!db.creators['beauty-skincare']) {
    db.creators['beauty-skincare'] = {
      id: 'beauty-skincare', username: 'beauty-skincare', name: 'Skin By Jen',
      email: 'jen@example.com', photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400',
      bio: 'Dermatologist & skincare expert. Science-backed routines & product reviews',
      category: 'Beauty', membershipPrice: '$16/month',
      experienceDrops: [{ id: 'bs-drop-1', title: 'Skin Consultation Call', type: '1-on-1 Video Call', status: 'active', drawDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Personal skin analysis + custom routine plan', numberOfWinners: 5, entries: 2890, media: [] }],
      stats: { totalEarnings: 89000, pendingPayout: 12100, totalMembers: 1120, thisMonthNewMembers: 145, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }

  // NEW NICHE: PARENTING
  if (!db.creators['parenting-mom']) {
    db.creators['parenting-mom'] = {
      id: 'parenting-mom', username: 'parenting-mom', name: 'Mom Life Lisa',
      email: 'lisa.mom@example.com', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
      bio: 'Mom of 4 & parenting coach. Real tips for real parents. No judgement zone',
      category: 'Parenting', membershipPrice: '$6/month',
      experienceDrops: [{ id: 'pm-drop-1', title: 'Family Day Out', type: 'Group Experience', status: 'active', drawDate: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'Fun family day at the theme park with me!', numberOfWinners: 3, entries: 1876, media: [] }],
      stats: { totalEarnings: 34000, pendingPayout: 4600, totalMembers: 1890, thisMonthNewMembers: 234, contentViews: 98000 },
      createdAt: new Date().toISOString()
    };
  }

  // NEW NICHE: PETS
  if (!db.creators['pets-dogs']) {
    db.creators['pets-dogs'] = {
      id: 'pets-dogs', username: 'pets-dogs', name: 'Dog Dad Dave',
      email: 'dave@example.com', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
      bio: 'Professional dog trainer. Behaviour tips, training guides & cute content',
      category: 'Pets', membershipPrice: '$8/month',
      experienceDrops: [{ id: 'pd-drop-1', title: 'Private Training Session', type: '1-on-1 Experience', status: 'active', drawDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], description: 'In-person training session for you and your pup', numberOfWinners: 3, entries: 2567, media: [] }],
      stats: { totalEarnings: 42000, pendingPayout: 5700, totalMembers: 1340, thisMonthNewMembers: 167, contentViews: 89000 },
      createdAt: new Date().toISOString()
    };
  }

  // Demo Fan
  if (!db.fans) db.fans = {};
  if (!db.fans['demo-fan']) {
    db.fans['demo-fan'] = {
      id: 'demo-fan',
      name: 'Alex Johnson',
      email: 'alex@example.com',
      photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400',
      createdAt: new Date().toISOString()
    };
    
    // Add demo subscriptions
    if (!db.subscriptions) db.subscriptions = [];
    db.subscriptions.push(
      { id: 'sub-1', fanId: 'demo-fan', creatorId: 'sarah-chen', joinedAt: new Date(Date.now() - 7 * 86400000).toISOString(), status: 'active' },
      { id: 'sub-2', fanId: 'demo-fan', creatorId: 'marcus-gaming', joinedAt: new Date(Date.now() - 3 * 86400000).toISOString(), status: 'active' }
    );
  }
  // Ensure demo fan has photo (migration for existing data)
  if (db.fans['demo-fan'] && !db.fans['demo-fan'].photo) {
    db.fans['demo-fan'].photo = 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400';
    saveDB(db);
  }

  // Demo Posts (OnlyFans-style content feed)
  if (!db.posts || db.posts.length === 0) {
    db.posts = [
      // Sarah Chen's posts
      {
        id: 'post-1',
        creatorId: 'sarah-chen',
        type: 'photo',
        caption: 'Good morning everyone! Starting the day with sunrise yoga on the beach. This is what inner peace looks like. Ready to take on whatever comes my way today.',
        channel: 'behind-the-scenes',
        mediaUrls: ['https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800'],
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        likes: 234,
        comments: [
          { id: 'c1', fanId: 'demo-fan', fanName: 'Alex J.', fanPhoto: 'https://ui-avatars.com/api/?name=Alex+J&background=3B82F6&color=fff', text: 'This is so peaceful!', createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString() },
          { id: 'c2', fanId: 'fan-2', fanName: 'Emma R.', fanPhoto: 'https://ui-avatars.com/api/?name=Emma+R&background=06B6D4&color=fff', text: 'Goals! Where is this?', createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString() }
        ],
        isPinned: true
      },
      {
        id: 'post-2',
        creatorId: 'sarah-chen',
        type: 'photo',
        caption: 'Behind the scenes from my upcoming wellness retreat prep. So excited to share this journey with you all! The location is absolutely stunning.',
        channel: 'exclusive-updates',
        mediaUrls: ['https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800', 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800'],
        createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
        likes: 456,
        comments: [
          { id: 'c3', fanId: 'fan-3', fanName: 'Mike L.', fanPhoto: 'https://ui-avatars.com/api/?name=Mike+L&background=8B5CF6&color=fff', text: 'Cannot wait for this!', createdAt: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString() }
        ],
        isPinned: false
      },
      {
        id: 'post-3',
        creatorId: 'sarah-chen',
        type: 'text',
        caption: 'Quick wellness tip: Start your morning with 5 minutes of deep breathing before checking your phone. It sets the tone for your entire day. Trust me on this one.\n\nI have been doing this for 3 years and it has completely transformed my mornings. Who else is going to try this tomorrow?',
        channel: 'personal',
        mediaUrls: [],
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        likes: 189,
        comments: [],
        isPinned: false
      },
      {
        id: 'post-4',
        creatorId: 'sarah-chen',
        type: 'photo',
        caption: 'Sneak peek at the new merch collection! These yoga mats are handmade with sustainable materials. Members get early access + 30% off.',
        channel: 'exclusive-updates',
        mediaUrls: ['https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=800'],
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        likes: 312,
        comments: [
          { id: 'c4', fanId: 'demo-fan', fanName: 'Alex J.', fanPhoto: 'https://ui-avatars.com/api/?name=Alex+J&background=3B82F6&color=fff', text: 'Just ordered mine!', createdAt: new Date(Date.now() - 1.5 * 24 * 60 * 60 * 1000).toISOString() }
        ],
        isPinned: false
      },
      {
        id: 'post-5',
        creatorId: 'sarah-chen',
        type: 'photo',
        caption: 'My daily smoothie recipe that keeps me energized all morning. Swipe for the full recipe breakdown. DM me if you want the shopping list!',
        channel: 'behind-the-scenes',
        mediaUrls: ['https://images.unsplash.com/photo-1638176066666-ffb2f013c7dd?w=800'],
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        likes: 267,
        comments: [],
        isPinned: false
      },
      // Marcus Gaming's posts
      {
        id: 'post-6',
        creatorId: 'marcus-gaming',
        type: 'photo',
        caption: 'HUGE WIN today! Just clutched a 1v4 in the finals. This is what 10 years of practice looks like. Full breakdown video coming soon for members only.',
        channel: 'exclusive-updates',
        mediaUrls: ['https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800'],
        createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        likes: 892,
        comments: [
          { id: 'c5', fanId: 'fan-4', fanName: 'Tyler K.', fanPhoto: 'https://ui-avatars.com/api/?name=Tyler+K&background=EF4444&color=fff', text: 'GOAT status confirmed', createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
          { id: 'c6', fanId: 'fan-5', fanName: 'Jordan P.', fanPhoto: 'https://ui-avatars.com/api/?name=Jordan+P&background=22C55E&color=fff', text: 'Teach me your ways!', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() }
        ],
        isPinned: true
      },
      {
        id: 'post-7',
        creatorId: 'marcus-gaming',
        type: 'photo',
        caption: 'New setup reveal! Finally upgraded my streaming station. What do you think? The RGB is a bit much but I love it.',
        channel: 'behind-the-scenes',
        mediaUrls: ['https://images.unsplash.com/photo-1612287230202-1ff1d85d1bdf?w=800', 'https://images.unsplash.com/photo-1593640408182-31c70c8268f5?w=800'],
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        likes: 1234,
        comments: [],
        isPinned: false
      },
      {
        id: 'post-8',
        creatorId: 'marcus-gaming',
        type: 'text',
        caption: 'Pro tip for all my aspiring gamers: Your sleep schedule matters more than your aim practice. I know it sounds crazy but trust me.\n\nI improved my reaction time by 15% just by getting proper sleep. Drop a comment if you want me to make a full video on this!',
        channel: 'personal',
        mediaUrls: [],
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        likes: 567,
        comments: [
          { id: 'c7', fanId: 'demo-fan', fanName: 'Alex J.', fanPhoto: 'https://ui-avatars.com/api/?name=Alex+J&background=3B82F6&color=fff', text: 'Yes please! Need this video', createdAt: new Date(Date.now() - 1.8 * 24 * 60 * 60 * 1000).toISOString() }
        ],
        isPinned: false
      },
      {
        id: 'post-9',
        creatorId: 'marcus-gaming',
        type: 'photo',
        caption: 'Tournament prep mode activated. Going live in 2 hours with some warm-up games. Members get early access to the stream!',
        channel: 'exclusive-updates',
        mediaUrls: ['https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=800'],
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        likes: 445,
        comments: [],
        isPinned: false
      }
    ];
  }

  // Demo Stories
  if (!db.stories || db.stories.length === 0) {
    db.stories = [
      {
        id: 'story-1',
        creatorId: 'sarah-chen',
        mediaUrl: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800',
        caption: 'Morning vibes',
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        expiresAt: new Date(Date.now() + 22 * 60 * 60 * 1000).toISOString(),
        views: 234,
        viewedBy: []
      },
      {
        id: 'story-2',
        creatorId: 'sarah-chen',
        mediaUrl: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800',
        caption: 'New routine dropping soon!',
        createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        expiresAt: new Date(Date.now() + 23 * 60 * 60 * 1000).toISOString(),
        views: 156,
        viewedBy: []
      },
      {
        id: 'story-3',
        creatorId: 'marcus-gaming',
        mediaUrl: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800',
        caption: 'LIVE in 30 mins!',
        createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        expiresAt: new Date(Date.now() + 23.5 * 60 * 60 * 1000).toISOString(),
        views: 489,
        viewedBy: []
      }
    ];
  }

  // Demo Conversations & Messages
  if (!db.conversations || db.conversations.length === 0) {
    db.conversations = [
      {
        id: 'conv-1',
        participants: ['demo-fan', 'sarah-chen'],
        lastMessage: 'Thank you so much for the kind words! See you at the next live session!',
        lastMessageAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        unreadCount: { 'demo-fan': 1, 'sarah-chen': 0 }
      },
      {
        id: 'conv-2',
        participants: ['demo-fan', 'marcus-gaming'],
        lastMessage: 'GG! That was an epic match. Thanks for watching!',
        lastMessageAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
        unreadCount: { 'demo-fan': 0, 'marcus-gaming': 0 }
      }
    ];
    db.messages = [
      // Sarah Chen conversation
      { id: 'msg-1', conversationId: 'conv-1', senderId: 'demo-fan', text: 'Hi Sarah! I love your content so much. Your morning routines have really helped me.', createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
      { id: 'msg-2', conversationId: 'conv-1', senderId: 'sarah-chen', text: 'Thank you so much for the kind words! See you at the next live session!', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
      // Marcus Gaming conversation
      { id: 'msg-3', conversationId: 'conv-2', senderId: 'demo-fan', text: 'That clutch play was insane! How did you pull that off?', createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() },
      { id: 'msg-4', conversationId: 'conv-2', senderId: 'marcus-gaming', text: 'GG! That was an epic match. Thanks for watching!', createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString() }
    ];
  }

  // Demo Notifications
  if (!db.notifications || db.notifications.length === 0) {
    db.notifications = [
      { id: 'notif-1', userId: 'demo-fan', type: 'new_post', title: 'Sarah Chen posted', message: 'Good morning everyone! Starting the day with sunrise yoga...', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), read: false, link: '/c/sarah-chen/feed' },
      { id: 'notif-2', userId: 'demo-fan', type: 'story', title: 'Sarah Chen added a story', message: 'Check out their latest story!', createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), read: false, link: '/c/sarah-chen/feed' },
      { id: 'notif-3', userId: 'demo-fan', type: 'experience_drop', title: 'New Experience Drop!', message: 'Marcus Gaming just launched "Gaming Session + Signed Merch!"', createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), read: true, link: '/c/marcus-gaming' },
      { id: 'notif-4', userId: 'demo-fan', type: 'message', title: 'New message from Sarah Chen', message: 'Thank you so much for the kind words!', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), read: false, link: '/messages' },
      { id: 'notif-5', userId: 'demo-fan', type: 'winner', title: 'Congratulations!', message: 'You won the Holiday Wellness Package giveaway!', createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), read: true, link: '/fan/dashboard' },
      // Creator notifications
      { id: 'notif-6', userId: 'sarah-chen', type: 'new_member', title: 'New Member!', message: 'Alex Johnson joined your inner circle', createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), read: true, link: '/creator/dashboard' },
      { id: 'notif-7', userId: 'sarah-chen', type: 'tip', title: 'You received a tip!', message: 'Alex Johnson tipped $10 on your post', createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), read: false, link: '/creator/dashboard' },
      { id: 'notif-8', userId: 'sarah-chen', type: 'milestone', title: 'Milestone Reached!', message: 'You hit 850 members!', createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(), read: false, link: '/creator/dashboard' }
    ];
  }

  // Demo Tips
  if (!db.tips || db.tips.length === 0) {
    db.tips = [
      { id: 'tip-1', fromId: 'demo-fan', toId: 'sarah-chen', postId: 'post-1', amount: 10, message: 'Love this content!', createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString() },
      { id: 'tip-2', fromId: 'fan-2', toId: 'sarah-chen', postId: 'post-2', amount: 5, message: '', createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString() },
      { id: 'tip-3', fromId: 'fan-3', toId: 'marcus-gaming', postId: 'post-6', amount: 20, message: 'GOAT!', createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() }
    ];
  }

  // Demo Saved Posts
  if (!db.savedPosts || db.savedPosts.length === 0) {
    db.savedPosts = [
      { id: 'save-1', fanId: 'demo-fan', postId: 'post-1', savedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString() },
      { id: 'save-2', fanId: 'demo-fan', postId: 'post-6', savedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() }
    ];
  }
  
  saveDB(db);
  return db;
};

// ============ CONTEXT ============
const AuthContext = React.createContext();
const useAuth = () => React.useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initDemoData();
    const stored = localStorage.getItem('fanpass_demo_user');
    if (stored) setUser(JSON.parse(stored));
    setLoading(false);
  }, []);

  const loginAsCreator = (creatorId) => {
    const db = getDB();
    const creator = db.creators[creatorId];
    if (!creator) return null;
    const userData = { ...creator, role: 'creator' };
    setUser(userData);
    localStorage.setItem('fanpass_demo_user', JSON.stringify(userData));
    return userData;
  };

  const registerCreator = (data) => {
    const db = getDB();
    const creatorId = data.name.toLowerCase().replace(/\s+/g, '-');
    const creator = {
      id: creatorId,
      username: creatorId,
      name: data.name,
      email: data.email,
      photo: `https://ui-avatars.com/api/?name=${encodeURIComponent(data.name)}&background=d4a853&color=0a1628&size=200`,
      bio: '',
      membershipPrice: '$5/month',
      experienceDrop: { title: '', type: '', drawDate: '', description: '', numberOfWinners: 1, pastWinners: [] },
      bankDetails: null,
      content: [],
      stats: { totalEarnings: 0, pendingPayout: 0, totalMembers: 0 },
      createdAt: new Date().toISOString()
    };
    db.creators[creatorId] = creator;
    saveDB(db);
    const userData = { ...creator, role: 'creator' };
    setUser(userData);
    localStorage.setItem('fanpass_demo_user', JSON.stringify(userData));
    return userData;
  };

  const loginAsFan = (fanId) => {
    const db = getDB();
    const fan = (db.fans || {})[fanId];
    if (!fan) return null;
    const userData = { ...fan, role: 'fan' };
    setUser(userData);
    localStorage.setItem('fanpass_demo_user', JSON.stringify(userData));
    return userData;
  };

  const registerFan = (data, creatorId = null) => {
    const db = getDB();
    if (!db.fans) db.fans = {};
    if (!db.subscriptions) db.subscriptions = [];
    
    const fanId = `fan-${Date.now()}`;
    const fan = {
      id: fanId,
      name: data.name,
      email: data.email,
      createdAt: new Date().toISOString()
    };
    db.fans[fanId] = fan;
    
    // If registering via creator link, auto-subscribe
    if (creatorId && db.creators[creatorId]) {
      db.subscriptions.push({
        id: `sub-${Date.now()}`,
        fanId: fanId,
        creatorId: creatorId,
        joinedAt: new Date().toISOString(),
        status: 'active'
      });
    }
    
    saveDB(db);
    const userData = { ...fan, role: 'fan' };
    setUser(userData);
    localStorage.setItem('fanpass_demo_user', JSON.stringify(userData));
    return userData;
  };

  const subscribeTo = (creatorId) => {
    if (!user || user.role !== 'fan') return false;
    const db = getDB();
    
    // Check if already subscribed
    const existing = db.subscriptions.find(s => s.fanId === user.id && s.creatorId === creatorId);
    if (existing) return false;
    
    db.subscriptions.push({
      id: `sub-${Date.now()}`,
      fanId: user.id,
      creatorId: creatorId,
      joinedAt: new Date().toISOString(),
      status: 'active'
    });
    
    // Update creator stats
    if (db.creators[creatorId]) {
      db.creators[creatorId].stats.totalMembers += 1;
    }
    
    saveDB(db);
    return true;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('fanpass_demo_user');
  };

  const updateUser = (updates) => {
    const db = getDB();
    if (user.role === 'creator') {
      db.creators[user.id] = { ...db.creators[user.id], ...updates };
      saveDB(db);
    }
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem('fanpass_demo_user', JSON.stringify(updated));
  };

  return (
    <AuthContext.Provider value={{ user, loginAsCreator, registerCreator, loginAsFan, registerFan, subscribeTo, logout, updateUser, loading }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// ============ DESIGN TOKENS - Ocean Blue + Teal Theme (Refined) ============
const colors = {
  // Primary - Muted Steel Blue (sophisticated, less vibrant)
  accent: '#4B6BFB',
  accentHover: '#6B8AFB',
  accentGlow: 'rgba(75, 107, 251, 0.12)',
  // Secondary - Soft Slate Blue
  accentSecondary: '#64748B',
  accentLight: '#94A3B8',
  // Backgrounds - Dark Navy
  bgPrimary: '#0F172A',
  bgCard: '#1E293B',
  bgSecondary: '#334155',
  // Status
  success: '#4ade80',
  // Text - improved readability
  textMuted: '#94A3B8',
  textPrimary: '#F8FAFC',
  textAccent: '#A5B4FC', // Softer indigo for accent text
};

// Platform Stats (for social proof)
const platformStats = {
  totalDraws: 1256,
  totalExperiences: 1256,
  totalCreators: 2847,
  totalWinners: 1256,
  totalEntries: 8472931
};

// ============ LOGO COMPONENT - Refined Design ============
const Logo = ({ size = "default" }) => {
  const isLarge = size === "large";
  const iconSize = isLarge ? 24 : 18;
  const textSize = isLarge ? 'text-2xl' : 'text-lg';
  
  return (
    <div className="flex items-center gap-1.5">
      {/* Lock icon - no background */}
      <Lock 
        style={{ 
          width: iconSize, 
          height: iconSize,
          color: '#6366F1'
        }}
        strokeWidth={2.5}
      />
      {/* FanVault text */}
      <div className={`font-semibold ${textSize}`} style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '-0.01em' }}>
        <span style={{ color: '#818CF8' }}>Fan</span>
        <span style={{ color: '#E2E8F0' }}>Vault</span>
      </div>
    </div>
  );
};

// ============ CONFETTI EFFECT ============
const Confetti = ({ active }) => {
  if (!active) return null;
  
  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2,
    color: [colors.accent, colors.accentSecondary, colors.success, '#fff', '#fbbf24'][Math.floor(Math.random() * 5)]
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {confettiPieces.map(piece => (
        <motion.div
          key={piece.id}
          initial={{ y: -20, x: `${piece.left}vw`, opacity: 1, rotate: 0 }}
          animate={{ 
            y: '100vh', 
            opacity: [1, 1, 0],
            rotate: 360 * (Math.random() > 0.5 ? 1 : -1)
          }}
          transition={{ 
            duration: piece.duration, 
            delay: piece.delay,
            ease: 'linear'
          }}
          className="absolute w-3 h-3"
          style={{ 
            backgroundColor: piece.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px'
          }}
        />
      ))}
    </div>
  );
};

// ============ HEADER ============
const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const db = getDB();
  const userNotifications = (db.notifications || []).filter(n => n.userId === user?.id);
  const unreadCount = userNotifications.filter(n => !n.read).length;
  const unreadMessages = user ? (db.conversations || []).reduce((count, conv) => {
    if (conv.participants.includes(user.id)) {
      return count + (conv.unreadCount?.[user.id] || 0);
    }
    return count;
  }, 0) : 0;

  const markNotificationsRead = () => {
    const freshDb = getDB();
    freshDb.notifications = freshDb.notifications.map(n => 
      n.userId === user?.id ? { ...n, read: true } : n
    );
    saveDB(freshDb);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/discover?q=${encodeURIComponent(searchQuery)}`);
      setShowSearch(false);
      setSearchQuery('');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50" style={{ background: 'rgba(15, 23, 42, 0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <Logo />
          </Link>
          
          {/* Desktop Navigation */}
          {user && (
            <nav className="hidden md:flex items-center gap-1 ml-6">
              <Link 
                to={user.role === 'creator' ? '/creator/dashboard' : '/fan/dashboard'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname.includes('dashboard') ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                <Home className="w-4 h-4 inline mr-2" /> Home
              </Link>
              <Link 
                to="/discover"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === '/discover' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                <Compass className="w-4 h-4 inline mr-2" /> Discover
              </Link>
              <Link 
                to="/messages"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${location.pathname === '/messages' ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                <MessageSquare className="w-4 h-4 inline mr-2" /> Messages
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center" style={{ background: colors.accent }}>{unreadMessages}</span>
                )}
              </Link>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Search Button */}
          <button 
            onClick={() => setShowSearch(!showSearch)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors hidden md:flex"
          >
            <Search className="w-5 h-5 text-white/70" />
          </button>

          {user ? (
            <>
              {/* Notifications */}
              <div className="relative">
                <button 
                  onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications) markNotificationsRead(); }}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors relative"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5 text-white/70" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 w-5 h-5 rounded-full text-xs flex items-center justify-center animate-pulse" style={{ background: '#EF4444' }}>{unreadCount}</span>
                  )}
                </button>
                
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-2 w-80 rounded-xl overflow-hidden max-h-96 overflow-y-auto"
                      style={{ background: 'rgba(15, 23, 42, 0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <div className="p-3 border-b border-white/10 flex items-center justify-between">
                        <p className="font-semibold text-white">Notifications</p>
                        <button className="text-xs text-white/50 hover:text-white">Mark all read</button>
                      </div>
                      <div className="divide-y divide-white/5">
                        {userNotifications.length === 0 ? (
                          <div className="p-4 text-center text-white/50">No notifications</div>
                        ) : userNotifications.slice(0, 5).map(notif => (
                          <div 
                            key={notif.id} 
                            className={`p-3 hover:bg-white/5 cursor-pointer ${!notif.read ? 'bg-white/5' : ''}`}
                            onClick={() => { navigate(notif.link); setShowNotifications(false); }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: colors.accentGlow }}>
                                {notif.type === 'new_post' && <Image className="w-5 h-5" style={{ color: colors.accent }} />}
                                {notif.type === 'story' && <Camera className="w-5 h-5" style={{ color: colors.accent }} />}
                                {notif.type === 'message' && <MessageSquare className="w-5 h-5" style={{ color: colors.accent }} />}
                                {notif.type === 'experience_drop' && <Gift className="w-5 h-5" style={{ color: colors.accent }} />}
                                {notif.type === 'winner' && <Award className="w-5 h-5 text-yellow-400" />}
                                {notif.type === 'new_member' && <Users className="w-5 h-5" style={{ color: colors.accent }} />}
                                {notif.type === 'tip' && <DollarSign className="w-5 h-5 text-green-400" />}
                                {notif.type === 'milestone' && <TrendingUp className="w-5 h-5 text-purple-400" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-white">{notif.title}</p>
                                <p className="text-white/50 text-xs truncate">{notif.message}</p>
                                <p className="text-white/30 text-xs mt-1">{formatTimeAgo(notif.createdAt)}</p>
                              </div>
                              {!notif.read && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: colors.accent }} />}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-2 border-t border-white/10">
                        <button className="w-full p-2 text-sm text-center rounded-lg hover:bg-white/10" style={{ color: colors.accent }}>
                          View All Notifications
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Profile Dropdown */}
              <div className="relative">
                <button 
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="profile-dropdown-btn"
                >
                  <img 
                    src={user.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=6366F1&color=fff&size=100`} 
                    alt={user.name} 
                    className="w-8 h-8 rounded-full object-cover ring-2" 
                    style={{ ringColor: colors.accent }} 
                  />
                  <span className="hidden md:block text-white font-medium">{user.name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform hidden md:block ${showProfileDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {showProfileDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 top-full mt-2 w-56 rounded-xl overflow-hidden"
                      style={{ background: 'rgba(15, 23, 42, 0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <div className="p-3 border-b border-white/10">
                        <p className="font-semibold text-white">{user.name}</p>
                        <p className="text-white/50 text-sm">@{user.username || user.id}</p>
                      </div>
                      <div className="p-2">
                        {user.role === 'creator' && (
                          <>
                            <button
                              onClick={() => { navigate(`/c/${user.username || user.id}`); setShowProfileDropdown(false); }}
                              className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-white/80"
                            >
                              <ExternalLink className="w-4 h-4" /> View My Public Page
                            </button>
                            <button
                              onClick={() => { navigate('/creator/dashboard'); setShowProfileDropdown(false); }}
                              className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-white/80"
                            >
                              <Crown className="w-4 h-4" /> Dashboard
                            </button>
                            <button
                              onClick={() => { navigate('/creator/analytics'); setShowProfileDropdown(false); }}
                              className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-white/80"
                            >
                              <BarChart3 className="w-4 h-4" /> Analytics
                            </button>
                          </>
                        )}
                        {user.role === 'fan' && (
                          <>
                            <button
                              onClick={() => { navigate('/fan/dashboard'); setShowProfileDropdown(false); }}
                              className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-white/80"
                            >
                              <Ticket className="w-4 h-4" /> My Inner Circles
                            </button>
                            <button
                              onClick={() => { navigate('/saved'); setShowProfileDropdown(false); }}
                              className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-white/80"
                            >
                              <Bookmark className="w-4 h-4" /> Saved Posts
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { navigate('/settings'); setShowProfileDropdown(false); }}
                          className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-white/80"
                        >
                          <Settings className="w-4 h-4" /> Settings
                        </button>
                        <button
                          onClick={() => { logout(); setShowProfileDropdown(false); navigate('/'); }}
                          className="w-full text-left p-3 rounded-lg hover:bg-white/10 flex items-center gap-3 text-red-400"
                        >
                          <LogOut className="w-4 h-4" /> Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1.5">
              <button onClick={() => navigate('/discover')} className="text-slate-400 hover:text-white text-sm py-1.5 px-3 rounded-md hover:bg-white/5 transition-colors hidden md:flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5" /> Discover
              </button>
              <button onClick={() => navigate('/auth/fan')} className="text-slate-400 hover:text-white text-sm py-1.5 px-3 rounded-md hover:bg-white/5 transition-colors hidden md:flex items-center">
                Fan Login
              </button>
              <button onClick={() => navigate('/auth/creator')} className="btn-gold text-xs py-1.5 px-3" data-testid="creator-login-btn">
                Creator Login
              </button>
            </div>
          )}

          <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="md:hidden p-2 hover:bg-white/10 rounded-lg">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-white/10 px-4 py-3"
          >
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input 
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search creators, content, experiences..."
                  className="input-field w-full pl-12 pr-12"
                  autoFocus
                />
                <button type="button" onClick={() => setShowSearch(false)} className="absolute right-4 top-1/2 -translate-y-1/2">
                  <X className="w-5 h-5 text-white/40 hover:text-white" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t border-white/10 overflow-hidden"
          >
            <nav className="p-4 space-y-2">
              {user && (
                <>
                  <Link to={user.role === 'creator' ? '/creator/dashboard' : '/fan/dashboard'} onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <Home className="w-4 h-4 inline mr-3" /> Home
                  </Link>
                  <Link to="/discover" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <Compass className="w-4 h-4 inline mr-3" /> Discover
                  </Link>
                  <Link to="/messages" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5 relative">
                    <MessageSquare className="w-4 h-4 inline mr-3" /> Messages
                    {unreadMessages > 0 && <span className="ml-2 px-2 py-0.5 rounded-full text-xs" style={{ background: colors.accent }}>{unreadMessages}</span>}
                  </Link>
                </>
              )}
              {!user && (
                <>
                  <Link to="/discover" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <Compass className="w-4 h-4 inline mr-3" /> Discover Creators
                  </Link>
                  <Link to="/auth/fan" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <User className="w-4 h-4 inline mr-3" /> Fan Login
                  </Link>
                  <Link to="/auth/creator" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <Crown className="w-4 h-4 inline mr-3" /> Creator Login
                  </Link>
                </>
              )}
              {user?.role === 'creator' && (
                <>
                  <Link to={`/c/${user.username || user.id}`} onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <ExternalLink className="w-4 h-4 inline mr-3" /> View My Public Page
                  </Link>
                  <Link to="/creator/analytics" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <BarChart3 className="w-4 h-4 inline mr-3" /> Analytics
                  </Link>
                </>
              )}
              {user?.role === 'fan' && (
                <Link to="/saved" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                  <Bookmark className="w-4 h-4 inline mr-3" /> Saved Posts
                </Link>
              )}
              {user && (
                <>
                  <Link to="/settings" onClick={() => setShowMobileMenu(false)} className="block p-3 rounded-lg hover:bg-white/5">
                    <Settings className="w-4 h-4 inline mr-3" /> Settings
                  </Link>
                  <button onClick={() => { logout(); setShowMobileMenu(false); navigate('/'); }} className="w-full text-left p-3 rounded-lg hover:bg-white/5 text-red-400">
                    <LogOut className="w-4 h-4 inline mr-3" /> Logout
                  </button>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Helper function for time formatting
const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

// ============ HOME PAGE ============
const HomePage = () => {
  const navigate = useNavigate();
  const db = getDB();
  const creators = Object.values(db.creators);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Featured Experiences for carousel
  const featuredExperiences = [
    {
      id: 1,
      title: "VIP 1-on-1 Meet & Greet",
      creator: "Sarah Chen",
      creatorPhoto: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150",
      category: "Wellness",
      prize: "Private video call + signed merch",
      entries: 2847,
      endsIn: "3 days",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800"
    },
    {
      id: 2,
      title: "1-on-1 Singing Lesson",
      creator: "Aria Vox",
      creatorPhoto: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150",
      category: "Music",
      prize: "Private vocal coaching session + song recording",
      entries: 4521,
      endsIn: "5 days",
      image: "https://images.unsplash.com/photo-1709284015303-aea71874498e?w=800"
    },
    {
      id: 3,
      title: "Private Cooking Masterclass",
      creator: "Chef Antonio",
      creatorPhoto: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
      category: "Food",
      prize: "In-person cooking session + dinner",
      entries: 1893,
      endsIn: "7 days",
      image: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800"
    },
    {
      id: 4,
      title: "Gaming Session & Strategy Call",
      creator: "Marcus Gaming",
      creatorPhoto: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150",
      category: "Gaming",
      prize: "2-hour private gaming session",
      entries: 3156,
      endsIn: "4 days",
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800"
    },
    {
      id: 5,
      title: "Business Mentorship Day",
      creator: "Alex Entrepreneur",
      creatorPhoto: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150",
      category: "Business",
      prize: "Full day 1-on-1 business coaching",
      entries: 2234,
      endsIn: "6 days",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800"
    },
    {
      id: 6,
      title: "Backstage Concert Experience",
      creator: "DJ Pulse",
      creatorPhoto: "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=150",
      category: "Music",
      prize: "VIP backstage pass + meet the artists",
      entries: 5890,
      endsIn: "2 days",
      image: "https://images.unsplash.com/photo-1768054485751-bab2eda850b8?w=800"
    },
    {
      id: 7,
      title: "Private Guitar Masterclass",
      creator: "Jake Strings",
      creatorPhoto: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150",
      category: "Music",
      prize: "2-hour acoustic session + signed guitar",
      entries: 1745,
      endsIn: "8 days",
      image: "https://images.pexels.com/photos/7520930/pexels-photo-7520930.jpeg?auto=compress&cs=tinysrgb&w=800"
    },
    {
      id: 8,
      title: "Elite Personal Training Day",
      creator: "Coach Titan",
      creatorPhoto: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150",
      category: "Fitness",
      prize: "Full day training + custom meal plan",
      entries: 3102,
      endsIn: "4 days",
      image: "https://images.unsplash.com/photo-1739430547883-dc519d7f7e56?w=800"
    },
    {
      id: 9,
      title: "Music Production Bootcamp",
      creator: "BeatMaker Pro",
      creatorPhoto: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150",
      category: "Music",
      prize: "Studio session + produce your own track",
      entries: 2678,
      endsIn: "10 days",
      image: "https://images.pexels.com/photos/8412342/pexels-photo-8412342.jpeg?auto=compress&cs=tinysrgb&w=800"
    }
  ];

  // Recent winners for social proof
  const recentWinners = [
    { name: "Jessica M.", prize: "VIP Meet & Greet", creator: "Sarah Chen", photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100" },
    { name: "David K.", prize: "Gaming Session", creator: "Marcus Gaming", photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100" },
    { name: "Emily R.", prize: "Wellness Retreat", creator: "Luna Martinez", photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100" },
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredExperiences.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredExperiences.length]);
  
  return (
    <div className="min-h-screen pb-20" style={{ background: '#0F172A' }}>
      {/* Hero Section with Rotating Carousel */}
      <div className="relative">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/50 to-slate-900 z-10 pointer-events-none" />
        
        {/* Carousel Background Images */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 0.3, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${featuredExperiences[currentSlide].image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </AnimatePresence>

        {/* Hero Content */}
        <div className="relative z-20 max-w-6xl mx-auto px-4 pt-28 pb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-white">Join the </span>
              <span style={{ background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Inner Circle</span>
              <span className="text-white"> of Your Favourite Creator</span>
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-slate-300 leading-relaxed">
              Members get VIP access and free entries to every experience drop.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <button onClick={() => navigate('/auth/fan')} className="btn-gold text-sm py-2.5 px-6">
                <User className="w-4 h-4" /> Find Your Creator
              </button>
              <button onClick={() => navigate('/auth/creator')} className="btn-outline text-sm py-2.5 px-6">
                <Crown className="w-4 h-4" /> Become a Creator
              </button>
            </div>
          </motion.div>

          {/* Featured Experience Carousel Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="glass p-6 rounded-2xl border border-white/10"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Experience Image */}
                  <div className="md:w-1/2 relative rounded-xl overflow-hidden aspect-video">
                    <img 
                      src={featuredExperiences[currentSlide].image} 
                      alt={featuredExperiences[currentSlide].title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium bg-indigo-500/90 text-white">
                      {featuredExperiences[currentSlide].category}
                    </div>
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/90 text-white flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {featuredExperiences[currentSlide].endsIn}
                    </div>
                  </div>
                  
                  {/* Experience Details */}
                  <div className="md:w-1/2 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <img 
                          src={featuredExperiences[currentSlide].creatorPhoto} 
                          alt={featuredExperiences[currentSlide].creator}
                          className="w-10 h-10 rounded-full object-cover border-2 border-indigo-500/50"
                        />
                        <div>
                          <p className="text-sm text-slate-400">Hosted by</p>
                          <p className="font-semibold text-white">{featuredExperiences[currentSlide].creator}</p>
                        </div>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                        {featuredExperiences[currentSlide].title}
                      </h3>
                      <p className="text-slate-300 mb-4">
                        <Gift className="w-4 h-4 inline mr-2 text-indigo-400" />
                        {featuredExperiences[currentSlide].prize}
                      </p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2 text-sm text-slate-400">
                          <Users className="w-4 h-4" />
                          <span>{featuredExperiences[currentSlide].entries.toLocaleString()} entries</span>
                        </div>
                        <span className="text-xs text-emerald-400 font-medium">Members automatically entered</span>
                      </div>
                      <button 
                        onClick={() => navigate('/auth/fan')}
                        className="btn-gold w-full py-3"
                      >
                        Subscribe & Enter <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Carousel Dots */}
            <div className="flex justify-center gap-2 mt-4">
              {featuredExperiences.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-indigo-500 w-6' : 'bg-slate-600 hover:bg-slate-500'}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-8">
        {/* Platform Stats - Enhanced */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
        >
          {[
            { value: '2,847+', label: 'Active Creators', icon: Crown },
            { value: '14,200+', label: 'Experience Drops', icon: Gift },
            { value: '127K+', label: 'Happy Members', icon: Users },
            { value: '98%', label: 'Satisfaction Rate', icon: Star },
          ].map((stat, idx) => (
            <div key={idx} className="glass p-4 rounded-xl text-center border border-white/5">
              <stat.icon className="w-5 h-5 mx-auto mb-2 text-indigo-400" />
              <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
              <p className="text-slate-500 text-xs">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Recent Winners - Social Proof */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-semibold text-white">Recent Winners</h3>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {recentWinners.map((winner, idx) => (
              <div key={idx} className="glass px-4 py-3 rounded-full flex items-center gap-3 border border-white/5">
                <img src={winner.photo} alt={winner.name} className="w-8 h-8 rounded-full object-cover" />
                <div className="text-sm">
                  <span className="text-white font-medium">{winner.name}</span>
                  <span className="text-slate-400"> won </span>
                  <span className="text-indigo-400">{winner.prize}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Membership Privileges Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-4">Membership Privileges</h2>
          <p className="text-slate-400 text-center mb-10 max-w-xl mx-auto">Join any creator's membership and unlock exclusive benefits</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Gift, title: 'Auto-Entry to Giveaways', desc: 'Every membership = automatic entries into exclusive experience draws' },
              { icon: Lock, title: 'Private Content Access', desc: 'Behind-the-scenes, early releases, and member-only posts' },
              { icon: MessageCircle, title: 'Direct Creator Access', desc: 'Message your favourite creators directly' },
              { icon: Star, title: 'Priority Perks', desc: 'Early access to merch, events, and announcements' },
            ].map((perk, idx) => (
              <div key={idx} className="glass p-6 rounded-xl border border-white/5 hover:border-indigo-500/30 transition-colors">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(99, 102, 241, 0.15)' }}>
                  <perk.icon className="w-5 h-5 text-indigo-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{perk.title}</h3>
                <p className="text-slate-400 text-sm">{perk.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Trust Indicators - Enhanced */}
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center gap-6 mb-20 py-6 border-y border-white/5"
        >
          {[
            { icon: Shield, text: 'Verified Fair Draws' },
            { icon: Lock, text: 'Secure Payments' },
            { icon: Zap, text: 'Instant Payouts' },
            { icon: RefreshCw, text: 'Cancel Anytime' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 text-slate-400 text-sm">
              <item.icon className="w-4 h-4 text-emerald-400" />
              <span>{item.text}</span>
            </div>
          ))}
        </motion.div>

        {/* How It Works - Refined */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-4">How It Works</h2>
          <p className="text-slate-400 text-center mb-12 max-w-xl mx-auto">Simple, transparent, and rewarding for everyone</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Fan Journey */}
            <div className="glass p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99, 102, 241, 0.15)' }}>
                  <User className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-2xl font-bold">For Fans</h3>
              </div>
              <div className="space-y-6">
                {[
                  { step: '01', title: 'Find Your Favourite Creator', desc: 'Discover creators through social media or our platform' },
                  { step: '02', title: 'Join Their Membership', desc: 'One-click signup for exclusive access' },
                  { step: '03', title: 'Win Exclusive Experiences', desc: 'Automatically entered into all their giveaways' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="text-2xl font-bold text-indigo-400">{item.step}</span>
                    <div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-slate-500 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Creator Journey */}
            <div className="glass p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99, 102, 241, 0.15)' }}>
                  <Crown className="w-6 h-6 text-indigo-400" />
                </div>
                <h3 className="text-2xl font-bold">For Creators</h3>
              </div>
              <div className="space-y-6">
                {[
                  { step: '01', title: 'Create Your Profile', desc: 'Set up in minutes with your unique link' },
                  { step: '02', title: 'Build Your Community', desc: 'Share content and grow your member base' },
                  { step: '03', title: 'Monetise & Connect', desc: 'Run experience drops and earn recurring revenue' }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="text-2xl font-bold text-indigo-400">{item.step}</span>
                    <div>
                      <p className="font-semibold text-white">{item.title}</p>
                      <p className="text-slate-500 text-sm mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Featured Creators - Enhanced Cards */}
        {creators.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-20"
          >
            <h2 className="text-3xl font-bold text-center mb-4">Featured Creators</h2>
            <p className="text-slate-400 text-center mb-12">Join their community and win exclusive experiences</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {creators.slice(0, 6).map(creator => (
                <div 
                  key={creator.id} 
                  className="glass p-5 rounded-xl cursor-pointer group hover:border-indigo-500/30 border border-transparent transition-all"
                  onClick={() => navigate(`/c/${creator.username}`)}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <img src={creator.photo} alt={creator.name} className="w-14 h-14 rounded-full object-cover border-2 border-indigo-500/30" />
                    <div className="flex-1">
                      <h3 className="font-bold text-white group-hover:text-indigo-400 transition-colors">{creator.name}</h3>
                      <p className="text-slate-500 text-sm">@{creator.username}</p>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-800 text-slate-400">{creator.category || 'Creator'}</span>
                  </div>
                  {creator.experienceDrop?.title && (
                    <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Gift className="w-4 h-4 text-indigo-400" />
                        <span className="text-white font-medium">{creator.experienceDrop.title}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 ml-6">Subscribe to enter</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <span className="text-slate-500 text-sm">{creator.stats?.totalMembers || 0} members</span>
                    <span className="font-bold text-indigo-400">{creator.membershipPrice}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-8">
              <button onClick={() => navigate('/discover')} className="btn-outline py-2.5 px-6">
                <Compass className="w-4 h-4" /> Discover All Creators
              </button>
            </div>
          </motion.div>
        )}

        {/* Testimonials Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-4">What Our Community Says</h2>
          <p className="text-slate-400 text-center mb-12">Real experiences from fans and creators</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "Won a private cooking class with my favourite chef! The membership paid for itself 100x over.",
                name: "Sarah T.",
                role: "Member",
                photo: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100"
              },
              {
                quote: "FanVault has completely changed how I connect with my community. The experience drops are a game-changer.",
                name: "Marcus K.",
                role: "Creator",
                photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100"
              },
              {
                quote: "Finally a platform that makes exclusive experiences feel truly exclusive. Love the transparency.",
                name: "Emily R.",
                role: "Member",
                photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100"
              }
            ].map((testimonial, idx) => (
              <div key={idx} className="glass p-6 rounded-xl border border-white/5">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <img src={testimonial.photo} alt={testimonial.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="text-center mb-20 py-12 px-8 rounded-2xl"
          style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(129, 140, 248, 0.1) 100%)', border: '1px solid rgba(99, 102, 241, 0.2)' }}
        >
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-slate-400 mb-8 max-w-lg mx-auto">Join thousands of fans and creators already on FanVault</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/auth/fan')} className="btn-gold py-3 px-8">
              <User className="w-4 h-4" /> Join as a Fan
            </button>
            <button onClick={() => navigate('/auth/creator')} className="btn-outline py-3 px-8">
              <Crown className="w-4 h-4" /> Start as a Creator
            </button>
          </div>
        </motion.div>

        {/* Comprehensive Footer */}
        <footer className="border-t border-white/10 pt-12 pb-8">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Brand */}
            <div>
              <Logo />
              <p className="text-slate-500 text-sm mt-4">The premier platform for creator memberships and exclusive fan experiences.</p>
            </div>
            
            {/* Platform */}
            <div>
              <h4 className="font-semibold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/discover')} className="text-slate-400 hover:text-white transition-colors">Discover Creators</button></li>
                <li><button onClick={() => navigate('/auth/creator')} className="text-slate-400 hover:text-white transition-colors">Become a Creator</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">Pricing</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">How It Works</button></li>
              </ul>
            </div>
            
            {/* Support */}
            <div>
              <h4 className="font-semibold text-white mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><button className="text-slate-400 hover:text-white transition-colors">Help Centre</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">Contact Us</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">FAQ</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">Creator Resources</button></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><button className="text-slate-400 hover:text-white transition-colors">Terms of Service</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">Cookie Policy</button></li>
                <li><button className="text-slate-400 hover:text-white transition-colors">Community Guidelines</button></li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Footer */}
          <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/5">
            <p className="text-slate-500 text-sm mb-4 md:mb-0">© 2026 FanVault. All rights reserved.</p>
            <div className="flex gap-4">
              <button className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                <Globe className="w-4 h-4" />
              </button>
              <button className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                <MessageCircle className="w-4 h-4" />
              </button>
              <button className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

// ============ AUTH PAGES ============
const AuthPage = ({ type }) => {
  const { loginAsCreator, registerCreator, loginAsFan, registerFan } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const db = getDB();

  const isCreator = type === 'creator';
  const existingAccounts = isCreator ? Object.values(db.creators) : Object.values(db.fans);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (isLogin) {
      // Demo login - just pick from existing
      if (existingAccounts.length > 0) {
        const account = existingAccounts[0];
        if (isCreator) {
          loginAsCreator(account.id);
          navigate('/creator/dashboard');
        } else {
          loginAsFan(account.id);
          navigate('/fan/dashboard');
        }
      } else {
        setError('No accounts found. Please sign up first.');
      }
    } else {
      // Register
      if (!formData.name || !formData.email) {
        setError('Please fill in all fields');
        return;
      }
      if (isCreator) {
        registerCreator(formData);
        navigate('/creator/dashboard');
      } else {
        registerFan(formData);
        navigate('/fan/dashboard');
      }
    }
  };

  const handleDemoLogin = (account) => {
    if (isCreator) {
      loginAsCreator(account.id);
      navigate('/creator/dashboard');
    } else {
      loginAsFan(account.id);
      navigate('/fan/dashboard');
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 px-4 flex items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="glass p-8 rounded-2xl max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center" style={{ background: colors.accentGlow, border: `1px solid ${colors.accent}30` }}>
            {isCreator ? (
              <Crown className="w-8 h-8" style={{ color: colors.accent }} />
            ) : (
              <Ticket className="w-8 h-8" style={{ color: colors.accent }} />
            )}
          </div>
          <h1 className="text-3xl font-bold">{isCreator ? 'Creator' : 'Fan'} {isLogin ? 'Login' : 'Sign Up'}</h1>
          <p className="text-gray-400 mt-2">
            {isCreator 
              ? 'Manage your memberships and Experience Drops' 
              : 'Access exclusive content from your favorite creators'}
          </p>
        </div>

        {/* Demo Account Quick Login */}
        {isLogin && existingAccounts.length > 0 && (
          <div className="mb-6">
            <p className="text-gray-500 text-sm mb-3 text-center">Quick Demo Login:</p>
            <div className="space-y-2">
              {existingAccounts.slice(0, 2).map(account => (
                <button
                  key={account.id}
                  onClick={() => handleDemoLogin(account)}
                  className="w-full p-4 rounded-xl flex items-center gap-3 transition-all card-interactive"
                >
                  <div className="avatar-ring">
                    <img src={account.photo || `https://ui-avatars.com/api/?name=${account.name}&background=5dd9e8&color=1a2332`} alt="" className="w-10 h-10 rounded-full object-cover" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{account.name}</p>
                    <p className="text-gray-500 text-xs">{account.email}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 ml-auto" style={{ color: colors.accent }} />
                </button>
              ))}
            </div>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 text-gray-500" style={{ background: colors.bgCard }}>or continue with</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-white/70 text-sm mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="input-field w-full pl-12"
                />
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-white/70 text-sm mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="input-field w-full pl-12"
              />
            </div>
          </div>

          <div>
            <label className="block text-white/70 text-sm mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="input-field w-full pl-12 pr-12"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2">
                {showPassword ? <EyeOff className="w-5 h-5 text-white/40" /> : <Eye className="w-5 h-5 text-white/40" />}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          <button type="submit" className="btn-gold w-full py-4">
            {isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-white/50 text-sm mt-6">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button onClick={() => setIsLogin(!isLogin)} className="text-gold-primary hover:underline">
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>

        <p className="text-center text-white/30 text-xs mt-4">
          Demo mode - no real credentials required
        </p>
      </motion.div>
    </div>
  );
};

// ============ CREATOR DASHBOARD ============
const CreatorDashboard = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  const [showCreateDropModal, setShowCreateDropModal] = useState(false);
  const [showLiveDrawModal, setShowLiveDrawModal] = useState(null);
  const [showEntriesModal, setShowEntriesModal] = useState(null);
  const [drawStep, setDrawStep] = useState('ready'); // ready, drawing, winner
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [membershipTiers, setMembershipTiers] = useState([
    { id: 'basic', name: 'Basic', price: 5, benefits: ['Access to exclusive content', 'Community chat access', 'Monthly newsletter'], active: true },
    { id: 'premium', name: 'Premium', price: 15, benefits: ['All Basic benefits', '2x entries to giveaways', 'Priority DM responses', 'Exclusive behind-the-scenes'], active: false },
    { id: 'vip', name: 'VIP', price: 50, benefits: ['All Premium benefits', '5x entries to giveaways', 'Monthly 1-on-1 video call', 'Signed merchandise', 'Name in credits'], active: false }
  ]);
  const [newDrop, setNewDrop] = useState({
    title: '',
    type: '1-on-1 Experience',
    description: '',
    numberOfWinners: 1,
    drawDate: '',
    editId: null,
    image: ''
  });
  const [formData, setFormData] = useState({
    membershipPrice: '',
    prizeName: '',
    drawDate: '',
    bankName: '',
    accountNumber: '',
    bsb: '',
    accountHolderName: ''
  });
  const [newContent, setNewContent] = useState({ text: '', image: '', channel: 'all' });
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'creator') {
      navigate('/auth/creator');
      return;
    }
    setFormData({
      membershipPrice: user.membershipPrice || '$5/month',
      dropTitle: user.experienceDrop?.title || '',
      dropType: user.experienceDrop?.type || '',
      drawDate: user.experienceDrop?.drawDate || '',
      dropDescription: user.experienceDrop?.description || '',
      numberOfWinners: user.experienceDrop?.numberOfWinners || 1,
      dropMedia: user.experienceDrop?.media || [],
      bankName: user.bankDetails?.bankName || '',
      accountNumber: '',
      routingNumber: ''
    });
  }, [user, navigate]);

  if (!user) return null;

  const shareLink = `${window.location.origin}/c/${user.username}`;
  const db = getDB();
  const memberCount = db.subscriptions.filter(s => s.creatorId === user.id).length;

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const saveSettings = () => {
    // Create new drop object
    const newDrop = {
      id: `drop-${Date.now()}`,
      title: formData.dropTitle,
      type: formData.dropType,
      status: 'active',
      drawDate: formData.drawDate,
      description: formData.dropDescription,
      numberOfWinners: parseInt(formData.numberOfWinners) || 1,
      entries: 0,
      media: formData.dropMedia || []
    };
    
    // Add to experienceDrops array
    const existingDrops = user.experienceDrops || [];
    const updatedDrops = [newDrop, ...existingDrops];
    
    // Also update single experienceDrop for backward compatibility
    const updates = {
      membershipPrice: formData.membershipPrice,
      experienceDrops: updatedDrops,
      experienceDrop: { 
        title: formData.dropTitle, 
        type: formData.dropType,
        drawDate: formData.drawDate,
        description: formData.dropDescription,
        numberOfWinners: parseInt(formData.numberOfWinners) || 1,
        media: formData.dropMedia || [],
        pastWinners: user.experienceDrop?.pastWinners || []
      }
    };
    updateUser(updates);
    
    // Reset form
    setFormData({
      ...formData,
      dropTitle: '',
      dropType: '',
      dropDescription: '',
      drawDate: '',
      numberOfWinners: 1,
      dropMedia: []
    });
    
    showSaveMessage('Experience Drop created!');
  };

  const saveBankDetails = () => {
    if (!formData.bankName || !formData.accountNumber || !formData.routingNumber) {
      showSaveMessage('Please fill in all bank details');
      return;
    }
    const bankDetails = {
      bankName: formData.bankName,
      accountLast4: formData.accountNumber.slice(-4),
      routingLast4: formData.routingNumber.slice(-4),
      verified: true
    };
    updateUser({ bankDetails });
    setFormData({ ...formData, accountNumber: '', routingNumber: '' });
    showSaveMessage('Bank account connected!');
  };

  const addContent = () => {
    if (!newContent.text) return;
    const content = {
      id: Date.now().toString(),
      text: newContent.text,
      image: newContent.image,
      date: new Date().toISOString()
    };
    updateUser({ content: [content, ...(user.content || [])] });
    setNewContent({ text: '', image: '' });
    showSaveMessage('Content posted!');
  };

  const showSaveMessage = (msg) => {
    setSaveMessage(msg);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const openCreateDropModal = () => {
    // Clear form for new drop
    setNewDrop({
      title: '',
      type: '1-on-1 Experience',
      description: '',
      numberOfWinners: 1,
      drawDate: '',
      editId: null
    });
    setShowCreateDropModal(true);
  };

  // Updated tab structure per UX recommendations
  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'experienceDrops', label: 'Experience Drops', icon: Gift },
    { id: 'membership', label: 'Membership Tiers', icon: Crown },
    { id: 'content', label: 'Content', icon: Image },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'payouts', label: 'Payouts', icon: Wallet }
  ];
  
  // Get experience drops categorized by status
  const getExperienceDrops = () => {
    const drops = user.experienceDrops || [];
    return {
      active: drops.filter(d => d.status === 'active'),
      scheduled: drops.filter(d => d.status === 'scheduled'),
      completed: drops.filter(d => d.status === 'completed')
    };
  };
  
  const experienceDrops = getExperienceDrops();
  const hasActiveDrops = experienceDrops.active.length > 0;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header with Create CTA */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <img src={user.photo} alt={user.name} className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover ring-2 flex-shrink-0" style={{ ringColor: colors.accent }} />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Creator Dashboard</h1>
                <p className="text-white/60 text-sm md:text-base">Welcome back, {user.name}</p>
              </div>
            </div>
            {/* Primary CTA - Create New Experience Drop */}
            <button 
              onClick={openCreateDropModal}
              className="btn-gold py-3 px-6 w-full md:w-auto"
              data-testid="create-drop-header-btn"
            >
              <Plus className="w-5 h-5 mr-2" /> New Drop
            </button>
          </div>

          {saveMessage && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 rounded-xl text-center font-medium"
              style={{ background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.5)' }}
            >
              <Check className="w-5 h-5 inline mr-2" /> {saveMessage}
            </motion.div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="glass p-3 md:p-4 rounded-xl text-center">
              <Users className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2" style={{ color: colors.accent }} />
              <p className="text-xl md:text-2xl font-bold">{user.stats?.totalMembers?.toLocaleString() || memberCount}</p>
              <p className="text-white/60 text-xs md:text-sm">Active Members</p>
              <p className="text-xs mt-1 hidden md:block" style={{ color: colors.accent }}>+{user.stats?.thisMonthNewMembers || 0} this month</p>
            </div>
            <div className="glass p-3 md:p-4 rounded-xl text-center">
              <DollarSign className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2" style={{ color: colors.accent }} />
              <p className="text-xl md:text-2xl font-bold">${user.stats?.totalEarnings?.toLocaleString() || 0}</p>
              <p className="text-white/60 text-xs md:text-sm">Total Earnings</p>
              <p className="text-xs mt-1 hidden md:block" style={{ color: colors.accent }}>+${user.stats?.thisMonthEarnings?.toLocaleString() || 0} this month</p>
            </div>
            <div className="glass p-3 md:p-4 rounded-xl text-center">
              <Wallet className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2" style={{ color: colors.accent }} />
              <p className="text-xl md:text-2xl font-bold">${user.stats?.pendingPayout?.toLocaleString() || 0}</p>
              <p className="text-white/60 text-xs md:text-sm">Pending Payout</p>
              <p className="text-xs mt-1 text-white/40 hidden md:block">Next payout: Friday</p>
            </div>
            <div className="glass p-3 md:p-4 rounded-xl text-center">
              <Award className="w-5 h-5 md:w-6 md:h-6 mx-auto mb-1 md:mb-2" style={{ color: colors.accent }} />
              <p className="text-xl md:text-2xl font-bold">${user.stats?.avgSubscriptionPrice || '5'}</p>
              <p className="text-white/60 text-xs md:text-sm">Avg. Subscription</p>
              <p className="text-xs mt-1 text-white/40 hidden md:block">per month</p>
            </div>
          </div>

          {/* Tabs - Scrollable on mobile */}
          <div className="flex gap-2 mb-4 md:mb-6 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap transition-all text-sm md:text-base flex-shrink-0 ${
                  activeTab === tab.id ? 'text-white' : 'text-white/70 hover:bg-white/10'
                }`}
                style={activeTab === tab.id ? { background: 'linear-gradient(135deg, #1E40AF, #06B6D4)' } : {}}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="glass p-4 md:p-8 rounded-2xl">
            {activeTab === 'overview' && (
              <div>
                {/* Empty State for New Creators */}
                {!hasActiveDrops && !user.experienceDrop?.title ? (
                  <div className="text-center py-12">
                    <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: colors.accentGlow }}>
                      <Gift className="w-12 h-12" style={{ color: colors.accent }} />
                    </div>
                    <h2 className="text-2xl font-bold mb-3">Create Your First Experience Drop</h2>
                    <p className="text-white/60 max-w-md mx-auto mb-8">
                      Offer exclusive experiences to your fans and grow your community. Experience Drops are the best way to engage and reward your members!
                    </p>
                    <button 
                      onClick={openCreateDropModal}
                      className="btn-gold py-4 px-8 text-lg"
                      data-testid="create-first-drop-btn"
                    >
                      <Plus className="w-5 h-5 mr-2" /> Create Experience Drop
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Your Share Link - Primary CTA */}
                    <div className="p-6 rounded-xl mb-6" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(6, 182, 212, 0.15))', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Share2 className="w-5 h-5" style={{ color: colors.accent }} />
                          <h3 className="font-semibold">Your Creator Link</h3>
                        </div>
                        <button 
                          onClick={() => navigate(`/c/${user.username}`)}
                          className="text-sm flex items-center gap-1 hover:opacity-80"
                          style={{ color: colors.accent }}
                        >
                          Preview <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-white/60 text-sm mb-4">
                        Share this link to grow your community. New members are automatically entered into all your active drops.
                      </p>
                      <div className="flex items-center gap-3">
                        <input type="text" value={shareLink} readOnly className="input-field flex-1 text-sm py-2" />
                        <button onClick={copyLink} className="btn-gold px-4 py-2 flex items-center gap-2">
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Active Experience Drops Summary */}
                    <div className="p-6 rounded-xl mb-6" style={{ background: colors.accentGlow, border: `1px solid ${colors.accent}30` }}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold flex items-center gap-2">
                          <Gift className="w-5 h-5" style={{ color: colors.accentSecondary }} /> Active Experience Drops
                        </h3>
                        <span className="px-2 py-1 rounded-full text-xs font-medium badge-live">
                          {experienceDrops.active.length} LIVE
                        </span>
                      </div>
                      {experienceDrops.active.length > 0 ? (
                        <div className="space-y-3">
                          {experienceDrops.active.slice(0, 3).map((drop, idx) => (
                            <div key={drop.id} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                              <div>
                                <p className="font-medium text-white">{drop.title}</p>
                                <p className="text-white/50 text-xs">Draw: {drop.drawDate} • {drop.entries || 0} entries</p>
                              </div>
                            </div>
                          ))}
                          <button 
                            onClick={() => setActiveTab('experienceDrops')} 
                            className="btn-outline w-full py-2 text-sm mt-2"
                          >
                            View All Drops
                          </button>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-white/60 mb-3">No active drops. Create one to engage your fans!</p>
                          <button onClick={openCreateDropModal} className="btn-gold py-2 px-4 text-sm">
                            <Plus className="w-4 h-4 mr-2" /> Create Your First Drop
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Recent Activity - Condensed to 3-5 items */}
                    <div className="p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" style={{ color: colors.accent }} /> Recent Activity
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                            <Users className="w-4 h-4 text-green-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">New member joined</p>
                            <p className="text-white/50 text-xs">@wellness_fan_203 subscribed</p>
                          </div>
                          <p className="text-white/40 text-xs">2 hours ago</p>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: colors.accentGlow }}>
                            <DollarSign className="w-4 h-4" style={{ color: colors.accent }} />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">Payment received</p>
                            <p className="text-white/50 text-xs">$5.00 from subscription</p>
                          </div>
                          <p className="text-white/40 text-xs">5 hours ago</p>
                        </div>
                        <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: colors.accentGlow }}>
                            <Gift className="w-4 h-4" style={{ color: colors.accent }} />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">Experience Drop entry</p>
                            <p className="text-white/50 text-xs">12 new entries today</p>
                          </div>
                          <p className="text-white/40 text-xs">Today</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Experience Drops Tab - List View with Multiple Drops */}
            {activeTab === 'experienceDrops' && (
              <div className="space-y-6">
                {/* Header */}
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Gift className="w-5 h-5" style={{ color: colors.accent }} /> Experience Drops
                  </h2>
                  <p className="text-white/50 text-sm mt-1">Manage your exclusive fan experiences</p>
                </div>

                {/* Active Drops */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-400"></span> Active ({experienceDrops.active.length})
                  </h3>
                  {experienceDrops.active.length > 0 ? (
                    <div className="space-y-4">
                      {experienceDrops.active.map(drop => {
                        const drawDatePassed = new Date(drop.drawDate) <= new Date();
                        return (
                          <div key={drop.id} className="p-5 rounded-xl" style={{ background: colors.accentGlow, border: `1px solid ${colors.accent}30` }}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <span className="text-xs px-2 py-1 rounded-full mr-2" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                  {drop.type}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }}>
                                  {drop.numberOfWinners} Winner{drop.numberOfWinners > 1 ? 's' : ''}
                                </span>
                              </div>
                              <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }}>
                                ACTIVE
                              </span>
                            </div>
                            <h4 className="text-xl font-bold mb-2 text-white">{drop.title}</h4>
                            <p className="text-white/60 text-sm mb-4">{drop.description}</p>
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex gap-4 text-sm text-white/50">
                                <span><Calendar className="w-4 h-4 inline mr-1" /> {drop.drawDate}</span>
                                <span><Users className="w-4 h-4 inline mr-1" /> {drop.entries} fans entered</span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button 
                                onClick={() => {
                                  setNewDrop({
                                    title: drop.title,
                                    type: drop.type,
                                    description: drop.description || '',
                                    numberOfWinners: drop.numberOfWinners,
                                    drawDate: drop.drawDate,
                                    editId: drop.id
                                  });
                                  setShowCreateDropModal(true);
                                }}
                                className="btn-outline py-2 px-3 text-sm"
                                data-testid={`edit-drop-${drop.id}`}
                              >
                                <Edit3 className="w-4 h-4 mr-1" /> Edit
                              </button>
                              <button className="btn-outline py-2 px-3 text-sm">
                                <Users className="w-4 h-4 mr-1" /> Entries ({drop.entries})
                              </button>
                              <button 
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this drop?')) {
                                    const db = getDB();
                                    if (db.creators[user.id]) {
                                      db.creators[user.id].experienceDrops = db.creators[user.id].experienceDrops.filter(d => d.id !== drop.id);
                                      saveDB(db);
                                      updateUser({ experienceDrops: db.creators[user.id].experienceDrops });
                                      showSaveMessage('Drop deleted');
                                    }
                                  }
                                }}
                                className="btn-outline py-2 px-3 text-sm text-red-400 border-red-400/30 hover:bg-red-400/10"
                              >
                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                              </button>
                              {drawDatePassed && (
                                <button 
                                  onClick={() => setShowLiveDrawModal(drop)}
                                  className="btn-gold py-2 px-4 text-sm"
                                  data-testid={`start-draw-${drop.id}`}
                                >
                                  <Play className="w-4 h-4 mr-1" /> Start Live Draw
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-white/50">No active drops. Create one to engage your fans!</p>
                      <button onClick={openCreateDropModal} className="btn-outline mt-4 py-2 px-4 text-sm">
                        <Plus className="w-4 h-4 mr-1" /> Create Drop
                      </button>
                    </div>
                  )}
                </div>

                {/* Completed Drops */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400"></span> Completed ({experienceDrops.completed.length})
                  </h3>
                  {experienceDrops.completed.length > 0 ? (
                    <div className="space-y-3">
                      {experienceDrops.completed.map(drop => (
                        <div key={drop.id} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-white">{drop.title}</p>
                            <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                              COMPLETED
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm text-white/50">
                            <span>{drop.entries?.toLocaleString()} entries • {drop.numberOfWinners} winner{drop.numberOfWinners > 1 ? 's' : ''}</span>
                            <span>Drew on {drop.drawDate}</span>
                          </div>
                          {drop.winners?.length > 0 && (
                            <div className="flex gap-2 mt-3">
                              {drop.winners.slice(0, 3).map((winner, idx) => (
                                <img key={idx} src={winner.photo} alt={winner.name} className="w-8 h-8 rounded-full object-cover" title={winner.name} />
                              ))}
                              {drop.winners.length > 3 && (
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                  +{drop.winners.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/40 text-sm">No completed drops yet</p>
                  )}
                </div>
              </div>
            )}

            {/* Membership Tiers Tab */}
            {activeTab === 'membership' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <Crown className="w-5 h-5" style={{ color: colors.accent }} /> Membership Tiers
                    </h2>
                    <p className="text-white/50 text-sm mt-1">Create different membership levels to maximise revenue</p>
                  </div>
                </div>

                {/* Tier Cards */}
                <div className="grid md:grid-cols-3 gap-6">
                  {membershipTiers.map((tier, idx) => (
                    <div 
                      key={tier.id}
                      className={`relative p-6 rounded-2xl transition-all ${tier.active ? 'ring-2' : ''}`}
                      style={{ 
                        background: tier.active ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.1))' : 'rgba(255,255,255,0.03)',
                        border: tier.active ? '1px solid rgba(99, 102, 241, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                        ringColor: tier.active ? '#6366F1' : 'transparent'
                      }}
                    >
                      {tier.id === 'premium' && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                          MOST POPULAR
                        </div>
                      )}
                      {tier.id === 'vip' && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white">
                          HIGHEST VALUE
                        </div>
                      )}
                      
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold" style={{ color: tier.active ? '#818CF8' : 'white' }}>${tier.price}</span>
                          <span className="text-white/50">/month</span>
                        </div>
                      </div>

                      <ul className="space-y-3 mb-6">
                        {tier.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tier.active ? '#818CF8' : '#4ade80' }} />
                            <span className="text-white/80">{benefit}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex gap-2">
                        <button 
                          onClick={() => {
                            const updated = membershipTiers.map(t => ({...t, active: t.id === tier.id}));
                            setMembershipTiers(updated);
                            showSaveMessage(`${tier.name} tier activated!`);
                          }}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                            tier.active 
                              ? 'bg-indigo-500 text-white' 
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {tier.active ? 'Active' : 'Activate'}
                        </button>
                        <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                          <Edit3 className="w-4 h-4 text-white/50" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tier Stats */}
                <div className="glass p-6 rounded-xl">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" style={{ color: colors.accent }} /> Tier Performance
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-3xl font-bold text-white">{Math.floor((user.stats?.totalMembers || 250) * 0.6)}</p>
                      <p className="text-white/50 text-sm">Basic Members</p>
                      <p className="text-xs mt-1" style={{ color: colors.accent }}>60% of total</p>
                    </div>
                    <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-3xl font-bold text-white">{Math.floor((user.stats?.totalMembers || 250) * 0.3)}</p>
                      <p className="text-white/50 text-sm">Premium Members</p>
                      <p className="text-xs mt-1" style={{ color: colors.accent }}>30% of total</p>
                    </div>
                    <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <p className="text-3xl font-bold text-white">{Math.floor((user.stats?.totalMembers || 250) * 0.1)}</p>
                      <p className="text-white/50 text-sm">VIP Members</p>
                      <p className="text-xs mt-1" style={{ color: colors.accent }}>10% of total</p>
                    </div>
                  </div>
                </div>

                {/* Revenue Impact */}
                <div className="p-6 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.05))', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">Projected Monthly Revenue</h3>
                      <p className="text-white/50 text-sm">Based on current member distribution</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-white/50 text-xs mb-1">Basic ({Math.floor((user.stats?.totalMembers || 250) * 0.6)} × $5)</p>
                      <p className="text-xl font-bold text-white">${Math.floor((user.stats?.totalMembers || 250) * 0.6) * 5}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs mb-1">Premium ({Math.floor((user.stats?.totalMembers || 250) * 0.3)} × $15)</p>
                      <p className="text-xl font-bold text-white">${Math.floor((user.stats?.totalMembers || 250) * 0.3) * 15}</p>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs mb-1">VIP ({Math.floor((user.stats?.totalMembers || 250) * 0.1)} × $50)</p>
                      <p className="text-xl font-bold text-white">${Math.floor((user.stats?.totalMembers || 250) * 0.1) * 50}</p>
                    </div>
                    <div className="p-3 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                      <p className="text-emerald-400 text-xs mb-1">Total Monthly</p>
                      <p className="text-2xl font-bold text-emerald-400">
                        ${(Math.floor((user.stats?.totalMembers || 250) * 0.6) * 5) + (Math.floor((user.stats?.totalMembers || 250) * 0.3) * 15) + (Math.floor((user.stats?.totalMembers || 250) * 0.1) * 50)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'content' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Image className="w-5 h-5" style={{ color: colors.accent }} /> Content Studio
                  </h2>
                  <Link 
                    to={`/c/${user.username}/feed`}
                    className="btn-outline py-2 px-4 text-sm"
                    data-testid="view-feed-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> View Feed
                  </Link>
                </div>
                
                {/* Content Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{getDB().posts?.filter(p => p.creatorId === user.id).length || 0}</p>
                    <p className="text-white/50 text-sm">Total Posts</p>
                  </div>
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{user.stats?.contentViews?.toLocaleString() || 0}</p>
                    <p className="text-white/50 text-sm">Total Views</p>
                  </div>
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{getDB().posts?.filter(p => p.creatorId === user.id).reduce((sum, p) => sum + (p.likes || 0), 0) || 0}</p>
                    <p className="text-white/50 text-sm">Total Likes</p>
                  </div>
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{getDB().posts?.filter(p => p.creatorId === user.id).reduce((sum, p) => sum + (p.comments?.length || 0), 0) || 0}</p>
                    <p className="text-white/50 text-sm">Comments</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Create Post Section */}
                  <div className="glass p-6 rounded-xl space-y-4">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Plus className="w-5 h-5" style={{ color: colors.accent }} /> Create New Post
                    </h3>
                    
                    {/* Channel Selection */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Channel *</label>
                      <select 
                        value={newContent.channel || 'exclusive-updates'}
                        onChange={(e) => setNewContent({ ...newContent, channel: e.target.value })}
                        className="input-field w-full"
                        data-testid="content-channel-select"
                      >
                        <option value="exclusive-updates">Exclusive Updates</option>
                        <option value="behind-the-scenes">Behind the Scenes</option>
                        <option value="personal">Personal</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Caption *</label>
                      <textarea 
                        value={newContent.text}
                        onChange={(e) => setNewContent({ ...newContent, text: e.target.value })}
                        placeholder="Share something exclusive with your inner circle..."
                        className="input-field w-full h-32 resize-none"
                        data-testid="content-caption-input"
                      />
                    </div>
                    
                    {/* File Upload Area */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Media (optional)</label>
                      <div 
                        className="border-2 border-dashed rounded-xl p-6 text-center transition-colors hover:border-white/30 cursor-pointer"
                        style={{ borderColor: 'rgba(255,255,255,0.15)' }}
                      >
                        <div className="flex justify-center gap-4 mb-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: colors.accentGlow }}>
                            <Camera className="w-6 h-6" style={{ color: colors.accent }} />
                          </div>
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: colors.accentGlow }}>
                            <Video className="w-6 h-6" style={{ color: colors.accent }} />
                          </div>
                        </div>
                        <p className="text-white/70 mb-2">Drag and drop or click to upload</p>
                        <p className="text-white/40 text-xs">JPG, PNG, GIF, MP4, MOV • Photos up to 10MB • Videos up to 500MB</p>
                      </div>
                      <p className="text-white/40 text-xs mt-2">For demo: Enter an image URL below</p>
                      <input 
                        type="text" 
                        value={newContent.image}
                        onChange={(e) => setNewContent({ ...newContent, image: e.target.value })}
                        placeholder="https://images.unsplash.com/..."
                        className="input-field w-full mt-2"
                        data-testid="content-image-input"
                      />
                    </div>
                    
                    {/* Pin Post Option */}
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={newContent.isPinned || false}
                        onChange={(e) => setNewContent({ ...newContent, isPinned: e.target.checked })}
                        className="w-5 h-5 rounded"
                      />
                      <span className="text-white/70">Pin to top of feed</span>
                    </label>
                    
                    <button 
                      onClick={() => {
                        if (!newContent.text?.trim()) return;
                        const db = getDB();
                        const newPost = {
                          id: `post-${Date.now()}`,
                          creatorId: user.id,
                          type: newContent.image ? 'photo' : 'text',
                          caption: newContent.text,
                          channel: newContent.channel || 'exclusive-updates',
                          mediaUrls: newContent.image ? [newContent.image] : [],
                          createdAt: new Date().toISOString(),
                          likes: 0,
                          comments: [],
                          isPinned: newContent.isPinned || false
                        };
                        db.posts = [newPost, ...(db.posts || [])];
                        saveDB(db);
                        setNewContent({ text: '', image: '', channel: 'exclusive-updates', isPinned: false });
                        showSaveMessage('Post published!');
                      }} 
                      className="btn-gold w-full py-3"
                      data-testid="publish-post-btn"
                    >
                      <Send className="w-5 h-5 mr-2" /> Publish Post
                    </button>
                  </div>
                  
                  {/* Recent Posts */}
                  <div>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5" style={{ color: colors.accent }} /> Recent Posts
                    </h3>
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                      {(() => {
                        const db = getDB();
                        const creatorPosts = (db.posts || []).filter(p => p.creatorId === user.id).slice(0, 5);
                        return creatorPosts.length > 0 ? creatorPosts.map(post => (
                          <div key={post.id} className="glass p-4 rounded-xl" data-testid={`recent-post-${post.id}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                                  {post.channel?.replace('-', ' ') || 'All'}
                                </span>
                                {post.isPinned && (
                                  <span className="text-xs px-2 py-1 rounded-full" style={{ background: colors.accentGlow, color: colors.accent }}>
                                    PINNED
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <button className="p-1.5 rounded-lg hover:bg-white/10" title="Edit">
                                  <Edit3 className="w-4 h-4 text-white/50" />
                                </button>
                                <button className="p-1.5 rounded-lg hover:bg-red-500/20" title="Delete">
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </button>
                              </div>
                            </div>
                            <p className="text-sm mb-2 line-clamp-2">{post.caption}</p>
                            {post.mediaUrls?.length > 0 && (
                              <img src={post.mediaUrls[0]} alt="" className="w-full h-24 object-cover rounded-lg mb-2" />
                            )}
                            <div className="flex items-center justify-between text-white/40 text-xs">
                              <span>{new Date(post.createdAt).toLocaleDateString()}</span>
                              <div className="flex items-center gap-3">
                                <span className="flex items-center gap-1">
                                  <Heart className="w-3 h-3" /> {post.likes || 0}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MessageCircle className="w-3 h-3" /> {post.comments?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        )) : (
                          <div className="text-center py-8">
                            <Image className="w-12 h-12 mx-auto mb-3" style={{ color: colors.accent, opacity: 0.5 }} />
                            <p className="text-white/50">No posts yet. Create your first post!</p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'members' && (
              <div className="space-y-6" data-testid="members-tab">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Users className="w-5 h-5" style={{ color: colors.accent }} /> Members
                  </h2>
                  <button 
                    className="btn-outline py-2 px-4 text-sm"
                    data-testid="new-announcement-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" /> New Announcement
                  </button>
                </div>

                {/* Member Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{user.stats?.totalMembers || 0}</p>
                    <p className="text-white/50 text-sm">Total Members</p>
                  </div>
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{user.stats?.thisMonthNewMembers || 0}</p>
                    <p className="text-white/50 text-sm">New This Month</p>
                  </div>
                  <div className="p-4 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <p className="text-2xl font-bold" style={{ color: colors.accent }}>{Math.round((user.stats?.totalMembers || 0) * 0.92)}</p>
                    <p className="text-white/50 text-sm">Active (92%)</p>
                  </div>
                </div>

                {/* Search & Filter */}
                <div className="flex gap-4">
                  <div className="flex-1 relative">
                    <input 
                      type="text" 
                      placeholder="Search members by name or email..."
                      className="input-field w-full pl-10"
                      data-testid="member-search-input"
                    />
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  </div>
                  <select className="input-field" data-testid="member-sort-select">
                    <option value="recent">Most Recent</option>
                    <option value="oldest">Oldest First</option>
                    <option value="name">By Name</option>
                  </select>
                </div>

                {/* Member List */}
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 p-4 text-white/50 text-sm font-medium" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="col-span-4">Member</div>
                    <div className="col-span-3">Email</div>
                    <div className="col-span-2">Joined</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1"></div>
                  </div>

                  {/* Mock Member Rows */}
                  {[
                    { name: 'Emma Wilson', email: 'emma.w@email.com', joined: '2026-02-15', status: 'active', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100' },
                    { name: 'James Rodriguez', email: 'j.rodriguez@email.com', joined: '2026-02-12', status: 'active', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100' },
                    { name: 'Sophie Chen', email: 'sophie.c@email.com', joined: '2026-02-08', status: 'active', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100' },
                    { name: 'Michael Brown', email: 'm.brown@email.com', joined: '2026-02-05', status: 'active', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100' },
                    { name: 'Isabella Garcia', email: 'i.garcia@email.com', joined: '2026-01-28', status: 'active', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100' },
                    { name: 'David Kim', email: 'd.kim@email.com', joined: '2026-01-20', status: 'inactive', photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100' },
                  ].map((member, idx) => (
                    <div 
                      key={idx} 
                      className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-white/5 transition-colors"
                      style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
                      data-testid={`member-row-${idx}`}
                    >
                      <div className="col-span-4 flex items-center gap-3">
                        <img src={member.photo} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                        <span className="font-medium">{member.name}</span>
                      </div>
                      <div className="col-span-3 text-white/60 text-sm truncate">{member.email}</div>
                      <div className="col-span-2 text-white/50 text-sm">{new Date(member.joined).toLocaleDateString()}</div>
                      <div className="col-span-2">
                        <span 
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            background: member.status === 'active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 191, 36, 0.2)',
                            color: member.status === 'active' ? '#4ade80' : '#fbbf24'
                          }}
                        >
                          {member.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="col-span-1 text-right">
                        <button className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                          <ChevronRight className="w-4 h-4 text-white/40" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between text-sm">
                  <p className="text-white/50">Showing 6 of {user.stats?.totalMembers || 250} members</p>
                  <div className="flex gap-2">
                    <button className="px-3 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>Previous</button>
                    <button className="px-3 py-1 rounded-lg" style={{ background: colors.accent, color: colors.bgPrimary }}>1</button>
                    <button className="px-3 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>2</button>
                    <button className="px-3 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>3</button>
                    <button className="px-3 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>Next</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'payouts' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left: Payout Summary */}
                  <div className="glass p-5 rounded-xl">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Wallet className="w-5 h-5" style={{ color: colors.accent }} /> Payout Summary
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Total Earnings</span>
                        <span className="font-bold">${user.stats?.totalEarnings?.toLocaleString() || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Platform Fee (20%)</span>
                        <span className="text-white/50">-${((user.stats?.totalEarnings || 0) * 0.2).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Already Paid Out</span>
                        <span className="text-white/50">-${((user.stats?.totalEarnings || 0) * 0.8 - (user.stats?.pendingPayout || 0)).toLocaleString()}</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 flex justify-between">
                        <span className="font-semibold">Available Balance</span>
                        <span className="font-bold text-xl" style={{ color: colors.accent }}>${user.stats?.pendingPayout?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                    <p className="text-white/40 text-xs mt-4">Next payout: Friday • Automatic weekly payouts</p>
                  </div>

                  {/* Right: Bank Account */}
                  <div className="glass p-5 rounded-xl">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" style={{ color: colors.accent }} /> Bank Account
                    </h3>
                    
                    {user.bankDetails?.verified ? (
                      <div>
                        <div className="flex items-center gap-3 p-3 rounded-lg mb-3" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                          <Check className="w-5 h-5 text-green-500" />
                          <span className="text-green-400 font-medium text-sm">Connected</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/50">Bank</span>
                            <span>{user.bankDetails.bankName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/50">Account</span>
                            <span>••••{user.bankDetails.accountLast4}</span>
                          </div>
                        </div>
                        <button 
                          onClick={() => {
                            const db = getDB();
                            if (db.creators[user.id]) {
                              db.creators[user.id].bankDetails = { verified: false };
                              saveDB(db);
                              updateUser({ bankDetails: { verified: false } });
                            }
                          }}
                          className="btn-outline w-full py-2 text-sm mt-4"
                        >
                          Change Bank Account
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <select 
                          value={formData.bankName}
                          onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                          className="input-field w-full py-2 text-sm"
                        >
                          <option value="">Select bank...</option>
                          <option value="Commonwealth Bank">Commonwealth Bank</option>
                          <option value="Westpac">Westpac</option>
                          <option value="ANZ">ANZ</option>
                          <option value="NAB">NAB</option>
                          <option value="Bendigo Bank">Bendigo Bank</option>
                          <option value="ING">ING</option>
                        </select>
                        <div className="grid grid-cols-2 gap-3">
                          <input 
                            type="text" 
                            value={formData.bsb || ''}
                            onChange={(e) => setFormData({ ...formData, bsb: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            placeholder="BSB"
                            maxLength={6}
                            className="input-field py-2 text-sm"
                          />
                          <input 
                            type="text" 
                            value={formData.accountNumber}
                            onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                            placeholder="Account No."
                            className="input-field py-2 text-sm"
                          />
                        </div>
                        <input 
                          type="text" 
                          value={formData.accountHolderName || ''}
                          onChange={(e) => setFormData({ ...formData, accountHolderName: e.target.value })}
                          placeholder="Account holder name"
                          className="input-field w-full py-2 text-sm"
                        />
                        <button onClick={saveBankDetails} className="btn-gold w-full py-2 text-sm">
                          Connect Account
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payout History */}
                <div className="glass p-5 rounded-xl">
                  <h3 className="font-semibold mb-4">Recent Payouts</h3>
                  <div className="space-y-3">
                    {[
                      { date: 'Feb 14, 2026', amount: 1250, status: 'completed' },
                      { date: 'Feb 7, 2026', amount: 980, status: 'completed' },
                      { date: 'Jan 31, 2026', amount: 1420, status: 'completed' }
                    ].map((payout, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                        <div>
                          <p className="font-medium">${payout.amount.toLocaleString()}</p>
                          <p className="text-white/50 text-xs">{payout.date}</p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">Completed</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Create Experience Drop Modal */}
      <AnimatePresence>
        {showCreateDropModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCreateDropModal(false)}
          >
            <motion.div 
              className="glass p-8 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Gift className="w-6 h-6" style={{ color: colors.accent }} /> 
                  {newDrop.editId ? 'Edit Experience Drop' : 'Create Experience Drop'}
                </h2>
                <button onClick={() => setShowCreateDropModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-5">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Experience Title *</label>
                  <input 
                    type="text" 
                    value={newDrop.title || ''}
                    onChange={(e) => setNewDrop({ ...newDrop, title: e.target.value })}
                    placeholder="e.g. VIP Meet & Greet, Trip to Bali..."
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Experience Type *</label>
                  <select 
                    value={newDrop.type || ''}
                    onChange={(e) => setNewDrop({ ...newDrop, type: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="">Select type...</option>
                    <option value="1-on-1 Video Call">1-on-1 Video Call</option>
                    <option value="Group Session">Group Session</option>
                    <option value="Live Stream Appearance">Live Stream Appearance</option>
                    <option value="Mentorship Session">Mentorship Session</option>
                    <option value="Meet & Greet">Meet & Greet</option>
                    <option value="Exclusive Event Access">Exclusive Event Access</option>
                    <option value="Physical Prize">Physical Prize</option>
                    <option value="1-on-1 Experience">1-on-1 Experience</option>
                    <option value="Custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/70 text-sm mb-2">Description</label>
                  <textarea 
                    value={newDrop.description || ''}
                    onChange={(e) => setNewDrop({ ...newDrop, description: e.target.value })}
                    placeholder="Tell your fans what they'll win..."
                    className="input-field w-full h-20 resize-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Number of Winners *</label>
                    <input 
                      type="number" 
                      min="1"
                      max="100"
                      value={newDrop.numberOfWinners || 1}
                      onChange={(e) => setNewDrop({ ...newDrop, numberOfWinners: parseInt(e.target.value) || 1 })}
                      className="input-field w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-sm mb-2">Draw Date *</label>
                    <input 
                      type="date" 
                      value={newDrop.drawDate || ''}
                      onChange={(e) => setNewDrop({ ...newDrop, drawDate: e.target.value })}
                      className="input-field w-full"
                    />
                  </div>
                </div>

                <div className="pt-4 flex gap-3">
                  <button 
                    onClick={() => setShowCreateDropModal(false)} 
                    className="btn-outline flex-1 py-3"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => {
                      const db = getDB();
                      if (db.creators[user.id]) {
                        if (newDrop.editId) {
                          // Update existing drop
                          const dropIndex = db.creators[user.id].experienceDrops.findIndex(d => d.id === newDrop.editId);
                          if (dropIndex !== -1) {
                            db.creators[user.id].experienceDrops[dropIndex] = {
                              ...db.creators[user.id].experienceDrops[dropIndex],
                              title: newDrop.title,
                              type: newDrop.type,
                              description: newDrop.description,
                              numberOfWinners: newDrop.numberOfWinners,
                              drawDate: newDrop.drawDate
                            };
                            showSaveMessage('Drop updated!');
                          }
                        } else {
                          // Create new drop
                          const drop = {
                            id: `drop-${Date.now()}`,
                            title: newDrop.title,
                            type: newDrop.type,
                            description: newDrop.description,
                            numberOfWinners: newDrop.numberOfWinners,
                            drawDate: newDrop.drawDate,
                            status: 'active',
                            entries: Math.floor(Math.random() * 100) + 50,
                            media: []
                          };
                          db.creators[user.id].experienceDrops = [...(db.creators[user.id].experienceDrops || []), drop];
                          showSaveMessage('Drop created!');
                        }
                        saveDB(db);
                        updateUser({ experienceDrops: db.creators[user.id].experienceDrops });
                      }
                      setShowCreateDropModal(false);
                    }} 
                    disabled={!newDrop.title || !newDrop.type || !newDrop.drawDate}
                    className="btn-gold flex-1 py-3 disabled:opacity-50"
                  >
                    <Sparkles className="w-5 h-5 mr-2" /> {newDrop.editId ? 'Update Drop' : 'Create Drop'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Live Draw Modal */}
      <AnimatePresence>
        {showLiveDrawModal && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.95)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Confetti Effect */}
            {drawStep === 'winner' && (
              <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(50)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-3 h-3 rounded-sm"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: '-20px',
                      background: ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'][Math.floor(Math.random() * 5)]
                    }}
                    animate={{
                      y: [0, window.innerHeight + 100],
                      x: [0, (Math.random() - 0.5) * 200],
                      rotate: [0, 720],
                      opacity: [1, 0.8, 0]
                    }}
                    transition={{
                      duration: 3 + Math.random() * 2,
                      delay: Math.random() * 0.5,
                      ease: 'easeOut'
                    }}
                  />
                ))}
              </div>
            )}

            <motion.div 
              className="glass p-8 rounded-2xl max-w-xl w-full text-center relative"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              {drawStep === 'ready' && (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))' }}>
                    <Gift className="w-10 h-10 text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Ready to Select Winner?</h2>
                  <p className="text-white/60 mb-4">{showLiveDrawModal.title}</p>
                  
                  {/* Entry Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div>
                      <p className="text-2xl font-bold text-indigo-400">{showLiveDrawModal.entries || 0}</p>
                      <p className="text-white/50 text-xs">Total Entries</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{showLiveDrawModal.numberOfWinners || 1}</p>
                      <p className="text-white/50 text-xs">Winners</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-400">{Math.round((showLiveDrawModal.entries || 100) / 100 * 100) / 100}%</p>
                      <p className="text-white/50 text-xs">Win Chance</p>
                    </div>
                  </div>

                  {/* Entry Preview */}
                  <div className="mb-6">
                    <p className="text-white/50 text-sm mb-3">Participants preview</p>
                    <div className="flex justify-center -space-x-2">
                      {['https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50',
                        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50',
                        'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50',
                        'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=50',
                        'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=50'
                      ].map((photo, i) => (
                        <img key={i} src={photo} alt="" className="w-10 h-10 rounded-full border-2 border-slate-900 object-cover" />
                      ))}
                      <div className="w-10 h-10 rounded-full border-2 border-slate-900 bg-slate-700 flex items-center justify-center text-xs font-medium">
                        +{(showLiveDrawModal.entries || 100) - 5}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button 
                      onClick={() => {
                        setShowLiveDrawModal(null);
                        setDrawStep('ready');
                      }}
                      className="btn-outline flex-1 py-3"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={() => {
                        setDrawStep('drawing');
                        // Simulate drawing animation
                        setTimeout(() => {
                          const winners = [
                            { name: 'Emma Wilson', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100' },
                            { name: 'James Rodriguez', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100' },
                            { name: 'Sophie Chen', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100' },
                            { name: 'Michael Brown', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100' },
                            { name: 'Olivia Martinez', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100' }
                          ];
                          setSelectedWinner(winners[Math.floor(Math.random() * winners.length)]);
                          setDrawStep('winner');
                        }, 3500);
                      }}
                      className="btn-gold flex-1 py-3"
                    >
                      <Play className="w-5 h-5 mr-2" /> Start Live Draw
                    </button>
                  </div>
                </>
              )}

              {drawStep === 'drawing' && (
                <>
                  <motion.div 
                    className="w-28 h-28 mx-auto mb-6 rounded-full flex items-center justify-center relative"
                    style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))' }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ border: '3px solid transparent', borderTopColor: '#6366F1' }}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                    <Sparkles className="w-12 h-12 text-indigo-400" />
                  </motion.div>
                  <h2 className="text-2xl font-bold mb-2">Selecting Winner...</h2>
                  <p className="text-white/50 mb-6">Randomising from {showLiveDrawModal.entries || 0} entries</p>
                  
                  {/* Scrolling Names Animation */}
                  <div className="h-48 overflow-hidden relative mb-4">
                    <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-slate-900 to-transparent z-10" />
                    <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-slate-900 to-transparent z-10" />
                    <motion.div
                      animate={{ y: [0, -400, -800, -1200] }}
                      transition={{ duration: 3, ease: 'easeInOut' }}
                    >
                      {['Emma W.', 'James R.', 'Sophie C.', 'Michael B.', 'Olivia M.', 'David K.', 'Isabella G.', 'Noah P.', 'Ava L.', 'Liam J.', 'Mia S.', 'Ethan H.'].map((name, i) => (
                        <div
                          key={i}
                          className="py-3 px-4 my-2 mx-4 rounded-xl text-center"
                          style={{ background: 'rgba(255,255,255,0.05)' }}
                        >
                          <span className="font-medium">{name}</span>
                        </div>
                      ))}
                    </motion.div>
                  </div>
                </>
              )}

              {drawStep === 'winner' && (
                <>
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', bounce: 0.5, duration: 0.8 }}
                  >
                    <div className="w-24 h-24 mx-auto mb-4 rounded-full p-1" style={{ background: 'linear-gradient(135deg, #22c55e, #10b981)' }}>
                      <img 
                        src={selectedWinner?.photo || 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'} 
                        alt={selectedWinner?.name} 
                        className="w-full h-full rounded-full object-cover"
                      />
                    </div>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <PartyPopper className="w-6 h-6 text-amber-400" />
                      <h2 className="text-2xl font-bold text-emerald-400">Winner!</h2>
                      <PartyPopper className="w-6 h-6 text-amber-400" />
                    </div>
                    <p className="text-3xl font-bold mb-2 text-white">{selectedWinner?.name || 'Emma Wilson'}</p>
                    <p className="text-white/50 mb-6">
                      Winner of: <span className="text-indigo-400">{showLiveDrawModal.title}</span>
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="flex gap-3"
                  >
                    <button 
                      onClick={() => {
                        // Mark drop as completed
                        const db = getDB();
                        if (db.creators[user.id]) {
                          const dropIdx = db.creators[user.id].experienceDrops?.findIndex(d => d.id === showLiveDrawModal.id);
                          if (dropIdx !== -1) {
                            db.creators[user.id].experienceDrops[dropIdx].status = 'completed';
                            db.creators[user.id].experienceDrops[dropIdx].winners = [selectedWinner];
                            saveDB(db);
                            updateUser({ experienceDrops: db.creators[user.id].experienceDrops });
                          }
                        }
                        setShowLiveDrawModal(null);
                        setDrawStep('ready');
                        setSelectedWinner(null);
                        showSaveMessage('Winner recorded!');
                      }}
                      className="btn-outline flex-1 py-3"
                    >
                      <Check className="w-5 h-5 mr-2" /> Complete Draw
                    </button>
                    <button className="btn-gold flex-1 py-3">
                      <Mail className="w-5 h-5 mr-2" /> Notify Winner
                    </button>
                  </motion.div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ CREATOR PUBLIC PAGE (Fan View) ============
const CreatorPublicPage = () => {
  const { username } = useParams();
  const { user, subscribeTo, registerFan } = useAuth();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState('card'); // 'card', 'processing', 'success'
  const [signupData, setSignupData] = useState({ name: '', email: '' });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const db = getDB();
    const c = db.creators[username];
    setCreator(c);
    
    if (c) {
      // Get member count
      const count = c.stats?.totalMembers || db.subscriptions.filter(s => s.creatorId === c.id).length;
      setMemberCount(count);
    }
    
    if (user?.role === 'fan' && c) {
      const sub = db.subscriptions.find(s => s.fanId === user.id && s.creatorId === c.id);
      setIsSubscribed(!!sub);
    }
  }, [username, user]);

  // Countdown timer
  useEffect(() => {
    if (!creator?.experienceDrop?.drawDate) return;
    
    const updateCountdown = () => {
      const drawDate = new Date(creator.experienceDrop.drawDate);
      const now = new Date();
      const diff = drawDate - now;
      
      if (diff > 0) {
        setCountdown({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60)
        });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [creator]);

  const handleSubscribe = () => {
    if (!user) {
      setShowSignup(true);
      return;
    }
    
    if (user.role === 'fan') {
      setShowPayment(true);
      setPaymentStep('card');
    }
  };

  const processPayment = () => {
    setPaymentStep('processing');
    setTimeout(() => {
      setPaymentStep('success');
      subscribeTo(creator.id);
      setIsSubscribed(true);
      setMemberCount(prev => prev + 1);
      setTimeout(() => {
        setShowPayment(false);
      }, 2000);
    }, 2000);
  };

  const handleSignupAndSubscribe = () => {
    if (!signupData.name || !signupData.email) return;
    setShowSignup(false);
    setShowPayment(true);
    setPaymentStep('card');
  };

  const completeSignupAndPayment = () => {
    setPaymentStep('processing');
    setTimeout(() => {
      setPaymentStep('success');
      registerFan(signupData, creator.id);
      setIsSubscribed(true);
      setMemberCount(prev => prev + 1);
      setTimeout(() => {
        setShowPayment(false);
      }, 2000);
    }, 2000);
  };

  if (!creator) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Creator Not Found</h1>
          <p className="text-white/60">This creator page doesn't exist.</p>
          <button onClick={() => navigate('/')} className="btn-gold mt-4">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          {/* Creator Profile */}
          <div className="relative inline-block mb-6">
            <img 
              src={creator.photo} 
              alt={creator.name} 
              className="w-32 h-32 rounded-full object-cover mx-auto ring-4"
              style={{ ringColor: colors.accent }}
            />
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold" style={{ background: colors.accent, color: colors.bgPrimary }}>
              VERIFIED
            </div>
          </div>

          <h1 className="text-4xl font-bold mb-2">{creator.name}</h1>
          <p className="text-white/60 mb-2">@{creator.username}</p>
          
          {/* Member Count Badge */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <Users className="w-4 h-4" style={{ color: colors.accent }} />
              <span className="font-semibold">{memberCount.toLocaleString()} members</span>
            </div>
          </div>
          
          {creator.bio && <p className="text-white/70 mb-8">{creator.bio}</p>}

          {/* Active Experience Drops - Show ALL */}
          <div className="glass p-8 rounded-2xl mb-8 text-left">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8" style={{ color: colors.accent }} />
                <h2 className="text-2xl font-bold">Experience Drops</h2>
              </div>
              {creator.experienceDrops?.filter(d => d.status === 'active').length > 0 && (
                <span className="badge-live px-3 py-1 rounded-full text-xs font-bold">
                  {creator.experienceDrops.filter(d => d.status === 'active').length} LIVE
                </span>
              )}
            </div>
            
            {/* Show ALL active drops */}
            {creator.experienceDrops?.filter(d => d.status === 'active').length > 0 ? (
              <div className={`grid gap-4 mb-6 ${creator.experienceDrops.filter(d => d.status === 'active').length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : ''}`}>
                {creator.experienceDrops.filter(d => d.status === 'active').map((drop, idx) => (
                  <div key={drop.id} className="p-5 rounded-xl" style={{ background: colors.accentGlow, border: `1px solid ${colors.accent}30` }}>
                    <div className="mb-2">
                      <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(255,255,255,0.1)' }}>
                        {drop.type}
                      </span>
                      {drop.numberOfWinners > 1 && (
                        <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }}>
                          {drop.numberOfWinners} Winners
                        </span>
                      )}
                    </div>
                    <h4 className="text-lg font-bold mb-2 text-white">{drop.title}</h4>
                    {drop.description && (
                      <p className="text-white/60 text-sm mb-3 line-clamp-2">{drop.description}</p>
                    )}
                    <div className="flex items-center justify-between text-sm text-white/50">
                      <span><Calendar className="w-3 h-3 inline mr-1" /> {new Date(drop.drawDate).toLocaleDateString()}</span>
                      <span><Users className="w-3 h-3 inline mr-1" /> {drop.entries} entered</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : creator.experienceDrop?.title ? (
              <div className="mb-6">
                {/* Countdown Timer */}
                {countdown.days >= 0 && (
                  <div className="mb-4 p-4 rounded-xl text-center" style={{ background: colors.accentGlow, border: `1px solid ${colors.accent}30` }}>
                    <p className="text-white/60 text-sm mb-2">Draw starts in</p>
                    <div className="flex justify-center gap-3">
                      <div className="text-center">
                        <p className="text-2xl font-bold" style={{ color: colors.accent }}>{countdown.days}</p>
                        <p className="text-white/50 text-xs">days</p>
                      </div>
                      <span className="text-2xl font-bold text-white/30">:</span>
                      <div className="text-center">
                        <p className="text-2xl font-bold" style={{ color: colors.accent }}>{countdown.hours}</p>
                        <p className="text-white/50 text-xs">hours</p>
                      </div>
                      <span className="text-2xl font-bold text-white/30">:</span>
                      <div className="text-center">
                        <p className="text-2xl font-bold" style={{ color: colors.accent }}>{countdown.minutes}</p>
                        <p className="text-white/50 text-xs">mins</p>
                      </div>
                      <span className="text-2xl font-bold text-white/30">:</span>
                      <div className="text-center">
                        <p className="text-2xl font-bold" style={{ color: colors.accent }}>{countdown.seconds}</p>
                        <p className="text-white/50 text-xs">secs</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Experience Media Gallery */}
                {creator.experienceDrop.media?.length > 0 && (
                  <div className="mb-4 rounded-xl overflow-hidden">
                    <div className={`grid ${creator.experienceDrop.media.length === 1 ? '' : 'grid-cols-2'} gap-2`}>
                      {creator.experienceDrop.media.slice(0, 4).map((media, idx) => (
                        <div key={idx} className={`relative ${creator.experienceDrop.media.length === 1 ? 'aspect-video' : 'aspect-square'} overflow-hidden rounded-lg`}>
                          <img 
                            src={media.url} 
                            alt={`Experience ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-6 rounded-xl" style={{ background: colors.accentGlow, border: `1px solid ${colors.accent}30` }}>
                  <p className="text-2xl font-bold mb-2" style={{ color: colors.accent }}>{creator.experienceDrop.title}</p>
                  {creator.experienceDrop.description && (
                    <p className="text-white/70 mb-4 text-sm leading-relaxed">{creator.experienceDrop.description}</p>
                  )}
                  <div className="flex items-center justify-between text-white/70 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Draw: {creator.experienceDrop.drawDate ? new Date(creator.experienceDrop.drawDate).toLocaleDateString() : 'TBA'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" style={{ color: colors.accent }} />
                      <span><strong style={{ color: colors.accent }}>{creator.stats?.totalExperienceEntries || memberCount}</strong> fans entered ✨</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-xl mb-6" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <p className="text-white/50">No active experience drops right now. Join for exclusive content!</p>
              </div>
            )}

            {/* Recent Winners */}
            {creator.experienceDrop?.pastWinners?.length > 0 && (
              <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Star className="w-5 h-5" style={{ color: colors.accent }} /> Recent Winners
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {creator.experienceDrop.pastWinners.map((winner, idx) => (
                    <div key={idx} className="flex-shrink-0 text-center">
                      <img 
                        src={winner.photo} 
                        alt={winner.name}
                        className="w-12 h-12 rounded-full object-cover mx-auto mb-1 ring-2"
                        style={{ ringColor: colors.accent }}
                      />
                      <p className="text-xs font-medium">{winner.name}</p>
                      <p className="text-xs text-white/40">{new Date(winner.date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* What You Get Section */}
            {!isSubscribed && (
              <div className="mb-6 p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" style={{ color: colors.accent }} /> What You Get as a Member
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80">Automatic entry to ALL experience drops above</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80">Exclusive behind-the-scenes content daily</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80">Access to themed content channels</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80">Member-only perks & discounts</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 text-green-400 flex-shrink-0" />
                    <span className="text-white/80">Direct connection with {creator.name.split(' ')[0]}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 rounded-xl mb-6" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <span className="text-white/70">Join Inner Circle</span>
              <span className="text-2xl font-bold" style={{ color: colors.accent }}>{creator.membershipPrice || '$5/month'}</span>
            </div>

            {isSubscribed ? (
              <div className="space-y-3">
                <div className="p-4 rounded-xl" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-semibold">You're in the inner circle!</span>
                  </div>
                  <p className="text-white/60 text-sm mt-1">You're automatically entered to win all experiences</p>
                </div>
                <button 
                  onClick={() => navigate(`/c/${creator.username}/feed`)} 
                  className="btn-gold w-full py-4 text-lg"
                  data-testid="view-feed-btn"
                >
                  <Image className="w-5 h-5 mr-2" /> View Exclusive Content
                </button>
                <button 
                  onClick={() => navigate('/fan/dashboard')} 
                  className="btn-outline w-full py-3"
                  data-testid="my-memberships-btn"
                >
                  <Ticket className="w-5 h-5 mr-2" /> My Memberships
                </button>
              </div>
            ) : (
              <button 
                onClick={handleSubscribe} 
                className="btn-gold w-full py-4 text-lg"
                data-testid="become-member-btn"
              >
                <Crown className="w-5 h-5 mr-2" /> Join {creator.name.split(' ')[0]}'s Inner Circle
              </button>
            )}

            {!isSubscribed && (
              <p className="text-white/50 text-sm text-center mt-4">
                Get exclusive access to {creator.name.split(' ')[0]}'s world—behind-the-scenes content, updates, and your chance to win personal experiences.
              </p>
            )}
          </div>

          {/* Content Preview - Using new posts system */}
          {(() => {
            const db = getDB();
            const creatorPosts = (db.posts || []).filter(p => p.creatorId === creator.id).slice(0, 3);
            if (creatorPosts.length === 0) return null;
            
            return (
              <div className="glass p-6 rounded-2xl text-left">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Image className="w-5 h-5" style={{ color: colors.accent }} /> Latest Posts
                  </h3>
                  {isSubscribed && (
                    <button 
                      onClick={() => navigate(`/c/${creator.username}/feed`)}
                      className="text-sm font-medium flex items-center gap-1 hover:opacity-80"
                      style={{ color: colors.accent }}
                    >
                      View All <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {isSubscribed ? (
                  <div className="space-y-4">
                    {creatorPosts.map(post => (
                      <div 
                        key={post.id} 
                        className="p-4 rounded-xl cursor-pointer hover:bg-white/10 transition-colors" 
                        style={{ background: 'rgba(255,255,255,0.05)' }}
                        onClick={() => navigate(`/c/${creator.username}/feed`)}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <img src={creator.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                          <div className="flex-1">
                            <p className="font-bold">{creator.name}</p>
                            <p className="text-white/40 text-xs">{new Date(post.createdAt).toLocaleDateString()}</p>
                          </div>
                          {post.isPinned && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: colors.accentGlow, color: colors.accent }}>PINNED</span>
                          )}
                        </div>
                        <p className="mb-3 line-clamp-2">{post.caption}</p>
                        {post.mediaUrls?.length > 0 && (
                          <img src={post.mediaUrls[0]} alt="" className="w-full h-48 object-cover rounded-lg" />
                        )}
                        <div className="flex items-center gap-4 mt-3 text-white/50 text-sm">
                          <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {post.likes || 0}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> {post.comments?.length || 0}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative">
                    <div className="space-y-4 filter blur-md pointer-events-none">
                      {creatorPosts.slice(0, 2).map(post => (
                        <div key={post.id} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <p>{post.caption?.substring(0, 100)}...</p>
                          {post.mediaUrls?.length > 0 && (
                            <div className="w-full h-32 rounded-lg mt-2" style={{ background: 'rgba(255,255,255,0.1)' }} />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center p-6 rounded-xl" style={{ background: 'rgba(10, 22, 40, 0.95)' }}>
                        <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: colors.accent }} />
                        <p className="font-bold text-lg">Members Only</p>
                        <p className="text-white/60 text-sm mb-4">{creatorPosts.length} exclusive posts</p>
                        <button 
                          onClick={handleSubscribe}
                          className="btn-gold py-2 px-6"
                        >
                          Join to Unlock
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </motion.div>
      </div>

      {/* Signup Modal */}
      <AnimatePresence>
        {showSignup && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSignup(false)}
          >
            <motion.div 
              className="glass p-8 rounded-2xl max-w-md w-full"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <PartyPopper className="w-16 h-16 mx-auto mb-4" style={{ color: colors.accent }} />
                <h2 className="text-2xl font-bold">Join {creator.name.split(' ')[0]}'s Inner Circle!</h2>
                <p className="text-white/60 mt-2">Create your account to join {creator.name}</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Your Name</label>
                  <input 
                    type="text"
                    value={signupData.name}
                    onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                    placeholder="Your name"
                    className="input-field w-full"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Email</label>
                  <input 
                    type="email"
                    value={signupData.email}
                    onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="input-field w-full"
                  />
                </div>
                <button onClick={handleSignupAndSubscribe} className="btn-gold w-full py-4" data-testid="confirm-membership-btn">
                  Continue to Payment
                </button>
              </div>
              
              <p className="text-white/40 text-sm text-center mt-4">
                Demo mode - no real payment required
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPayment && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.9)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => paymentStep === 'card' && setShowPayment(false)}
          >
            <motion.div 
              className="glass p-8 rounded-2xl max-w-md w-full"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
            >
              {paymentStep === 'card' && (
                <>
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: colors.accentGlow }}>
                      <CreditCard className="w-8 h-8" style={{ color: colors.accent }} />
                    </div>
                    <h2 className="text-2xl font-bold">Complete Your Purchase</h2>
                    <p className="text-white/60 mt-2">Join {creator.name}'s inner circle</p>
                  </div>

                  {/* Order Summary */}
                  <div className="p-4 rounded-xl mb-6" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/70">Inner Circle Access</span>
                      <span className="font-bold">{creator.membershipPrice || '$5/month'}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-white/50">You're automatically entered to win</span>
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                  </div>
                  
                  {/* Card Form (Mocked) */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-white/70 text-sm mb-2">Card Number</label>
                      <div className="relative">
                        <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <input 
                          type="text"
                          placeholder="4242 4242 4242 4242"
                          className="input-field w-full pl-12"
                          defaultValue="4242 4242 4242 4242"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Expiry</label>
                        <input 
                          type="text"
                          placeholder="MM/YY"
                          className="input-field w-full"
                          defaultValue="12/28"
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">CVC</label>
                        <input 
                          type="text"
                          placeholder="123"
                          className="input-field w-full"
                          defaultValue="123"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <button 
                    onClick={user ? processPayment : completeSignupAndPayment} 
                    className="btn-gold w-full py-4 text-lg"
                  >
                    <Lock className="w-5 h-5 mr-2" /> Pay {creator.membershipPrice || '$5/month'}
                  </button>
                  
                  <div className="flex items-center justify-center gap-4 mt-4 text-white/40 text-sm">
                    <div className="flex items-center gap-1">
                      <Shield className="w-4 h-4" /> Secure
                    </div>
                    <div className="flex items-center gap-1">
                      <Lock className="w-4 h-4" /> Encrypted
                    </div>
                  </div>
                </>
              )}

              {paymentStep === 'processing' && (
                <div className="text-center py-8">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="w-16 h-16 mx-auto mb-6 rounded-full border-4 border-white/20"
                    style={{ borderTopColor: colors.accent }}
                  />
                  <h2 className="text-xl font-bold mb-2">Processing Payment...</h2>
                  <p className="text-white/60">Please wait while we confirm your subscription</p>
                </div>
              )}

              {paymentStep === 'success' && (
                <motion.div 
                  className="text-center py-8"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(34, 197, 94, 0.2)' }}
                  >
                    <Check className="w-10 h-10 text-green-400" />
                  </motion.div>
                  <h2 className="text-2xl font-bold mb-2">Welcome to the Club!</h2>
                  <p className="text-white/60 mb-4">You're now a {creator.name} member</p>
                  <div className="p-4 rounded-xl" style={{ background: 'rgba(212, 168, 83, 0.1)' }}>
                    <p className="text-sm" style={{ color: colors.accent }}>
                      You're automatically entered in all Experience Drops!
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ MEMBER CONTENT FEED (OnlyFans-style) ============
const CONTENT_CHANNELS = [
  { id: 'all', label: 'All Posts', icon: Grid },
  { id: 'exclusive-updates', label: 'Exclusive', icon: Crown },
  { id: 'behind-the-scenes', label: 'Behind the Scenes', icon: Camera },
  { id: 'personal', label: 'Personal', icon: Heart }
];

const MemberContentFeed = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [posts, setPosts] = useState([]);
  const [activeChannel, setActiveChannel] = useState('all');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [newComment, setNewComment] = useState({});
  const [showComments, setShowComments] = useState({});
  const [stories, setStories] = useState([]);
  const [showStory, setShowStory] = useState(null);
  const [showTipModal, setShowTipModal] = useState(null);
  const [savedPostIds, setSavedPostIds] = useState([]);

  useEffect(() => {
    const db = getDB();
    const c = db.creators[username];
    setCreator(c);
    
    if (c) {
      // Get posts for this creator
      const creatorPosts = (db.posts || []).filter(p => p.creatorId === c.id);
      // Sort by pinned first, then by date
      creatorPosts.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setPosts(creatorPosts);

      // Get stories for this creator
      const creatorStories = (db.stories || []).filter(s => s.creatorId === c.id && new Date(s.expiresAt) > new Date());
      setStories(creatorStories);
    }
    
    if (user?.role === 'fan' && c) {
      const sub = db.subscriptions.find(s => s.fanId === user.id && s.creatorId === c.id);
      setIsSubscribed(!!sub);
      // Get saved posts
      const saved = (db.savedPosts || []).filter(s => s.fanId === user.id).map(s => s.postId);
      setSavedPostIds(saved);
    }
  }, [username, user]);

  const filteredPosts = activeChannel === 'all' 
    ? posts 
    : posts.filter(p => p.channel === activeChannel);

  const handleLike = (postId) => {
    const db = getDB();
    const postIndex = db.posts.findIndex(p => p.id === postId);
    if (postIndex !== -1) {
      db.posts[postIndex].likes = (db.posts[postIndex].likes || 0) + 1;
      saveDB(db);
      setPosts(posts.map(p => p.id === postId ? { ...p, likes: p.likes + 1 } : p));
    }
  };

  const handleComment = (postId) => {
    if (!newComment[postId]?.trim() || !user) return;
    
    const db = getDB();
    const postIndex = db.posts.findIndex(p => p.id === postId);
    if (postIndex !== -1) {
      const comment = {
        id: `c-${Date.now()}`,
        fanId: user.id,
        fanName: user.name,
        fanPhoto: user.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=3B82F6&color=fff`,
        text: newComment[postId],
        createdAt: new Date().toISOString()
      };
      db.posts[postIndex].comments = [...(db.posts[postIndex].comments || []), comment];
      saveDB(db);
      setPosts(posts.map(p => p.id === postId ? { ...p, comments: [...(p.comments || []), comment] } : p));
      setNewComment({ ...newComment, [postId]: '' });
    }
  };

  const handleSavePost = (postId) => {
    const db = getDB();
    const isSaved = savedPostIds.includes(postId);
    
    if (isSaved) {
      db.savedPosts = (db.savedPosts || []).filter(s => !(s.fanId === user.id && s.postId === postId));
      setSavedPostIds(savedPostIds.filter(id => id !== postId));
    } else {
      db.savedPosts = [...(db.savedPosts || []), { id: `save-${Date.now()}`, fanId: user.id, postId, savedAt: new Date().toISOString() }];
      setSavedPostIds([...savedPostIds, postId]);
    }
    saveDB(db);
  };

  const handleTip = (postId, amount) => {
    const db = getDB();
    const tip = {
      id: `tip-${Date.now()}`,
      fromId: user.id,
      toId: creator.id,
      postId,
      amount,
      createdAt: new Date().toISOString()
    };
    db.tips = [...(db.tips || []), tip];
    saveDB(db);
    setShowTipModal(null);
  };

  if (!creator) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Creator Not Found</h1>
          <button onClick={() => navigate('/')} className="btn-gold mt-4">Go Home</button>
        </div>
      </div>
    );
  }

  // Check if user is subscribed
  if (!isSubscribed && user?.role !== 'creator') {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="glass p-12 rounded-2xl">
              <Lock className="w-16 h-16 mx-auto mb-6" style={{ color: colors.accent }} />
              <h1 className="text-3xl font-bold mb-4">Members Only Content</h1>
              <p className="text-white/60 mb-8 max-w-md mx-auto">
                Join {creator.name}'s inner circle to unlock exclusive posts, behind-the-scenes content, and personal updates.
              </p>
              <button 
                onClick={() => navigate(`/c/${username}`)}
                className="btn-gold py-4 px-8 text-lg"
                data-testid="join-to-unlock-btn"
              >
                <Crown className="w-5 h-5 mr-2" /> Join for {creator.membershipPrice}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="member-feed-page">
      <div className="max-w-2xl mx-auto">
        {/* Stories Section */}
        {stories.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {stories.map((story, idx) => (
                <button
                  key={story.id}
                  onClick={() => setShowStory(story)}
                  className="flex flex-col items-center gap-2 flex-shrink-0"
                >
                  <div className="w-16 h-16 rounded-full p-0.5" style={{ background: 'linear-gradient(135deg, #3B82F6, #06B6D4, #8B5CF6)' }}>
                    <img src={story.mediaUrl} alt="" className="w-full h-full rounded-full object-cover border-2 border-slate-900" />
                  </div>
                  <span className="text-xs text-white/70">{idx === 0 ? 'Latest' : formatTimeAgo(story.createdAt)}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Creator Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button onClick={() => navigate(`/c/${username}`)} className="p-2 rounded-lg hover:bg-white/10">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <img src={creator.photo} alt={creator.name} className="w-12 h-12 rounded-full object-cover ring-2" style={{ ringColor: colors.accent }} />
            <div className="flex-1">
              <h1 className="text-xl font-bold">{creator.name}</h1>
              <p className="text-white/50 text-sm">@{creator.username} • {posts.length} posts</p>
            </div>
            <button onClick={() => navigate(`/c/${username}`)} className="btn-outline py-2 px-4 text-sm">
              View Profile
            </button>
          </div>

          {/* Channel Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CONTENT_CHANNELS.map(channel => (
              <button
                key={channel.id}
                onClick={() => setActiveChannel(channel.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all text-sm ${
                  activeChannel === channel.id ? 'text-white' : 'text-white/60 hover:bg-white/10'
                }`}
                style={activeChannel === channel.id ? { background: 'linear-gradient(135deg, #1E40AF, #06B6D4)' } : { background: 'rgba(255,255,255,0.05)' }}
                data-testid={`channel-${channel.id}`}
              >
                <channel.icon className="w-4 h-4" />
                {channel.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Posts Feed */}
        <div className="space-y-6">
          {filteredPosts.length === 0 ? (
            <div className="glass p-12 rounded-2xl text-center">
              <Image className="w-12 h-12 mx-auto mb-4" style={{ color: colors.accent }} />
              <p className="text-white/60">No posts in this channel yet</p>
            </div>
          ) : (
            filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-2xl overflow-hidden"
                data-testid={`post-${post.id}`}
              >
                {/* Post Header */}
                <div className="p-4 flex items-center gap-3">
                  <img src={creator.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold">{creator.name}</p>
                      {post.isPinned && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: colors.accentGlow, color: colors.accent }}>
                          PINNED
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-white/50 text-sm">
                      <span>{formatTimeAgo(post.createdAt)}</span>
                      <span>•</span>
                      <span className="capitalize">{post.channel.replace('-', ' ')}</span>
                    </div>
                  </div>
                  <button className="p-2 rounded-lg hover:bg-white/10">
                    <MoreHorizontal className="w-5 h-5 text-white/50" />
                  </button>
                </div>

                {/* Post Content */}
                <div className="px-4 pb-3">
                  <p className="whitespace-pre-wrap">{post.caption}</p>
                </div>

                {/* Post Media */}
                {post.mediaUrls && post.mediaUrls.length > 0 && (
                  <div className={`${post.mediaUrls.length > 1 ? 'grid grid-cols-2 gap-0.5' : ''}`}>
                    {post.mediaUrls.map((url, idx) => (
                      <img 
                        key={idx}
                        src={url} 
                        alt="" 
                        className={`w-full object-cover ${post.mediaUrls.length === 1 ? 'max-h-[500px]' : 'aspect-square'}`}
                      />
                    ))}
                  </div>
                )}

                {/* Post Actions */}
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <button 
                      onClick={() => handleLike(post.id)}
                      className="flex items-center gap-2 text-white/70 hover:text-red-400 transition-colors"
                      data-testid={`like-btn-${post.id}`}
                    >
                      <Heart className="w-6 h-6" />
                      <span>{post.likes}</span>
                    </button>
                    <button 
                      onClick={() => setShowComments({ ...showComments, [post.id]: !showComments[post.id] })}
                      className="flex items-center gap-2 text-white/70 hover:text-blue-400 transition-colors"
                      data-testid={`comment-btn-${post.id}`}
                    >
                      <MessageCircle className="w-6 h-6" />
                      <span>{post.comments?.length || 0}</span>
                    </button>
                    <button 
                      onClick={() => setShowTipModal(post.id)}
                      className="flex items-center gap-2 text-white/70 hover:text-green-400 transition-colors"
                      data-testid={`tip-btn-${post.id}`}
                    >
                      <CircleDollarSign className="w-6 h-6" />
                      <span className="text-sm">Tip</span>
                    </button>
                    <button 
                      onClick={() => handleSavePost(post.id)}
                      className="flex items-center gap-2 transition-colors ml-auto"
                      style={{ color: savedPostIds.includes(post.id) ? colors.accent : 'rgba(255,255,255,0.7)' }}
                    >
                      <Bookmark className="w-6 h-6" style={{ fill: savedPostIds.includes(post.id) ? colors.accent : 'transparent' }} />
                    </button>
                    <button className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                      <Send className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Comments Section */}
                  <AnimatePresence>
                    {showComments[post.id] && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-white/10 pt-3 mt-3"
                      >
                        {/* Existing Comments */}
                        <div className="space-y-3 mb-3 max-h-48 overflow-y-auto">
                          {(post.comments || []).map(comment => (
                            <div key={comment.id} className="flex gap-3">
                              <img src={comment.fanPhoto} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                              <div className="flex-1 bg-white/5 rounded-xl p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-sm">{comment.fanName}</span>
                                  <span className="text-white/40 text-xs">{formatTimeAgo(comment.createdAt)}</span>
                                </div>
                                <p className="text-sm text-white/80">{comment.text}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add Comment */}
                        {user && (
                          <div className="flex gap-3">
                            <img 
                              src={user.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=3B82F6&color=fff`} 
                              alt="" 
                              className="w-8 h-8 rounded-full object-cover flex-shrink-0" 
                            />
                            <div className="flex-1 flex gap-2">
                              <input
                                type="text"
                                value={newComment[post.id] || ''}
                                onChange={(e) => setNewComment({ ...newComment, [post.id]: e.target.value })}
                                placeholder="Add a comment..."
                                className="input-field flex-1 py-2 text-sm"
                                onKeyPress={(e) => e.key === 'Enter' && handleComment(post.id)}
                                data-testid={`comment-input-${post.id}`}
                              />
                              <button 
                                onClick={() => handleComment(post.id)}
                                className="btn-gold px-4 py-2 text-sm"
                                data-testid={`submit-comment-${post.id}`}
                              >
                                Post
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Story Modal */}
      <AnimatePresence>
        {showStory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.95)' }}
            onClick={() => setShowStory(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setShowStory(null)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 hover:bg-black/70"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-3 absolute top-4 left-4 z-10">
                <img src={creator.photo} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white" />
                <div>
                  <p className="font-bold text-white">{creator.name}</p>
                  <p className="text-white/70 text-sm">{formatTimeAgo(showStory.createdAt)}</p>
                </div>
              </div>
              <img 
                src={showStory.mediaUrl} 
                alt="" 
                className="w-full rounded-2xl object-cover"
                style={{ maxHeight: '80vh' }}
              />
              {showStory.caption && (
                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl" style={{ background: 'rgba(0,0,0,0.7)' }}>
                  <p className="text-white">{showStory.caption}</p>
                </div>
              )}
              <div className="absolute bottom-4 right-4 flex items-center gap-2 text-white/70 text-sm">
                <Eye className="w-4 h-4" /> {showStory.views} views
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tip Modal */}
      <AnimatePresence>
        {showTipModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            onClick={() => setShowTipModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="glass p-6 rounded-2xl max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                  <CircleDollarSign className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold mb-2">Send a Tip</h3>
                <p className="text-white/60 text-sm">Show your appreciation for {creator.name}</p>
              </div>
              
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[1, 5, 10, 20].map(amount => (
                  <button
                    key={amount}
                    onClick={() => handleTip(showTipModal, amount)}
                    className="p-4 rounded-xl text-center hover:scale-105 transition-transform"
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    <p className="text-xl font-bold">${amount}</p>
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <input 
                  type="number" 
                  placeholder="Custom amount" 
                  className="input-field flex-1"
                  min="1"
                />
                <button className="btn-gold px-6">Send</button>
              </div>

              <button 
                onClick={() => setShowTipModal(null)}
                className="w-full text-center text-white/50 text-sm mt-4 hover:text-white"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============ FAN DASHBOARD ============
const FanDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [creators, setCreators] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'fan') {
      navigate('/auth/fan');
      return;
    }
    
    const db = getDB();
    const subs = db.subscriptions.filter(s => s.fanId === user.id);
    setSubscriptions(subs);
    setCreators(db.creators);
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentSecondary})` }}>
              <Ticket className="w-8 h-8 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">My Inner Circles</h1>
              <p className="text-white/60">Welcome, {user.name}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="glass p-4 rounded-xl text-center">
              <Ticket className="w-6 h-6 mx-auto mb-2" style={{ color: colors.accent }} />
              <p className="text-2xl font-bold">{subscriptions.length}</p>
              <p className="text-white/60 text-sm">Creators</p>
            </div>
            <div className="glass p-4 rounded-xl text-center">
              <Gift className="w-6 h-6 mx-auto mb-2" style={{ color: colors.accent }} />
              <p className="text-2xl font-bold">{subscriptions.length}</p>
              <p className="text-white/60 text-sm">Drop Entries</p>
            </div>
            <div className="glass p-4 rounded-xl text-center">
              <Star className="w-6 h-6 mx-auto mb-2" style={{ color: colors.accent }} />
              <p className="text-2xl font-bold">0</p>
              <p className="text-white/60 text-sm">Wins</p>
            </div>
          </div>

          {/* Inner Circles */}
          <h2 className="text-xl font-bold mb-4">Your Inner Circles</h2>
          {subscriptions.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-4">
              {subscriptions.map(sub => {
                const creator = creators[sub.creatorId];
                if (!creator) return null;
                
                return (
                  <div 
                    key={sub.id} 
                    className="glass p-6 rounded-2xl cursor-pointer hover:border-gold-primary transition-all"
                    style={{ border: '1px solid rgba(212, 168, 83, 0.2)' }}
                    onClick={() => navigate(`/c/${creator.username}`)}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <img src={creator.photo} alt={creator.name} className="w-14 h-14 rounded-full object-cover ring-2" style={{ ringColor: colors.accent }} />
                      <div>
                        <h3 className="text-lg font-bold">{creator.name}</h3>
                        <p className="text-white/60 text-sm">@{creator.username}</p>
                      </div>
                      <div className="ml-auto">
                        <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#4ade80' }}>
                          ACTIVE
                        </span>
                      </div>
                    </div>
                    
                    {creator.experienceDrop?.title && (
                      <div className="p-3 rounded-lg mb-3" style={{ background: 'rgba(212, 168, 83, 0.1)' }}>
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4" style={{ color: colors.accent }} />
                          <span className="text-sm font-medium" style={{ color: colors.accent }}>{creator.experienceDrop.title}</span>
                        </div>
                        <p className="text-white/50 text-xs mt-1">Draw: {creator.experienceDrop.drawDate || 'TBA'}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/50">Joined {new Date(sub.joinedAt).toLocaleDateString()}</span>
                      <span className="flex items-center gap-1 text-white/70">
                        View <ChevronRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="glass p-8 rounded-2xl text-center">
              <Ticket className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-bold mb-2">No Inner Circles Yet</h3>
              <p className="text-white/60 mb-4">Find a creator's link on social media to join your first inner circle!</p>
              <button onClick={() => navigate('/')} className="btn-gold">
                Find Your Creator
              </button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mt-8">
            <button onClick={() => navigate('/draw/results')} className="btn-outline w-full py-4">
              <Gift className="w-5 h-5 mr-2" /> Watch Draw Results
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ DIGITAL LOTTERY DRAW ============
const LiveDrawPage = () => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [winner, setWinner] = useState(null);
  const [currentName, setCurrentName] = useState('');
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [drawPhase, setDrawPhase] = useState('idle');
  const [countdown, setCountdown] = useState(null);
  const [shuffleSpeed, setShuffleSpeed] = useState(50);
  const [showConfetti, setShowConfetti] = useState(false);
  const [winnerHistory, setWinnerHistory] = useState([
    { name: 'Jessica M.', prize: 'VIP Concert Experience', date: '2 days ago' },
    { name: 'Ryan T.', prize: 'Private Gaming Session', date: '4 days ago' },
    { name: 'Amanda K.', prize: 'Luxury Spa Retreat', date: '1 week ago' },
  ]);

  // Simulated large entry count for demo
  const totalEntries = 12847;

  // Sound effects using Web Audio API
  const playSound = (type) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      if (type === 'tick') {
        oscillator.frequency.value = 800 + Math.random() * 400;
        gainNode.gain.value = 0.1;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.05);
      } else if (type === 'countdown') {
        oscillator.frequency.value = 440;
        gainNode.gain.value = 0.2;
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.15);
      } else if (type === 'winner') {
        // Play a celebratory chord
        [523.25, 659.25, 783.99].forEach((freq, i) => {
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          osc.connect(gain);
          gain.connect(audioContext.destination);
          osc.frequency.value = freq;
          gain.gain.value = 0.15;
          osc.start(audioContext.currentTime + i * 0.1);
          osc.stop(audioContext.currentTime + 0.5 + i * 0.1);
        });
      }
    } catch (e) {
      // Audio not supported
    }
  };

  // Initialize creator on mount
  useEffect(() => {
    const db = getDB();
    const creatorsList = Object.values(db.creators);
    if (creatorsList.length > 0 && !selectedCreator) {
      setSelectedCreator(creatorsList[0]);
    }
  }, []);

  const db = getDB();
  const creators = Object.values(db.creators);

  // Load participants when creator changes
  useEffect(() => {
    if (!selectedCreator) return;
    
    const currentDb = getDB();
    const subs = (currentDb.subscriptions || []).filter(s => s.creatorId === selectedCreator.id);
    let members = subs.map(s => (currentDb.fans || {})[s.fanId]).filter(Boolean);
    
    const demoNames = [
      'Emma W.', 'Liam K.', 'Olivia M.', 'Noah S.', 'Ava P.', 'Ethan R.', 
      'Sophia L.', 'Mason B.', 'Isabella G.', 'James T.', 'Mia C.', 'Benjamin F.',
      'Charlotte H.', 'Lucas D.', 'Amelia R.', 'Henry W.', 'Harper J.', 'Sebastian M.',
      'Evelyn K.', 'Jack P.', 'Luna S.', 'Owen B.', 'Chloe N.', 'Alexander V.',
      'Penelope Z.', 'William A.', 'Layla Q.', 'Daniel X.', 'Riley Y.', 'Michael U.'
    ];
    
    while (members.length < 30) {
      members.push({
        id: `demo-${members.length}`,
        name: demoNames[members.length % demoNames.length]
      });
    }
    setParticipants(members);
  }, [selectedCreator?.id]);

  // Name shuffling animation with sound
  useEffect(() => {
    if (drawPhase === 'idle' || drawPhase === 'reveal' || participants.length === 0) return;
    
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * participants.length);
      setCurrentName(participants[randomIndex]?.name || '');
      if (drawPhase === 'slowing') {
        playSound('tick');
      }
    }, shuffleSpeed);
    
    return () => clearInterval(interval);
  }, [drawPhase, shuffleSpeed, participants.length]);

  const startDraw = async () => {
    if (isDrawing || participants.length === 0) return;
    setIsDrawing(true);
    setWinner(null);
    setShowConfetti(false);
    setDrawPhase('shuffling');
    setShuffleSpeed(30);
    
    // Countdown phase with sound
    for (let i = 3; i >= 1; i--) {
      setCountdown(i);
      playSound('countdown');
      await new Promise(r => setTimeout(r, 1000));
    }
    setCountdown(null);
    
    // Fast shuffle
    setDrawPhase('shuffling');
    await new Promise(r => setTimeout(r, 2000));
    
    // Slow down gradually
    setDrawPhase('slowing');
    for (const speed of [80, 150, 300, 500, 800]) {
      setShuffleSpeed(speed);
      await new Promise(r => setTimeout(r, 800));
    }
    
    // Select winner
    const winnerIndex = Math.floor(Math.random() * participants.length);
    const selectedWinner = participants[winnerIndex];
    setCurrentName(selectedWinner.name);
    
    await new Promise(r => setTimeout(r, 500));
    setDrawPhase('reveal');
    setWinner(selectedWinner);
    setShowConfetti(true);
    playSound('winner');
    setIsDrawing(false);
    
    // Add to history
    setWinnerHistory(prev => [{
      name: selectedWinner.name,
      prize: selectedCreator?.experienceDrop?.title || 'Experience',
      date: 'Just now'
    }, ...prev.slice(0, 4)]);
  };

  const resetDraw = () => {
    setWinner(null);
    setDrawPhase('idle');
    setCurrentName('');
    setCountdown(null);
    setShowConfetti(false);
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <Confetti active={showConfetti} />
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          
          {/* Creator Selector */}
          {creators.length > 1 && (
            <div className="flex justify-center gap-2 mb-8 flex-wrap">
              {creators.map(c => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedCreator(c); resetDraw(); }}
                  disabled={isDrawing}
                  className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all disabled:opacity-50 ${
                    selectedCreator?.id === c.id ? '' : 'text-gray-400 hover:bg-white/5'
                  }`}
                  style={selectedCreator?.id === c.id ? { background: colors.accent, color: colors.bgPrimary } : { background: colors.bgSecondary }}
                >
                  <img src={c.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {/* Prize Display */}
          <div className="mb-8">
            <p className="text-gray-500 text-sm mb-2">PRIZE</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ color: colors.accent }}>
              {selectedCreator?.experienceDrop?.title || 'Amazing Experience!'}
            </h1>
          </div>

          {/* Entry Stats */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
            <div className="stat-card">
              <p className="stat-value" style={{ color: colors.accent }}>{totalEntries.toLocaleString()}</p>
              <p className="stat-label">Entries</p>
            </div>
            <div className="stat-card">
              <p className="stat-value">1</p>
              <p className="stat-label">Winner</p>
            </div>
            <div className="stat-card">
              <p className="stat-value" style={{ color: colors.accentSecondary }}>LIVE</p>
              <p className="stat-label">Status</p>
            </div>
          </div>

          {/* Main Draw Display */}
          <div className="relative mb-10">
            {/* Countdown Overlay */}
            <AnimatePresence>
              {countdown && (
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 1.5, opacity: 0 }}
                  className="absolute inset-0 z-20 flex items-center justify-center"
                  style={{ background: 'rgba(26, 35, 50, 0.9)' }}
                >
                  <span className="text-9xl font-bold" style={{ color: colors.accent, textShadow: `0 0 60px ${colors.accent}` }}>
                    {countdown}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Name Display Box */}
            <div 
              className="relative overflow-hidden rounded-2xl p-1 mx-auto max-w-2xl"
              style={{ 
                background: drawPhase !== 'idle' && !winner 
                  ? `linear-gradient(90deg, ${colors.accent}, ${colors.accentSecondary}, ${colors.accent})` 
                  : `1px solid rgba(255,255,255,0.1)`,
                backgroundSize: '200% 100%',
                animation: drawPhase === 'shuffling' ? 'shimmer 1s infinite linear' : 'none'
              }}
            >
              <div 
                className="rounded-xl py-16 px-8"
                style={{ background: colors.bgCard }}
              >
                {!isDrawing && !winner && drawPhase === 'idle' ? (
                  <div className="text-center">
                    <div className="flex justify-center gap-2 mb-4">
                      {[...Array(3)].map((_, i) => (
                        <motion.div 
                          key={i} 
                          className="w-3 h-3 rounded-full" 
                          style={{ background: colors.accent }}
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                        />
                      ))}
                    </div>
                    <p className="text-2xl text-gray-400">Ready to select winner</p>
                    <p className="text-gray-500 text-sm mt-2">from {totalEntries.toLocaleString()} verified entries</p>
                  </div>
                ) : winner ? (
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', damping: 10, stiffness: 100 }}
                  >
                    <motion.div
                      animate={{ rotate: [0, -5, 5, -5, 5, 0] }}
                      transition={{ duration: 0.5 }}
                    >
                      <PartyPopper className="w-16 h-16 mx-auto mb-4" style={{ color: colors.accent }} />
                    </motion.div>
                    <p className="text-lg text-gray-400 mb-2">WINNER</p>
                    <p className="text-5xl md:text-6xl font-bold" style={{ color: colors.accent }}>
                      {winner.name}
                    </p>
                    <p className="text-gray-400 mt-4">{selectedCreator?.experienceDrop?.title}</p>
                  </motion.div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">SELECTING FROM {totalEntries.toLocaleString()} ENTRIES</p>
                    <motion.p 
                      className="text-5xl md:text-6xl font-bold"
                      style={{ 
                        color: drawPhase === 'slowing' ? colors.accent : colors.textMuted,
                      }}
                      animate={drawPhase === 'slowing' ? { scale: [1, 1.02, 1] } : {}}
                      transition={{ duration: 0.1 }}
                    >
                      {currentName || '...'}
                    </motion.p>
                  </div>
                )}
              </div>
            </div>

            {/* Scanning Lines Effect */}
            {isDrawing && !winner && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                <motion.div
                  className="absolute left-0 right-0 h-0.5"
                  style={{ background: `linear-gradient(90deg, transparent, ${colors.accent}, transparent)` }}
                  animate={{ top: ['0%', '100%'] }}
                  transition={{ duration: 0.5, repeat: Infinity, ease: 'linear' }}
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          {!winner ? (
            <button 
              onClick={startDraw} 
              disabled={isDrawing}
              className="btn-gold py-4 px-16 text-xl disabled:opacity-50"
              data-testid="start-draw-btn"
            >
              {isDrawing ? (
                <>
                  <motion.div 
                    className="w-6 h-6 border-2 border-black border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                  Drawing...
                </>
              ) : (
                <>
                  <Play className="w-6 h-6" /> Start Draw
                </>
              )}
            </button>
          ) : (
            <div className="space-y-4">
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="glass p-6 rounded-xl inline-block"
              >
                <p className="text-gray-400 mb-1">Congratulations! You've won:</p>
                <p className="text-xl font-bold" style={{ color: colors.accent }}>{selectedCreator?.experienceDrop?.title}</p>
              </motion.div>
              <div>
                <button onClick={resetDraw} className="btn-outline py-3 px-8">
                  Draw Again
                </button>
              </div>
            </div>
          )}

          {/* Winner History */}
          <div className="mt-12 glass p-6 rounded-2xl">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" style={{ color: colors.accent }} /> 
              Recent Winners
            </h3>
            <div className="space-y-3">
              {winnerHistory.map((w, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: i === 0 && winner ? colors.accentGlow : colors.bgSecondary }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: colors.bgPrimary }}>
                      <User className="w-5 h-5" style={{ color: colors.accent }} />
                    </div>
                    <div className="text-left">
                      <p className="font-medium" style={{ color: i === 0 && winner ? colors.accent : 'white' }}>{w.name}</p>
                      <p className="text-gray-500 text-sm">{w.prize}</p>
                    </div>
                  </div>
                  <span className="text-gray-500 text-sm">{w.date}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" style={{ color: colors.success }} />
              Verified Random Selection
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" style={{ color: colors.success }} />
              {platformStats.totalWinners.toLocaleString()}+ Winners Paid
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ DRAW RESULTS PAGE (FOR FANS) ============
const DrawResultsPage = () => {
  const [selectedCreator, setSelectedCreator] = useState(null);
  
  useEffect(() => {
    const db = getDB();
    const creatorsList = Object.values(db.creators);
    if (creatorsList.length > 0 && !selectedCreator) {
      setSelectedCreator(creatorsList[0]);
    }
  }, []);

  const db = getDB();
  const creators = Object.values(db.creators);

  // Past winners data
  const pastDraws = selectedCreator?.experienceDrop?.pastWinners || [
    { name: 'Jessica M.', date: '2025-12-15', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150' },
    { name: 'David K.', date: '2025-11-20', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          
          {/* Header */}
          <div className="mb-8">
            <Gift className="w-16 h-16 mx-auto mb-4" style={{ color: colors.accent }} />
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Experience Drop Results</h1>
            <p className="text-white/60">View past winners and upcoming draws</p>
          </div>

          {/* Creator Selector */}
          {creators.length > 1 && (
            <div className="flex justify-center gap-2 mb-8 flex-wrap">
              {creators.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedCreator(c)}
                  className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                    selectedCreator?.id === c.id ? '' : 'text-gray-400 hover:bg-white/5'
                  }`}
                  style={selectedCreator?.id === c.id ? { background: colors.accent, color: colors.bgPrimary } : { background: colors.bgSecondary }}
                >
                  <img src={c.photo} alt="" className="w-6 h-6 rounded-full object-cover" />
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {/* Current/Next Draw Info */}
          {selectedCreator?.experienceDrop?.title && (
            <div className="glass p-8 rounded-2xl mb-8 text-left">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Next Experience Drop</h2>
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(0, 212, 255, 0.2)', color: colors.accent }}>
                  UPCOMING
                </span>
              </div>
              <p className="text-2xl font-bold mb-2" style={{ color: colors.accent }}>
                {selectedCreator.experienceDrop.title}
              </p>
              <p className="text-white/60 mb-4">{selectedCreator.experienceDrop.description}</p>
              <div className="flex items-center gap-6 text-white/50">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Draw: {selectedCreator.experienceDrop.drawDate ? new Date(selectedCreator.experienceDrop.drawDate).toLocaleDateString() : 'TBA'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span>{selectedCreator.stats?.totalExperienceEntries || 0} entries</span>
                </div>
              </div>
            </div>
          )}

          {/* Past Winners */}
          <div className="glass p-8 rounded-2xl text-left">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Star className="w-5 h-5" style={{ color: colors.accent }} /> Past Winners
            </h2>
            
            {pastDraws.length > 0 ? (
              <div className="space-y-4">
                {pastDraws.map((winner, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-4 p-4 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                  >
                    <img 
                      src={winner.photo} 
                      alt={winner.name}
                      className="w-12 h-12 rounded-full object-cover ring-2"
                      style={{ ringColor: colors.accent }}
                    />
                    <div className="flex-1">
                      <p className="font-semibold">{winner.name}</p>
                      <p className="text-white/50 text-sm">Won on {new Date(winner.date).toLocaleDateString()}</p>
                    </div>
                    <Award className="w-6 h-6" style={{ color: colors.accent }} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-white/50 text-center py-8">No past winners yet. Be the first!</p>
            )}
          </div>

          {/* Info Notice */}
          <div className="mt-8 p-4 rounded-xl text-center" style={{ background: 'rgba(0, 212, 255, 0.1)', border: `1px solid ${colors.accent}30` }}>
            <p className="text-white/70 text-sm">
              <Shield className="w-4 h-4 inline mr-2" style={{ color: colors.accent }} />
              Live draws are conducted by creators. You'll be notified if you win!
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ DISCOVER PAGE ============
const DiscoverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [priceFilter, setPriceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  const db = getDB();
  const allCreators = Object.values(db.creators);
  
  const categories = [
    { id: 'all', label: 'All Creators', icon: Grid, color: '#6366F1' },
    { id: 'Gaming', label: 'Gaming', icon: Play, color: '#8B5CF6' },
    { id: 'Wellness', label: 'Wellness', icon: Heart, color: '#EC4899' },
    { id: 'Music', label: 'Music', icon: Zap, color: '#F59E0B' },
    { id: 'Sports', label: 'Sports', icon: Activity, color: '#EF4444' },
    { id: 'Fashion', label: 'Fashion', icon: Sparkles, color: '#F472B6' },
    { id: 'Travel', label: 'Travel', icon: Globe, color: '#06B6D4' },
    { id: 'Technology', label: 'Tech', icon: TrendingUp, color: '#3B82F6' },
    { id: 'Comedy', label: 'Comedy', icon: Star, color: '#FBBF24' },
    { id: 'Photography', label: 'Photography', icon: Image, color: '#A855F7' },
    { id: 'Finance', label: 'Finance', icon: DollarSign, color: '#10B981' },
    { id: 'Food & Cooking', label: 'Food', icon: Flame, color: '#F97316' },
    { id: 'Fitness', label: 'Fitness', icon: Activity, color: '#14B8A6' },
    { id: 'Art & Design', label: 'Art', icon: Sparkles, color: '#8B5CF6' },
    { id: 'Beauty', label: 'Beauty', icon: Heart, color: '#FB7185' },
    { id: 'Parenting', label: 'Parenting', icon: Users, color: '#F472B6' },
    { id: 'Pets', label: 'Pets', icon: Heart, color: '#FB923C' }
  ];

  const priceRanges = [
    { id: 'all', label: 'All Prices' },
    { id: 'under5', label: 'Under $5/mo' },
    { id: '5to10', label: '$5 - $10/mo' },
    { id: '10to20', label: '$10 - $20/mo' },
    { id: 'over20', label: '$20+/mo' }
  ];

  // Featured experiences for discovery
  const featuredExperiences = [
    { title: "VIP Gaming Session", creator: "Marcus Gaming", category: "Gaming", entries: 2341, image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400" },
    { title: "Wellness Retreat Weekend", creator: "Luna Martinez", category: "Wellness", entries: 1892, image: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400" },
    { title: "Private Cooking Class", creator: "Chef Antonio", category: "Food & Cooking", entries: 987, image: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400" }
  ];

  // Parse search query from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) setSearchQuery(q);
  }, [location]);

  // Helper to extract price number
  const getPriceNumber = (priceStr) => {
    if (!priceStr) return 0;
    const match = priceStr.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  const filteredCreators = allCreators.filter(c => {
    const matchesCategory = activeCategory === 'all' || c.category === activeCategory || 
      (activeCategory === 'Wellness' && c.bio?.toLowerCase().includes('wellness')) ||
      (activeCategory === 'Business' && c.bio?.toLowerCase().includes('business'));
    const matchesSearch = !searchQuery || 
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.bio?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Price filter
    const price = getPriceNumber(c.membershipPrice);
    let matchesPrice = true;
    if (priceFilter === 'under5') matchesPrice = price < 5;
    else if (priceFilter === '5to10') matchesPrice = price >= 5 && price <= 10;
    else if (priceFilter === '10to20') matchesPrice = price > 10 && price <= 20;
    else if (priceFilter === 'over20') matchesPrice = price > 20;
    
    return matchesCategory && matchesSearch && matchesPrice;
  });

  // Featured/Trending creators (top by members)
  const trendingCreators = [...allCreators].sort((a, b) => (b.stats?.totalMembers || 0) - (a.stats?.totalMembers || 0)).slice(0, 4);

  const activeCategoryData = categories.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="discover-page" style={{ background: '#0F172A' }}>
      <div className="max-w-7xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold mb-3">
              Discover <span style={{ background: 'linear-gradient(135deg, #6366F1 0%, #818CF8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Creators</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">Find creators you love, join their community, and win exclusive experiences</p>
          </div>

          {/* Search Bar - Enhanced */}
          <div className="max-w-2xl mx-auto mb-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input 
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, category, or interest..."
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-slate-800/50 border border-slate-700/50 focus:border-indigo-500/50 focus:outline-none text-white placeholder-slate-500 transition-colors"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Category Pills - Enhanced */}
          <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide justify-center flex-wrap">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl whitespace-nowrap transition-all text-sm font-medium ${
                  activeCategory === cat.id 
                    ? 'text-white shadow-lg' 
                    : 'text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700/50'
                }`}
                style={activeCategory === cat.id ? { 
                  background: `linear-gradient(135deg, ${cat.color} 0%, ${cat.color}99 100%)`,
                  boxShadow: `0 4px 20px ${cat.color}40`
                } : {}}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>

          {/* Price Filter Row */}
          <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
            <span className="text-slate-500 text-sm mr-2">Price:</span>
            {priceRanges.map(range => (
              <button
                key={range.id}
                onClick={() => setPriceFilter(range.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  priceFilter === range.id 
                    ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' 
                    : 'text-slate-500 hover:text-slate-300 bg-slate-800/30 border border-transparent'
                }`}
              >
                {range.label}
              </button>
            ))}
          </div>

          {/* Featured Experiences Banner */}
          {!searchQuery && activeCategory === 'all' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Gift className="w-5 h-5 text-indigo-400" /> Live Experiences
                </h2>
                <span className="text-xs text-slate-500">Join a membership to enter</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {featuredExperiences.map((exp, idx) => (
                  <div key={idx} className="relative rounded-xl overflow-hidden group cursor-pointer" onClick={() => navigate('/auth/fan')}>
                    <img src={exp.image} alt={exp.title} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <span className="text-xs px-2 py-1 rounded-full bg-indigo-500/80 text-white mb-2 inline-block">{exp.category}</span>
                      <h3 className="font-bold text-white">{exp.title}</h3>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-slate-300">by {exp.creator}</span>
                        <span className="text-xs text-emerald-400">{exp.entries.toLocaleString()} entries</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Trending Section - Enhanced */}
          {!searchQuery && activeCategory === 'all' && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-12"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-400" /> Trending Creators
                </h2>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'}`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {trendingCreators.map((creator, idx) => (
                  <div 
                    key={creator.id}
                    className="glass rounded-xl cursor-pointer hover:border-indigo-500/30 border border-transparent transition-all overflow-hidden group"
                    onClick={() => navigate(`/c/${creator.username}`)}
                  >
                    {/* Cover/Banner */}
                    <div className="h-24 relative overflow-hidden">
                      <div 
                        className="absolute inset-0 bg-gradient-to-br opacity-80"
                        style={{ background: `linear-gradient(135deg, ${categories[idx % categories.length + 1]?.color || '#6366F1'} 0%, ${categories[(idx + 2) % categories.length + 1]?.color || '#818CF8'} 100%)` }}
                      />
                      {idx === 0 && (
                        <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold bg-orange-500 text-white flex items-center gap-1">
                          <Flame className="w-3 h-3" /> #1
                        </div>
                      )}
                      {idx > 0 && idx < 4 && (
                        <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-black/50 text-white">
                          #{idx + 1}
                        </div>
                      )}
                    </div>
                    
                    {/* Profile */}
                    <div className="px-4 pb-4">
                      <div className="flex justify-center -mt-10 mb-3">
                        <img 
                          src={creator.photo} 
                          alt={creator.name} 
                          className="w-20 h-20 rounded-full object-cover border-4 border-slate-900 group-hover:border-indigo-500/50 transition-colors"
                        />
                      </div>
                      <div className="text-center mb-3">
                        <h3 className="font-bold text-lg text-white group-hover:text-indigo-400 transition-colors">{creator.name}</h3>
                        <p className="text-slate-500 text-sm">@{creator.username}</p>
                      </div>
                      <p className="text-slate-400 text-sm text-center mb-4 line-clamp-2">{creator.bio}</p>
                      
                      {/* Experience Drop */}
                      {creator.experienceDrop?.title && (
                        <div className="p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-4">
                          <div className="flex items-center gap-2 text-xs">
                            <Gift className="w-3.5 h-3.5 text-indigo-400" />
                            <span className="text-white font-medium truncate">{creator.experienceDrop.title}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Stats */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
                        <div className="flex items-center gap-1 text-slate-500 text-sm">
                          <Users className="w-3.5 h-3.5" />
                          <span>{creator.stats?.totalMembers?.toLocaleString() || 0}</span>
                        </div>
                        <span className="font-bold text-indigo-400">{creator.membershipPrice}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Category Header */}
          {activeCategory !== 'all' && (
            <div className="mb-8 flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `${activeCategoryData?.color}20` }}
              >
                {activeCategoryData && <activeCategoryData.icon className="w-6 h-6" style={{ color: activeCategoryData.color }} />}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{activeCategoryData?.label}</h2>
                <p className="text-slate-500 text-sm">{filteredCreators.length} creators in this category</p>
              </div>
            </div>
          )}

          {/* All Creators Grid - Enhanced Cards */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">
                {searchQuery ? `Results for "${searchQuery}"` : activeCategory === 'all' ? 'All Creators' : ''}
              </h2>
              <span className="text-sm text-slate-500">{filteredCreators.length} creators</span>
            </div>
            
            {filteredCreators.length === 0 ? (
              <div className="glass p-16 rounded-2xl text-center">
                <Search className="w-16 h-16 mx-auto mb-4 text-slate-700" />
                <h3 className="text-xl font-semibold text-white mb-2">No creators found</h3>
                <p className="text-slate-500 mb-6">Try adjusting your search or browse a different category</p>
                <button onClick={() => { setSearchQuery(''); setActiveCategory('all'); }} className="btn-outline">
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredCreators.map(creator => {
                  const categoryData = categories.find(c => c.id === creator.category) || categories[0];
                  return (
                    <div 
                      key={creator.id}
                      className="glass p-5 rounded-xl cursor-pointer hover:border-indigo-500/30 border border-transparent transition-all group"
                      onClick={() => navigate(`/c/${creator.username}`)}
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <img 
                          src={creator.photo} 
                          alt={creator.name} 
                          className="w-14 h-14 rounded-full object-cover border-2 border-slate-700 group-hover:border-indigo-500/50 transition-colors"
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white group-hover:text-indigo-400 transition-colors truncate">{creator.name}</h3>
                          <p className="text-slate-500 text-sm">@{creator.username}</p>
                          <span 
                            className="inline-block text-xs px-2 py-0.5 rounded-full mt-1"
                            style={{ background: `${categoryData.color}20`, color: categoryData.color }}
                          >
                            {creator.category || 'Creator'}
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-slate-400 text-sm mb-4 line-clamp-2">{creator.bio}</p>
                      
                      {creator.experienceDrop?.title && (
                        <div className="p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-4">
                          <div className="flex items-center gap-2 text-xs">
                            <Gift className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                            <span className="text-white font-medium truncate">{creator.experienceDrop.title}</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
                        <div className="flex items-center gap-1 text-slate-500 text-sm">
                          <Users className="w-3.5 h-3.5" />
                          <span>{creator.stats?.totalMembers?.toLocaleString() || 0} members</span>
                        </div>
                        <span className="font-bold text-indigo-400">{creator.membershipPrice}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* CTA Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-16 text-center py-12 px-8 rounded-2xl"
            style={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(129, 140, 248, 0.05) 100%)', border: '1px solid rgba(99, 102, 241, 0.2)' }}
          >
            <Crown className="w-10 h-10 mx-auto mb-4 text-indigo-400" />
            <h2 className="text-2xl font-bold text-white mb-3">Are You a Creator?</h2>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">Join FanVault and start monetising your community with exclusive memberships and experiences</p>
            <button onClick={() => navigate('/auth/creator')} className="btn-gold py-3 px-8">
              Start Your Creator Journey <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ MESSAGES PAGE ============
const MessagesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/auth/fan');
      return;
    }
    const db = getDB();
    const userConvs = (db.conversations || []).filter(c => c.participants.includes(user.id));
    setConversations(userConvs);
    
    if (userConvs.length > 0 && !selectedConversation) {
      setSelectedConversation(userConvs[0]);
    }
  }, [user, navigate, selectedConversation]);

  useEffect(() => {
    if (selectedConversation) {
      const db = getDB();
      const convMessages = (db.messages || []).filter(m => m.conversationId === selectedConversation.id);
      setMessages(convMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    }
  }, [selectedConversation]);

  const getOtherParticipant = (conv) => {
    const otherId = conv.participants.find(p => p !== user?.id);
    const db = getDB();
    return db.creators[otherId] || db.fans[otherId] || { name: 'Unknown', photo: '' };
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    const db = getDB();
    const msg = {
      id: `msg-${Date.now()}`,
      conversationId: selectedConversation.id,
      senderId: user.id,
      text: newMessage,
      createdAt: new Date().toISOString()
    };
    db.messages = [...(db.messages || []), msg];
    
    // Update conversation
    const convIdx = db.conversations.findIndex(c => c.id === selectedConversation.id);
    if (convIdx !== -1) {
      db.conversations[convIdx].lastMessage = newMessage;
      db.conversations[convIdx].lastMessageAt = new Date().toISOString();
    }
    
    saveDB(db);
    setMessages([...messages, msg]);
    setNewMessage('');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="messages-page">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <MessageSquare className="w-8 h-8" style={{ color: colors.accent }} /> Messages
          </h1>

          <div className="glass rounded-2xl overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
            <div className="flex h-full">
              {/* Conversations List */}
              <div className="w-80 border-r border-white/10 flex flex-col">
                <div className="p-4 border-b border-white/10">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input type="text" placeholder="Search messages..." className="input-field w-full pl-10 py-2 text-sm" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="p-8 text-center text-white/50">
                      <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No messages yet</p>
                    </div>
                  ) : conversations.map(conv => {
                    const other = getOtherParticipant(conv);
                    const isSelected = selectedConversation?.id === conv.id;
                    const unread = conv.unreadCount?.[user.id] || 0;
                    
                    return (
                      <div
                        key={conv.id}
                        onClick={() => setSelectedConversation(conv)}
                        className={`p-4 cursor-pointer transition-colors ${isSelected ? 'bg-white/10' : 'hover:bg-white/5'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <img src={other.photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                            {unread > 0 && (
                              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center" style={{ background: colors.accent }}>{unread}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-semibold truncate">{other.name}</p>
                              <span className="text-white/40 text-xs">{formatTimeAgo(conv.lastMessageAt)}</span>
                            </div>
                            <p className="text-white/50 text-sm truncate">{conv.lastMessage}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Message Thread */}
              <div className="flex-1 flex flex-col">
                {selectedConversation ? (
                  <>
                    {/* Thread Header */}
                    <div className="p-4 border-b border-white/10 flex items-center gap-3">
                      <img src={getOtherParticipant(selectedConversation).photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="font-semibold">{getOtherParticipant(selectedConversation).name}</p>
                        <p className="text-white/50 text-xs">Active now</p>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.map(msg => {
                        const isOwn = msg.senderId === user.id;
                        return (
                          <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                            <div 
                              className={`max-w-[70%] p-3 rounded-2xl ${isOwn ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                              style={{ background: isOwn ? colors.accent : 'rgba(255,255,255,0.1)' }}
                            >
                              <p className="text-sm">{msg.text}</p>
                              <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-white/40'}`}>{formatTimeAgo(msg.createdAt)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Input */}
                    <div className="p-4 border-t border-white/10">
                      <div className="flex gap-3">
                        <input 
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          className="input-field flex-1"
                          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        />
                        <button onClick={sendMessage} className="btn-gold px-6">
                          <Send className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-white/50">
                    <div className="text-center">
                      <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Select a conversation to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ SETTINGS PAGE ============
const SettingsPage = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('profile');
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    bio: user?.bio || '',
    notifications: { email: true, push: true, marketing: false },
    privacy: { showActivity: true, allowMessages: true }
  });
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (!user) navigate('/auth/fan');
  }, [user, navigate]);

  const handleSave = () => {
    updateUser({ name: formData.name, bio: formData.bio });
    setSaveMessage('Settings saved!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  if (!user) return null;

  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'account', label: 'Account', icon: Settings }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="settings-page">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Settings className="w-8 h-8" style={{ color: colors.accent }} /> Settings
          </h1>

          {saveMessage && (
            <div className="mb-6 p-4 rounded-xl text-center font-medium" style={{ background: 'rgba(34, 197, 94, 0.2)', border: '1px solid rgba(34, 197, 94, 0.5)' }}>
              <Check className="w-5 h-5 inline mr-2" /> {saveMessage}
            </div>
          )}

          <div className="glass rounded-2xl overflow-hidden">
            <div className="flex">
              {/* Sidebar */}
              <div className="w-64 border-r border-white/10 p-4">
                {sections.map(section => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left p-3 rounded-lg flex items-center gap-3 mb-1 transition-colors ${
                      activeSection === section.id ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5'
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    {section.label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 p-6">
                {activeSection === 'profile' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold mb-4">Profile Settings</h2>
                    
                    <div className="flex items-center gap-6 mb-6">
                      <img src={user.photo} alt="" className="w-24 h-24 rounded-full object-cover ring-4" style={{ ringColor: colors.accent }} />
                      <div>
                        <button className="btn-outline py-2 px-4 text-sm mb-2">
                          <Upload className="w-4 h-4 mr-2" /> Change Photo
                        </button>
                        <p className="text-white/50 text-xs">JPG, PNG. Max 5MB</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Display Name</label>
                        <input 
                          type="text" 
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="input-field w-full" 
                        />
                      </div>
                      <div>
                        <label className="block text-white/70 text-sm mb-2">Email</label>
                        <input type="email" value={formData.email} className="input-field w-full" disabled />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white/70 text-sm mb-2">Bio</label>
                      <textarea 
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        className="input-field w-full h-24 resize-none"
                        placeholder="Tell us about yourself..."
                      />
                    </div>

                    <button onClick={handleSave} className="btn-gold py-3 px-8">
                      Save Changes
                    </button>
                  </div>
                )}

                {activeSection === 'notifications' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold mb-4">Notification Preferences</h2>
                    
                    {[
                      { id: 'email', label: 'Email Notifications', desc: 'Receive updates via email' },
                      { id: 'push', label: 'Push Notifications', desc: 'Get notified on your device' },
                      { id: 'newPosts', label: 'New Posts', desc: 'When creators you follow post' },
                      { id: 'messages', label: 'Direct Messages', desc: 'When you receive a message' },
                      { id: 'drops', label: 'Experience Drops', desc: 'New drops from creators' }
                    ].map(item => (
                      <div key={item.id} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-white/50 text-sm">{item.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" defaultChecked className="sr-only peer" />
                          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {activeSection === 'privacy' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold mb-4">Privacy Settings</h2>
                    
                    {[
                      { id: 'activity', label: 'Show Activity Status', desc: 'Let others see when you\'re active' },
                      { id: 'messages', label: 'Allow Messages', desc: 'Receive messages from creators' },
                      { id: 'profile', label: 'Public Profile', desc: 'Make your profile visible to others' }
                    ].map(item => (
                      <div key={item.id} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-white/50 text-sm">{item.desc}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" defaultChecked className="sr-only peer" />
                          <div className="w-11 h-6 bg-white/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                {activeSection === 'billing' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold mb-4">Billing & Payments</h2>
                    
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-8 rounded bg-gradient-to-r from-blue-600 to-blue-400 flex items-center justify-center text-xs font-bold">VISA</div>
                        <div className="flex-1">
                          <p className="font-medium">•••• •••• •••• 4242</p>
                          <p className="text-white/50 text-sm">Expires 12/26</p>
                        </div>
                        <button className="btn-outline py-2 px-4 text-sm">Edit</button>
                      </div>
                    </div>

                    <button className="btn-outline w-full py-3">
                      <Plus className="w-5 h-5 mr-2" /> Add Payment Method
                    </button>

                    <div className="mt-8">
                      <h3 className="font-semibold mb-3">Active Subscriptions</h3>
                      <div className="space-y-3">
                        {['Sarah Chen - $5/month', 'Marcus Gaming - $10/month'].map((sub, idx) => (
                          <div key={idx} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <span>{sub}</span>
                            <button className="text-red-400 text-sm hover:underline">Cancel</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'account' && (
                  <div className="space-y-6">
                    <h2 className="text-xl font-bold mb-4">Account Settings</h2>
                    
                    <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <h3 className="font-semibold mb-2">Change Password</h3>
                      <p className="text-white/50 text-sm mb-4">Update your password to keep your account secure</p>
                      <button className="btn-outline py-2 px-4 text-sm">Change Password</button>
                    </div>

                    <div className="p-4 rounded-xl border border-red-500/30" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                      <h3 className="font-semibold text-red-400 mb-2">Danger Zone</h3>
                      <p className="text-white/50 text-sm mb-4">Permanently delete your account and all data</p>
                      <button className="bg-red-500/20 text-red-400 border border-red-500/50 py-2 px-4 rounded-lg text-sm hover:bg-red-500/30">
                        Delete Account
                      </button>
                    </div>

                    <button onClick={() => { logout(); navigate('/'); }} className="btn-outline w-full py-3 text-red-400 border-red-400/50 hover:bg-red-400/10">
                      <LogOut className="w-5 h-5 mr-2" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ SAVED POSTS PAGE ============
const SavedPostsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savedPosts, setSavedPosts] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/auth/fan');
      return;
    }
    const db = getDB();
    const saved = (db.savedPosts || []).filter(s => s.fanId === user.id);
    const posts = saved.map(s => {
      const post = (db.posts || []).find(p => p.id === s.postId);
      if (post) {
        const creator = db.creators[post.creatorId];
        return { ...post, creator, savedAt: s.savedAt };
      }
      return null;
    }).filter(Boolean);
    setSavedPosts(posts);
  }, [user, navigate]);

  const removeSaved = (postId) => {
    const db = getDB();
    db.savedPosts = (db.savedPosts || []).filter(s => !(s.fanId === user.id && s.postId === postId));
    saveDB(db);
    setSavedPosts(savedPosts.filter(p => p.id !== postId));
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="saved-posts-page">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Bookmark className="w-8 h-8" style={{ color: colors.accent }} /> Saved Posts
          </h1>

          {savedPosts.length === 0 ? (
            <div className="glass p-12 rounded-2xl text-center">
              <Bookmark className="w-16 h-16 mx-auto mb-4 text-white/30" />
              <h2 className="text-xl font-bold mb-2">No saved posts yet</h2>
              <p className="text-white/60 mb-6">Posts you save will appear here</p>
              <button onClick={() => navigate('/discover')} className="btn-gold py-3 px-6">
                <Compass className="w-5 h-5 mr-2" /> Discover Creators
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {savedPosts.map(post => (
                <div key={post.id} className="glass rounded-2xl overflow-hidden">
                  <div className="p-4 flex items-center gap-3">
                    <img src={post.creator?.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    <div className="flex-1">
                      <p className="font-bold">{post.creator?.name}</p>
                      <p className="text-white/50 text-sm">Saved {formatTimeAgo(post.savedAt)}</p>
                    </div>
                    <button onClick={() => removeSaved(post.id)} className="p-2 rounded-lg hover:bg-white/10">
                      <Bookmark className="w-5 h-5" style={{ color: colors.accent, fill: colors.accent }} />
                    </button>
                  </div>
                  <div className="px-4 pb-3">
                    <p className="line-clamp-2">{post.caption}</p>
                  </div>
                  {post.mediaUrls?.length > 0 && (
                    <img src={post.mediaUrls[0]} alt="" className="w-full max-h-80 object-cover" />
                  )}
                  <div className="p-4 flex items-center gap-4 border-t border-white/10">
                    <span className="flex items-center gap-1 text-white/60">
                      <Heart className="w-5 h-5" /> {post.likes}
                    </span>
                    <span className="flex items-center gap-1 text-white/60">
                      <MessageCircle className="w-5 h-5" /> {post.comments?.length || 0}
                    </span>
                    <button 
                      onClick={() => navigate(`/c/${post.creator?.username}/feed`)}
                      className="ml-auto text-sm hover:underline"
                      style={{ color: colors.accent }}
                    >
                      View Full Post →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// ============ CREATOR ANALYTICS PAGE ============
const CreatorAnalyticsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'creator') {
      navigate('/auth/creator');
    }
  }, [user, navigate]);

  if (!user || user.role !== 'creator') return null;

  // Mock analytics data
  const revenueData = [
    { month: 'Jul', amount: 3200 },
    { month: 'Aug', amount: 4100 },
    { month: 'Sep', amount: 3800 },
    { month: 'Oct', amount: 4500 },
    { month: 'Nov', amount: 5200 },
    { month: 'Dec', amount: 4850 }
  ];

  const memberGrowth = [
    { month: 'Jul', count: 620 },
    { month: 'Aug', count: 680 },
    { month: 'Sep', count: 720 },
    { month: 'Oct', count: 765 },
    { month: 'Nov', count: 810 },
    { month: 'Dec', count: 850 }
  ];

  const topContent = [
    { title: 'Morning yoga routine', views: 2340, likes: 456, engagement: '19.5%' },
    { title: 'Behind the scenes retreat', views: 1890, likes: 312, engagement: '16.5%' },
    { title: 'Wellness tips thread', views: 1650, likes: 289, engagement: '17.5%' }
  ];

  const maxRevenue = Math.max(...revenueData.map(d => d.amount));
  const maxMembers = Math.max(...memberGrowth.map(d => d.count));

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="analytics-page">
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <BarChart3 className="w-8 h-8" style={{ color: colors.accent }} /> Analytics
            </h1>
            <select className="input-field py-2 px-4">
              <option>Last 30 days</option>
              <option>Last 90 days</option>
              <option>Last 12 months</option>
            </select>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass p-5 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-6 h-6" style={{ color: colors.accent }} />
                <span className="text-green-400 text-sm flex items-center"><ArrowUp className="w-3 h-3" /> 12%</span>
              </div>
              <p className="text-2xl font-bold">${user.stats?.thisMonthEarnings?.toLocaleString() || 0}</p>
              <p className="text-white/50 text-sm">This Month</p>
            </div>
            <div className="glass p-5 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-6 h-6" style={{ color: colors.accent }} />
                <span className="text-green-400 text-sm flex items-center"><ArrowUp className="w-3 h-3" /> 8%</span>
              </div>
              <p className="text-2xl font-bold">{user.stats?.totalMembers?.toLocaleString() || 0}</p>
              <p className="text-white/50 text-sm">Total Members</p>
            </div>
            <div className="glass p-5 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <EyeIcon className="w-6 h-6" style={{ color: colors.accent }} />
                <span className="text-green-400 text-sm flex items-center"><ArrowUp className="w-3 h-3" /> 24%</span>
              </div>
              <p className="text-2xl font-bold">{user.stats?.contentViews?.toLocaleString() || 0}</p>
              <p className="text-white/50 text-sm">Content Views</p>
            </div>
            <div className="glass p-5 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-6 h-6" style={{ color: colors.accent }} />
                <span className="text-green-400 text-sm flex items-center"><ArrowUp className="w-3 h-3" /> 3%</span>
              </div>
              <p className="text-2xl font-bold">{user.stats?.conversionRate || 14.8}%</p>
              <p className="text-white/50 text-sm">Conversion Rate</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Revenue Chart */}
            <div className="glass p-6 rounded-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" style={{ color: colors.accent }} /> Revenue
              </h3>
              <div className="h-48 flex items-end gap-2">
                {revenueData.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full rounded-t-lg transition-all hover:opacity-80"
                      style={{ 
                        height: `${(d.amount / maxRevenue) * 100}%`,
                        background: `linear-gradient(180deg, ${colors.accent} 0%, ${colors.accentSecondary} 100%)`
                      }}
                    />
                    <span className="text-xs text-white/50">{d.month}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <p className="text-2xl font-bold">${user.stats?.totalEarnings?.toLocaleString() || 0}</p>
                <p className="text-white/50 text-sm">Total Revenue</p>
              </div>
            </div>

            {/* Member Growth Chart */}
            <div className="glass p-6 rounded-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: colors.accent }} /> Member Growth
              </h3>
              <div className="h-48 flex items-end gap-2">
                {memberGrowth.map((d, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full rounded-t-lg transition-all hover:opacity-80"
                      style={{ 
                        height: `${(d.count / maxMembers) * 100}%`,
                        background: `linear-gradient(180deg, #22C55E 0%, #16A34A 100%)`
                      }}
                    />
                    <span className="text-xs text-white/50">{d.month}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <p className="text-2xl font-bold">+{user.stats?.thisMonthNewMembers || 0}</p>
                <p className="text-white/50 text-sm">New This Month</p>
              </div>
            </div>
          </div>

          {/* Top Content */}
          <div className="glass p-6 rounded-2xl mb-8">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" style={{ color: colors.accent }} /> Top Performing Content
            </h3>
            <div className="space-y-4">
              {topContent.map((content, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <span className="text-2xl font-bold text-white/30">#{i + 1}</span>
                  <div className="flex-1">
                    <p className="font-medium">{content.title}</p>
                    <div className="flex gap-4 text-sm text-white/50 mt-1">
                      <span><EyeIcon className="w-4 h-4 inline mr-1" /> {content.views}</span>
                      <span><Heart className="w-4 h-4 inline mr-1" /> {content.likes}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ color: colors.accent }}>{content.engagement}</p>
                    <p className="text-white/50 text-xs">Engagement</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Audience Insights */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass p-6 rounded-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5" style={{ color: colors.accent }} /> Top Locations
              </h3>
              <div className="space-y-3">
                {[
                  { country: 'United States', pct: 45 },
                  { country: 'United Kingdom', pct: 18 },
                  { country: 'Australia', pct: 12 },
                  { country: 'Canada', pct: 10 },
                  { country: 'Other', pct: 15 }
                ].map((loc, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{loc.country}</span>
                        <span className="text-white/50">{loc.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10">
                        <div className="h-full rounded-full" style={{ width: `${loc.pct}%`, background: colors.accent }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass p-6 rounded-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5" style={{ color: colors.accent }} /> Age Distribution
              </h3>
              <div className="space-y-3">
                {[
                  { age: '18-24', pct: 28 },
                  { age: '25-34', pct: 42 },
                  { age: '35-44', pct: 18 },
                  { age: '45+', pct: 12 }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{item.age}</span>
                        <span className="text-white/50">{item.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10">
                        <div className="h-full rounded-full" style={{ width: `${item.pct}%`, background: colors.accentSecondary }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass p-6 rounded-2xl">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" style={{ color: colors.accent }} /> Peak Activity
              </h3>
              <div className="space-y-3">
                {[
                  { time: '9AM - 12PM', pct: 35 },
                  { time: '12PM - 3PM', pct: 25 },
                  { time: '6PM - 9PM', pct: 55 },
                  { time: '9PM - 12AM', pct: 40 }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{item.time}</span>
                        <span className="text-white/50">{item.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10">
                        <div className="h-full rounded-full" style={{ width: `${item.pct}%`, background: '#8B5CF6' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${colors.bgPrimary} 0%, #0f1d32 100%)` }}>
          <Header />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/auth/creator" element={<AuthPage type="creator" />} />
            <Route path="/auth/fan" element={<AuthPage type="fan" />} />
            <Route path="/creator/dashboard" element={<CreatorDashboard />} />
            <Route path="/creator/analytics" element={<CreatorAnalyticsPage />} />
            <Route path="/fan/dashboard" element={<FanDashboard />} />
            <Route path="/c/:username" element={<CreatorPublicPage />} />
            <Route path="/c/:username/feed" element={<MemberContentFeed />} />
            <Route path="/draw/live" element={<LiveDrawPage />} />
            <Route path="/draw/results" element={<DrawResultsPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/saved" element={<SavedPostsPage />} />
          </Routes>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
